import React, { useEffect, useState, useRef, useCallback, useMemo, startTransition, useLayoutEffect} from "react";
import { v4 as uuidv4 } from "uuid";
import { useLocation } from "react-router-dom";
import Konva from "konva";
import useUndo from "../hooks/useUndo";
import "konva/lib/shapes/Image";
import BackgroundControls from "../components/BackgroundControls";
import "../index.css";
import Stack from '@mui/material/Stack';
import { Tooltip, LinearProgress, Slider, Box, Typography } from '@mui/material';
import type { Slot } from '@/types';
import type { Template } from "@/types/Template";
import { ImageData } from "../types/ImageData";
import AlbumPageCanvas from "../components/AlbumPageCanvas";
import { CreateInitialSpreads } from "../utils/albumData";
import { IconButton } from '@mui/material';
import { uploadAndResizeImage } from "../utils/imageUpload"
import { TextElement } from "../types/TextElement";
import type { PageType } from "../utils/albumData";
import OpacityControl from '../components/OpacityControl';
import type { ShapeType, ShapeElement } from '../types/ShapeElement';
import { Button,} from '@mui/material';
import EffectsControl from "../components/EffectsControl";
import { AlbumSaveData } from '../types/AlbumSaveData';
import type { AlbumImage } from "../types/AlbumSaveData";
import { saveAlbumToServer } from '../api/albumService';
import type { AlbumElement } from '@/types/AlbumElement'; // 
import { useAuth } from '../contexts/AuthContext';
import PhotoUploader from '../components/PhotoUploader';
import { useNavigate } from "react-router-dom";
import "./ToolbarButton.css";
import { ColorPicker } from '../components/ColorPicker';
import FontDropdown from '../components/FontDropdown';
import "./PhotoUploader.css";
import { calculateSnapping } from "../utils/snapUtils"; // adjust path if needed
import { MessageDialog } from '../components/MessageDialog';
import TemplateLoader from "../components/TemplateLoader"; // React component
import { allTemplatesMap } from "../templates";
import { jwtDecode } from 'jwt-decode';
import AutoCreateAlbumDialog from "../components/AutoCreateAlbumDialog";
import type { AutoCreateAlbumOptions } from "../types/AutoCreateAlbumOptions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { PrintProduct } from "@/types/PrintProduct";
import { PRINT_PRODUCTS } from "../templates/PR";
import PrintSelector from '../components/PrintSelector';
import { getOpenings, getDefaultOpening } from "../utils/openings/openingUtils";
import type { AlbumType } from "../utils/openings/types";
import { parseSize } from "../utils/sizeParser";
import PageManagerPanel from "../components/PageManagerPanel";
import Popover from "@mui/material/Popover";


interface AlbumPageDesignerProps {
    _id?: string;  // MongoDB ID, optional for new albums
    albumName: string;
    albumType: string;
    size: { width: number; height: number; unit: 'cm' | 'inches' };  // non-null here
    pageCount: number;
    loadedProjectData: any;
    backgroundColor: string;
    cutArea: number;
    unit: 'cm' | 'inches';
    spineWidth?: number;
   
}


const AlbumPageDesigner: React.FC<AlbumPageDesignerProps> = ({ albumName, albumType, size, pageCount, loadedProjectData, backgroundColor, cutArea, unit, spineWidth }) => {
    const pageWidth = size.width
    const cutGap = cutArea
    const safeAlbumType = albumType as AlbumType;
    const [stageSize, setStageSize] = useState({
        width: 0,
        height: 0
    });

    // Normalize pages function
    const normalizePages = (pagesFromData: any[], pageCount: number) => {
        if (!pagesFromData || !pagesFromData.length) return CreateInitialSpreads(pageCount, 0, backgroundColor ?? '#ffffff');
        return pagesFromData.map((page: any, index: number) => ({
            ...page,
            order: page.order ?? index,
            id: page.id ?? uuidv4(),
        }));
    };

    // Initial normalized pages
    const initialPages = normalizePages(loadedProjectData?.data?.pages, pageCount);

    const initialAlbumData: AlbumSaveData = loadedProjectData
        ? {
            _id: loadedProjectData._id,
            projectId: loadedProjectData._id ?? null,
            albumName: loadedProjectData.albumName ?? albumName,
            albumType: loadedProjectData.albumType ?? albumType,
            size: loadedProjectData.albumSize ?? size,
            layoutSize: loadedProjectData.layoutSize ?? {
                width: 0,
                height: 0,
            },
         
            backgroundColor: loadedProjectData.backgroundColor ?? backgroundColor,
            cutArea: loadedProjectData.cutArea ?? 0,
            spineWidth: loadedProjectData.spineWidth ?? spineWidth ?? 0,
            showCenterLine: loadedProjectData.showCenterLine ?? true,
            showSlotNumbers: loadedProjectData.showSlotNumbers ?? true,
            opening:
                loadedProjectData.opening ??
                getDefaultOpening(
                    loadedProjectData.albumType ?? albumType,
                    loadedProjectData.albumSize ?? size
                ),
            handtornMode:
                loadedProjectData.albumType === "HANDTORN"
                    ? loadedProjectData.handtornMode ?? "square"
                    : null,
            pages: initialPages,
            pageCount: initialPages.length,
            uploadedImages: loadedProjectData.uploadedImages ?? [],
            editorSettings:
                loadedProjectData.data?.editorSettings ?? {
                    thumbnailColumns: 2,
                },
         

        }
        : {
            _id: undefined,
            projectId: null,
            albumName,
            albumType,
            size,
            layoutSize: {
                width: stageSize.width,
                height: stageSize.height,
            },
            backgroundColor,
            cutArea,
            spineWidth: spineWidth ?? 0,
            showCenterLine: true,
            showSlotNumbers: false,
            opening: getDefaultOpening(safeAlbumType, size),
            handtornMode: albumType === "HANDTORN" ? "square" : null,
            pages: normalizePages([], pageCount),
            pageCount,
            editorSettings: {
                thumbnailColumns: 2,
            },
            uploadedImages: [],
        };

    const [albumData, setAlbumData] = useState<AlbumSaveData>(() => initialAlbumData);
    const pagesUndo = useUndo<PageType[]>(initialAlbumData.pages);
    const templateSlotsUndo = useUndo<Slot[]>([]);
    const pages: PageType[] = pagesUndo.present;

    const setPages = pagesUndo.set as React.Dispatch<React.SetStateAction<PageType[]>>;
    const undo = pagesUndo.undo;
    const redo = pagesUndo.redo;
    const canUndo = pagesUndo.canUndo;
    const canRedo = pagesUndo.canRedo;
    const setTemplateSlots = templateSlotsUndo.set;

    const isPagesReady =
        pages?.length > 0 &&
        pages.every(p => Array.isArray(p.slots));



    useEffect(() => {
        if (!loadedProjectData?.data?.pages) return;

        const pagesFromData = normalizePages(
            loadedProjectData.data.pages,
            loadedProjectData.pageCount
        );

        pagesFromData.forEach(page => {
            const savedGap = page.gap ?? 0;

            let baseSlots = [...page.slots];

            if (savedGap !== 0) {
                for (let i = 0; i < 2; i++) {
                    baseSlots = applySmartGap(
                        baseSlots,
                        -savedGap
                    );
                }
            }

            originalSlotsRef.current[page.id] =
                baseSlots.map(slot => ({ ...slot }));
        
        });

        pagesUndo.set(pagesFromData);
    }, [loadedProjectData]);
  


    const lastSavedDataRef = useRef<AlbumSaveData>(initialAlbumData);
      //save the album on call of it the first time it is created
    const didInitialSaveRef = useRef(false);
    const navigate = useNavigate();
  
    //images upload const
    const location = useLocation();
    const [, setUploadedImages] = useState<ImageData[]>([]);

    const stageRefs = useRef<Record<string, Konva.Stage | null>>({});

    const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);// for scroll the pages
    const divRef = useRef<HTMLDivElement>(null); // Keep this for drop logic

    const [guidesByPage, setGuidesByPage] = useState<Record<string, { points: number[] }[]>>({});

    const [selectedSlotIdsByPage, setSelectedSlotIdsByPage] = useState<Record<string, string[]>>({});
    const [selectedElement, setSelectedElement] = React.useState<{ type: 'text' | 'slot' | 'shape'; id: string } | null>(null);
    const [selectedTemplate, ] = useState<Template | null>(null);
    
    const [selectedPageId, setSelectedPageId] = useState<string | null>(pages[0]?.id ?? null);
    const selectedPage = pages.find(page => page.id === selectedPageId); //  Already on-the-fly
    const selectedIds = useMemo(() => {
        return selectedPageId ? selectedSlotIdsByPage[selectedPageId] || [] : [];
    }, [selectedPageId, selectedSlotIdsByPage]);
    const [autoDialogOpen, setAutoDialogOpen] = useState(false);

    const isCreatingRef = useRef(false);
    const isSavingRef = useRef(false);
  

    const selectedSlot = selectedPage?.slots.find((slot: Slot) => selectedIds.includes(slot.id));
    const selectedSlotId = selectedIds[0] ?? null;
    //show center line
    const [showCenterLine, setShowCenterLine] = useState(true);
    //show slot numbers
    const [showSlotNumbers, setShowSlotNumbers] = useState(false);
    const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
    // to Disable the page buttons
    const DISABLE_PAGE_ACTIONS = ["AC", "ASC", "CANVAS", "ARCH", "FCANVAS", "HANDTORN", "FPRINTS", "PRINTS", "FMOUNTS", "HIPSTER"];
   // disable the flip buttons
    const DISABLE_FLIP_ACTIONS = ["MONTAGE", "FMOUNTS", "HIPSTER", "FPRINTS", "PRINTS", "CANVAS", "FCANVAS", "HANDTORN", "ARCH"];

    const isAlbumType = DISABLE_PAGE_ACTIONS.includes(albumType);
    const disableFlip = DISABLE_FLIP_ACTIONS.includes(albumType);

   // Products with limited album functionality
    const excludedAlbumTypes = new Set([
        "AC","ASC","MONTAGE","CANVAS","FCANVAS","ARCH", "PRINTS", "FMOUNTS", "HIPSTER","HANDTORN", "FPRINTS"]);

    const supportsReviews = !excludedAlbumTypes.has(albumType);
    const supportsTemplateReload = !excludedAlbumTypes.has(albumType);
    const supportsCut = !excludedAlbumTypes.has(albumType);
    
    const disableGap = new Set(["MONTAGE", "CANVAS", "FCANVAS", "ARCH", "PRINTS", "FMOUNTS", "FPRINTS", "HANDTORN","HIPSTER", "AC", "ASC"]);
    const disableSlotOptions = new Set(["MONTAGE", "CANVAS", "FCANVAS", "ARCH", "FMOUNTS", "FPRINTS", "HANDTORN", "HIPSTER", "PRINTS"]);
    const disabledProducts = new Set(["ARCH", "MONTAGE", "FMOUNTS", "FPRINTS", "HANDTORN", "PRINTS", "HIPSTER"]);
    const disableText = new Set(["CANVAS", "FCANVAS", "ARCH", "MONTAGE", "FMOUNTS", "FPRINTS", "HANDTORN", "PRINTS"])
    const disableImage = new Set(["CANVAS", "FCANVAS", "ARCH", "FMOUNTS", "FPRINTS", "HANDTORN", "PRINTS"])
    const albumTypesNo = new Set(["BOUTIQUE", "LIFESTYLE", "PORTRAIT", "JOURNAL", "GUEST", "LITE", "LUXE"])

  

    // handtornSizes.ts
    const HANDTORN_SQUARE_SIZES = [
        { width: 8, height: 8 },
        { width: 10, height: 10 },
        { width: 12, height: 12 },
        { width: 14, height: 14 },
        { width: 16, height: 16 },
        { width: 20, height: 20 },
        { width: 24, height: 24 },
        { width: 30, height: 30 },
        { width: 40, height: 40 },
    ];


    const API_URL = process.env.REACT_APP_API_URL;

    type EditorMode = "auto" | "manual";
    const [editorMode, setEditorMode] = useState<EditorMode>(
        albumType === "AC" || albumType === "ASC"
            ? "manual"
            : "auto"
    );

    const imageCacheRef = React.useRef<Map<string, HTMLImageElement>>(new Map());
    const [showPageManager, setShowPageManager] = useState(false);

    const [photoUploaderWidth, setPhotoUploaderWidth] = useState(292);
    type PageReview = {
        status: "pending" | "approved" | "rejected";
        comment: string;
        photographerNote?: string;
    };

    const [pageReviews, setPageReviews] = useState<Record<string, PageReview>>({});
    const photographerNotesRef = useRef<Record<string, string>>({});

    const [popoverAnchor, setPopoverAnchor] = useState<Element | null>(null);
    const [popoverPageId, setPopoverPageId] = useState<string | null>(null);

    const openPopover = (
        event: React.MouseEvent<SVGSVGElement>,
        pageId: string
    ) => {
        setPopoverAnchor(event.currentTarget);
        setPopoverPageId(pageId);
    };

    const closePopover = () => {
        setPopoverAnchor(null);
        setPopoverPageId(null);
    }



    const savePhotographerNote = async (
        pageId: string,
        pageIndex: number,
        photographerNote: string
    ) => {
        const res = await fetch(
            `${API_URL}/export/web-proof/photographer-note/${albumData.albumName}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    pageId,
                    pageIndex,
                    photographerNote
                })
            }
        );

        return res.ok;
    };
  
    //dibale the slide show and export online proof in Photouploader
    const disabledWallArtAlbums = new Set([
        "MONTAGE",
        "CANVAS",
        "FCANVAS",
        "ARCH",
        "PRINTS",
        "FMOUNTS",
        "HIPSTER",
        "HANDTORN",
        "FPRINTS",
        "AC",
        "ASC"
    ]);

    const disableWallArts = disabledWallArtAlbums.has(albumType);

    const [thumbnailColumns, setThumbnailColumns] = useState(
        albumData.editorSettings?.thumbnailColumns ?? 2
    );


    const handleThumbnailColumnsChange = (cols: number) => {
        setThumbnailColumns(cols);

        setAlbumData(prev => ({
            ...prev,
            editorSettings: {
                ...prev.editorSettings,
                thumbnailColumns: cols,
            },
        }));
    };


    useEffect(() => {
        const cols = albumData.editorSettings?.thumbnailColumns ?? 2;
        const width =
            cols * 132 + (cols - 1) * 8 + 10;

        setPhotoUploaderWidth(width);
    }, [albumData.editorSettings?.thumbnailColumns]);

     //For the openings
    const openings = useMemo(() => {
        const size = {
            width: albumData.size.width,
            height: albumData.size.height,
        };

        return getOpenings(safeAlbumType, size);
    }, [safeAlbumType, albumData.size]);

    const [selectedOpening, setSelectedOpening] = useState<string | null>(null);
    const handtornMode = albumData.handtornMode ?? "square";

    
    useEffect(() => {
        if (!albumData.opening) return;
        if (!openings.length) return;

        setSelectedOpening(albumData.opening);
    }, [openings, albumData.opening]);


    const safeOpening = useMemo(() => {
        if (!openings.length) return null;

        if (selectedOpening && openings.includes(selectedOpening)) {
            return selectedOpening;
        }

        return openings[0]; // auto fallback
    }, [openings, selectedOpening]);

 
 
    //For the FPRINTS and FMOUNTS
    useEffect(() => {
        // This effect is ONLY for products whose slot size
        // comes from the selected opening.
        if (
            albumType !== "FPRINTS" &&
            albumType !== "FMOUNTS"
        ) {
            return;
        }

        if (!isPagesReady) return;
        if (!pages?.length) return;
        if (!stageSize.width || !stageSize.height) return;

        const opening = safeOpening;

        if (!opening) return;

        const { w, h } = parseSize(opening);
        const scale = stageSize.width / albumData.size.width;
        const width = w * scale;
        const height = h * scale;

        updatePages(prev =>
            prev.map(page => ({
                ...page,
                slots: page.slots.map(slot => {
                    const updatedSlot = {
                        ...slot,
                        width,
                        height,
                        x: (stageSize.width - width) / 2,
                        y: (stageSize.height - height) / 2,
                    };

                    return calculateSlotQuality(
                        updatedSlot,
                        pixelsPerInch,
                        opening
                    );
                }),
            }))
        );

    }, [
        isPagesReady,
        albumType,
        safeOpening,
        albumData.size,
        stageSize.width,
        stageSize.height,
    ]);



    //login token
    const auth = useAuth();
    const token = auth?.token || localStorage.getItem("token");

    interface TokenPayload {
        username: string;
        id?: string;
        exp?: number;
    }

    const [savedImages, setSavedImages] = useState<AlbumImage[]>([]);
    const [divideParts, setDivideParts] = useState<number>(2);
    const [divideGap, setDivideGap] = useState(4);
 
    const onUpdateGuides = (newGuides: { points: number[] }[]) => {
        if (!selectedPageId) return;
        setGuidesByPage(prev => ({
            ...prev,
            [selectedPageId]: newGuides
        }));
    };

    //new code for the themplates to load
    const stageWidth = size.width;
    const stageHeight = size.height;

    const openAR = formatAR(stageWidth / stageHeight);
    const CANVAS_WRAP = 4.723;
    function formatDisplaySize(size: number): string {
        return parseFloat(size.toFixed(3)).toString();
    }

    // Helper: normalize AR to 2 decimals
    function formatAR(ar: number) {
        return (Math.round(ar * 100) / 100).toFixed(2);
    }

    //'To save the stage as image'
    //const exportSelectedStage = () => {
    //    if (!selectedPageId) return;

    //    const stage = stageRefs.current[selectedPageId];
    //    console.log(stageRefs.current[selectedPageId]);
    //    if (!stage) return;

    //    const uri = stage.toDataURL({ pixelRatio: 1 });

    //    const link = document.createElement("a");
    //    link.download = `spread-${selectedPageId}.png`;
    //    link.href = uri;
    //    link.click();
    //};

    const isHandtornSquareSize =
        albumType === "HANDTORN" &&
        HANDTORN_SQUARE_SIZES.some(
            s => s.width === size.width && s.height === size.height
        );

    // fetch the reviews for the pages
    useEffect(() => {
        if (
            !supportsReviews ||
            !token ||
            !albumData?.albumName ||
            pages.length === 0
        ) {
            return;
        }

        const decoded = jwtDecode<TokenPayload>(token);
        const username = decoded.username;

        const fetchReview = async () => {
            try {
                const res = await fetch(
                    `${API_URL}/export/web-proof/latest/${username}/${albumData.albumName}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (res.status === 404) {
                    setPageReviews({});
                    return;
                }

                if (!res.ok) {
                    throw new Error("Failed loading reviews");
                }

                const exportData = await res.json();

                const reviewMap: Record<string, PageReview> = {};

                exportData.review?.forEach((r: {
                    pageIndex: number;
                    status: "pending" | "approved" | "rejected";
                    comment: string;
                    photographerNote?: string;
                }) => {
                    const page = pages[r.pageIndex];
                    if (!page) return;

                    reviewMap[page.id] = {
                        status: r.status,
                        comment: r.comment || "",
                        photographerNote: r.photographerNote || "",
                    };
                });

                setPageReviews(reviewMap);

            } catch (err) {
                console.error("Failed loading reviews", err);
            }
        };

        fetchReview();

    }, [token, albumData?.albumName, pages.length]);




    // Helper function to update pages in both albumData and pages state
     // eslint-disable-next-line react-hooks/exhaustive-deps
    const updatePages = (updateFn: (pages: PageType[]) => PageType[]) => {
        if (!stageSize.width || !stageSize.height) {      
            return;
        }
        const updatedPages = updateFn(pages);
        setAlbumData(prev => ({
            ...prev,
            pages: updatedPages,
            pageCount: updatedPages.length,
        }));

        setPages(updatedPages);
    };
 

    async function preloadImage(url: string) {
        const cache = imageCacheRef.current;

        if (cache.has(url)) return cache.get(url);

        const img = new Image();
        img.src = url;
        await img.decode(); // no layout, GPU-friendly
        cache.set(url, img);
        return img;
    }

  

    //#region Montage

    //load the templates
    useEffect(() => {
        if (!albumType) return;

        // Only for MONTAGE albums
        if (albumType === "MONTAGE") {
            const albumAR = size.width / size.height; // calculate AR
            const arKey = albumAR === 1 ? "1.00" : "2.0"; // square vs horizontal

            // Filter templates by AR
            const moduleTemplates = Object.values(allTemplatesMap)
                .flat()
                .filter(template => {
                    // The template is included if it belongs to this AR
                    const countH = template.slots.filter(s => s.width > s.height).length;
                    const countV = template.slots.filter(s => s.width < s.height).length;
                    const templateKey = `${arKey}_${String(countH + countV).padStart(2, "0")}_H${countH}_V${countV}`;
                    return allTemplatesMap[templateKey]?.includes(template);
                });

            setTemplateThumbnails(moduleTemplates);
        } else {
            // Clear thumbnails for other albums
            setTemplateThumbnails([]);
        }
    }, [albumType, size.width, size.height]);
     // #endregion

    //#region Prints

    const [printThumbnails, setPrintThumbnails] = useState<PrintProduct[]>(PRINT_PRODUCTS);
    const getOrientation = (size: { width: number; height: number }) => {
        if (size.width > size.height) return "horizontal";
        if (size.width < size.height) return "vertical";
        return "square";
    };
    const orientation = getOrientation(size);

    useEffect(() => {
        if (albumType !== "PRINTS") return;

        const products = PRINT_PRODUCTS.map(product => {
            if (
                product.id === "white-border" ||
                product.id === "black-border"
            ) {
                return {
                    ...product,
                    variants: {
                        horizontal: product.variants.horizontal,
                        vertical: product.variants.vertical,
                    },
                };
            }

            return product;
        });

        setPrintThumbnails(products);
    }, [albumType]);


    const handlePrintSelect = (product: PrintProduct) => {
        if (!selectedPageId) return;

        updatePages(prevPages =>
            prevPages.map(page => {
                if (page.id !== selectedPageId) return page;

                // Remove overlay
                if (product.id === "none" || product.overlayFamily === null) {
                    return {
                        ...page,
                        overlay: undefined,
                        elements: [...page.elements],
                    };
                }

                // Apply overlay
                return {
                    ...page,
                    overlay: {
                        type: product.overlayFamily,
                        opacity: 1,
                    },
                    elements: [...page.elements],
                };
            })
        );
    };


       // #endregion
    
    //#region  Load the images when album opens
    React.useEffect(() => {
        if (loadedProjectData) {
            setAlbumData(prev => ({
                ...prev,
                pages: loadedProjectData.data?.pages ?? CreateInitialSpreads(pageCount, 0),
                pageCount: loadedProjectData.data?.pages?.length ?? pageCount,
                uploadedImages: loadedProjectData.uploadedImages ?? [], // ensures PhotoUploader gets the images
            }));
        }
    }, [loadedProjectData, pageCount]);


    useEffect(() => {
        // Only fetch if we have both a valid token and album _id
        if (!token || !albumData?._id) return;

        const fetchAlbumImages = async () => {
            try {
                const res = await fetch(`${API_URL}/albums/${albumData._id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
             
                const albumDataFromServer = await res.json();

                // Extract images safely
                const imagesFromAlbum = albumDataFromServer.data?.images || [];
                const transformed: AlbumImage[] = imagesFromAlbum.map((img: any) => ({
                    id: img.id || uuidv4(),
                    originalUrl: img.originalUrl,
                    smallUrl: img.smallUrl,
                    name: img.name || "Imported",

                    hash: img.hash || "",

                    //  ADD THESE SAFE DEFAULTS
                    orientation: img.orientation ?? "horizontal",

                    width: img.width ?? undefined,
                    height: img.height ?? undefined,

                    originalWidth: img.originalWidth ?? undefined,
                    originalHeight: img.originalHeight ?? undefined,

                    takenAt: img.takenAt ?? null,
                    used: img.used ?? false,
                    rotation: img.rotation ?? 0,
                }));

                setSavedImages(transformed);
            } catch (err) {             
            }
        };

        fetchAlbumImages();
    }, [albumData?._id, token]);

    // #endregion

    //#region Metadata and Cache for the images
    type ImageMeta = {
        id: string;
        orientation: "H" | "V";

        width: number;
        height: number;
        takenAt?: string | null;
        rotation?: number;
        used?: boolean;
    };

    const metadataRef = useRef<Map<string, ImageMeta>>(new Map());

    const refreshMetadata = React.useCallback(async () => {
        if (!token) return;

        let username = "";
        try {
            const decoded = jwtDecode<TokenPayload>(token);
            username = decoded.username;
        } catch {
            return;
        }

        if (!username || !albumName) return;

        try {
            const res = await fetch(
                `${API_URL}/albums/${encodeURIComponent(username)}/${encodeURIComponent(albumName)}/metadata`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (!res.ok) return;

            const data: {
                id: string;
                name: string;

                orientation: "horizontal" | "vertical";

                width: number;
                height: number;

                takenAt?: string | null;

                rotation?: number;
                used?: boolean;

            }[] = await res.json();


            const map = new Map<string, ImageMeta>(
                data.map(d => [
                    d.name,
                    {
                        id: d.id,

                        orientation:
                            d.orientation === "horizontal"
                                ? "H"
                                : "V",

                        width: d.width,
                        height: d.height,

                        takenAt: d.takenAt,

                        rotation: d.rotation ?? 0,
                        used: d.used ?? false,
                    },
                ])
            );

            //  SINGLE source of truth
            metadataRef.current = map;
    

        } catch (err) {
            console.error("Failed to refresh metadata", err);
        }
    }, [API_URL, token, albumName]);

    //save the metadata after import
    const savingAlbumRef = useRef(false);

    const saveMetadata = async (metadataPayload: any[]) => {
        if (!token) return;

        let username = "";
        try {
            const decoded = jwtDecode<TokenPayload>(token);
            username = decoded.username;
        } catch {
            return;
        }

        if (!username || !albumName) return;

        try {
            // --- Save metadata first ---
            const res = await fetch(
                `${API_URL}/albums/${encodeURIComponent(username)}/${encodeURIComponent(albumName)}/metadata`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(metadataPayload),
                }
            );

            if (!res.ok) {
                console.error("Failed to save metadata");
                return;
            }

            const data = await res.json();         

            // --- Save album, but only one save at a time ---
            if (!savingAlbumRef.current) {
                savingAlbumRef.current = true;

                const currentData = albumDataRef.current;

                try {
                    await saveAlbumToServer(
                        currentData,
                        setAlbumData,
                        token,
                        lastSavedDataRef
                    );
                } catch (err) {
                    console.error(err);
                } finally {
                    savingAlbumRef.current = false;
                }
            }
        } catch (err) {
        }
    };



    useEffect(() => {
        refreshMetadata();
    }, [refreshMetadata]);


    //create cash for every image
    useEffect(() => {
        if (!savedImages.length) return;

        let cancelled = false;

        const preloadAll = async () => {
            for (const img of savedImages) {
                if (cancelled) return;
                if (!img.smallUrl) continue;

                // fire & forget — DO NOT await
                preloadImage(img.smallUrl).catch(() => { });
            }
        };

        // Run when browser is idle
        if ("requestIdleCallback" in window) {
            (window as any).requestIdleCallback(preloadAll);
        } else {
            setTimeout(preloadAll, 0);
        }

        return () => {
            cancelled = true;
        };
    }, [savedImages]);


     // #endregion

    // #region  Right click menu
    const [contextMenu, setContextMenu] = useState<{
        x: number;
        y: number;
        slotId: string;
        pageId: string;
    } | null>(null);

    const [copiedSlotEffects, setCopiedSlotEffects] = useState<Partial<Slot> | null>(null);
   


    // Handler called by child
    const handleSlotRightClick = (slotId: string, e: Konva.KonvaEventObject<MouseEvent>) => {
        e.evt.preventDefault();
        setContextMenu({
            x: e.evt.clientX,
            y: e.evt.clientY,
            pageId: selectedPageId!,
            slotId,
        });
    };
   
    const handleCopyEffects = () => {
        if (!contextMenu) return;
        const slot = pages
            .find(p => p.id === contextMenu.pageId)
            ?.slots.find(s => s.id === contextMenu.slotId);
        if (!slot) return;

        setCopiedSlotEffects({
            strokeColor: slot.strokeColor,
            strokeWidth: slot.strokeWidth,
            shadowColor: slot.shadowColor,
            shadowBlur: slot.shadowBlur,
            shadowOpacity: slot.shadowOpacity,
            shadowOffsetX: slot.shadowOffsetX,
            shadowOffsetY: slot.shadowOffsetY,
        });
        setContextMenu(null);
    };



    //Check if it has image
    const contextSlot =
        contextMenu
            ? pages
                .find(p => p.id === contextMenu.pageId)
                ?.slots.find(s => s.id === contextMenu.slotId)
            : null;

    const slotHasImage = !!contextSlot?.imageSrc;
    const canFitSlotToImage = slotHasImage && !!contextSlot?.imageSrc;

    const slotHasEffects = !!contextSlot && (
        contextSlot.strokeWidth !== 0 ||
        contextSlot.shadowOpacity !== 0 ||
        contextSlot.shadowBlur !== 0 ||
 
        contextSlot.blackWhite === true ||
        (contextSlot.opacity !== undefined && contextSlot.opacity !== 1)
    );

    const canCopyEffects = slotHasImage && slotHasEffects;
    const canPasteEffects = !!copiedSlotEffects && slotHasImage;
   


    const handleClearSlotImage = () => {
        if (!contextMenu) return;

        const page = pages.find(p => p.id === contextMenu.pageId);
        const slot = page?.slots.find(s => s.id === contextMenu.slotId);
        if (!slot) return;

        const imageId = slot.imageId;

        handleSlotChange(
            contextMenu.pageId,
            contextMenu.slotId,
            {
                imageId: undefined,
                imageSrc: undefined,
                originalSrc: undefined,
                originalImage: undefined,
                imageScale: undefined,
                imageRotation: 0,
                imageZoom: 1,
                imageCenter: undefined,            
                blackWhite: false,
                opacity: 1,
                quality: undefined
              
            }
        );

        setContextMenu(null);
    };

 
    const handlePasteEffects = () => {
        if (!contextMenu || !copiedSlotEffects) return;

        // If there are selected slots, let handleSlotChange apply to them
        const targetSlotId =
            selectedIds.length > 0
                ? selectedIds[0]          // dummy anchor, will be ignored
                : contextMenu.slotId;     // fallback (single slot)

        handleSlotChange(
            contextMenu.pageId,
            targetSlotId,
            copiedSlotEffects
        );

        setContextMenu(null);
    };


    const canUseFitSlotToImage =
        canFitSlotToImage &&
        !DISABLE_FLIP_ACTIONS.includes(albumType);


    const handleFitSlotToImage = () => {
        if (!contextMenu) return;

        const page = pages.find(p => p.id === contextMenu.pageId);
        const slot = page?.slots.find(s => s.id === contextMenu.slotId);

        if (!slot || !slot.imageSrc) return;

        const img = new Image();

        img.onload = () => {
            const imageRatio = img.width / img.height;

            const centerX = slot.x + slot.width / 2;
            const centerY = slot.y + slot.height / 2;

            let newWidth = slot.width;
            let newHeight = slot.height;

            if (slot.width >= slot.height) {
                // keep width
                newWidth = slot.width;
                newHeight = slot.width / imageRatio;
            } else {
                // keep height
                newHeight = slot.height;
                newWidth = slot.height * imageRatio;
            }

            const newX = centerX - newWidth / 2;
            const newY = centerY - newHeight / 2;

            handleSlotChange(
                contextMenu.pageId,
                contextMenu.slotId,
                {
                    x: newX,
                    y: newY,
                    width: newWidth,
                    height: newHeight,
                    imageZoom: 1,
                    imageCenter: undefined,
                }
            );

            setContextMenu(null);
        };

        img.src = slot.imageSrc;
    };

     // #endregion

    // #region Scale and Resize the stage

  useLayoutEffect(() => {
    const calculateStageSize = () => {
        if (!containerRef.current) return;

        const containerWidth = containerRef.current.offsetWidth;
        const viewportHeight = window.innerHeight;

        const aspectRatio = Number((size.width / size.height).toFixed(2));
       //const availableWidth = containerWidth - (viewportHeight >= 1030 ? 100 : viewportHeight >= 850 ? 300 : 460);
        const isWideAlbum = aspectRatio >= 2.0;
        const availableWidth = isWideAlbum
            ? containerWidth - 100
            : containerWidth -
            (viewportHeight >= 1030
                ? 100
                : viewportHeight >= 850
                    ? 300
                    : 460);

        // Groups of aspect ratios with the same width fraction
        const widthGroups: { fractions: number; ratios: number[] }[] = [
            { fractions: 0.98, ratios: [2.0] },
            { fractions: 0.97, ratios: [1.6] },
            { fractions: 0.96, ratios: [1.57] },
            { fractions: 0.94, ratios: [1.50] },
            { fractions: 0.90, ratios: [1.44, 1.43] },
            { fractions: 0.88, ratios: [1.42, 1.40] },
            { fractions: 0.87, ratios: [1.39] },
            { fractions: 0.84, ratios: [1.33] },
            { fractions: 0.82, ratios: [1.29] },
            { fractions: 0.80, ratios: [1.27, 1.25] },
            { fractions: 0.76, ratios: [1.19, 1.20] },
            { fractions: 0.74, ratios: [1.16] },
            { fractions: 0.64, ratios: [1.0] },
            { fractions: 0.53, ratios: [0.84, 0.83] },
            { fractions: 0.50, ratios: [0.80, 0.79] },
            { fractions: 0.48, ratios: [0.75] },
            { fractions: 0.45, ratios: [0.71, 0.70] },       
            { fractions: 0.43, ratios: [0.67] },
            { fractions: 0.40, ratios: [0.63] },

        ];

        // Find fraction for current AR
        const group = widthGroups.find(g => g.ratios.includes(aspectRatio));
        const fraction = group?.fractions ?? 0.94; // fallback

        const stageWidth = availableWidth * fraction;
        const stageHeight = stageWidth / aspectRatio;

        setStageSize({
            width: Math.round(stageWidth),
            height: Math.round(stageHeight),
        });
    };

    let timeout: NodeJS.Timeout;
    const debouncedResize = () => {
        clearTimeout(timeout);
        timeout = setTimeout(calculateStageSize, 300);
    };

    calculateStageSize();
    window.addEventListener("resize", debouncedResize);
    return () => window.removeEventListener("resize", debouncedResize);
  }, [albumType, size, selectedPage, photoUploaderWidth]);



    const prevStageSize = useRef({
        width: 0,
        height: 0,
    });

    const hasInitializedStageRef = useRef(false);


    useEffect(() => {
        if (!stageSize.width || !stageSize.height) return;

        const savedWidth = loadedProjectData?.layoutSize?.width;
        const savedHeight = loadedProjectData?.layoutSize?.height;

        // First real stage size
        if (!hasInitializedStageRef.current) {
            hasInitializedStageRef.current = true;

            // Existing album:
            // start from the coordinate system in which the album was saved.
            if (savedWidth && savedHeight) {
                prevStageSize.current = {
                    width: savedWidth,
                    height: savedHeight,
                };
            } else {
                // New album: establish current stage as the coordinate system.
                prevStageSize.current = stageSize;
                return;
            }
        }

        const prev = prevStageSize.current;

        if (!prev.width || !prev.height) {
            prevStageSize.current = stageSize;
            return;
        }

        const scaleX = stageSize.width / prev.width;
        const scaleY = stageSize.height / prev.height;
   
        if (scaleX === 1 && scaleY === 1) {
            prevStageSize.current = stageSize;
            return;
        }

        setPages(prevPages => {
         
            const scaledPages = pages.map(page => ({
                ...page,

                slots: page.slots.map(slot => ({
                    ...slot,
                    x: slot.x * scaleX,
                    y: slot.y * scaleY,
                    width: slot.width * scaleX,
                    height: slot.height * scaleY,
                })),

                texts: page.texts?.map(text => ({
                    ...text,
                    x: text.x * scaleX,
                    y: text.y * scaleY,
                    width: text.width
                        ? text.width * scaleX
                        : text.width,
                    fontSize: text.fontSize
                        ? text.fontSize * scaleY
                        : text.fontSize,
                })) ?? [],

                shapes: page.shapes?.map(shape => ({
                    ...shape,
                    x: shape.x * scaleX,
                    y: shape.y * scaleY,
                    width: shape.width * scaleX,
                    height: shape.height * scaleY,
                })) ?? [],
            }));


            setPages(scaledPages);

            setAlbumData(prev => ({
                ...prev,
                pages: scaledPages,
                pageCount: scaledPages.length,
                layoutSize: {
                    width: stageSize.width,
                    height: stageSize.height,
                },
            }));

            // IMPORTANT:
            // Keep albumData in the SAME coordinate system as the pages.
            setAlbumData(prevAlbum => ({
                ...prevAlbum,
                pages: scaledPages,
                pageCount: scaledPages.length,
                layoutSize: {
                    width: stageSize.width,
                    height: stageSize.height,
                },
            }));

            return scaledPages;
        });
      

        // From now on, resize relative to the CURRENT stage.
        prevStageSize.current = stageSize;

    }, [
        stageSize.width,
        stageSize.height,
        loadedProjectData?.layoutSize?.width,
        loadedProjectData?.layoutSize?.height,
    ]);



     // #endregion
 
    // #region Save Album and Go Back to albums
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState<string>("");
    const [dialogCallback, setDialogCallback] = useState<(() => void) | null>(null);
    const [dialogTitle, setDialogTitle] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [dialogSingleButton, setDialogSingleButton] = useState(false);
   
    //   // Handler to trigger the "Go Back" dialog
    const albumDataRef = useRef(albumData);
    useEffect(() => {
        albumDataRef.current = albumData;
    }, [albumData]);

    const handleGoBackClick = async () => {
        if (isSaving) return;

        setIsSaving(true);

        try {
            const currentData = albumDataRef.current;

            const success = await saveAlbumToServer(
                currentData,
                setAlbumData,
                token,
                lastSavedDataRef
            );

            if (!success) {
                alert("Failed to save album. Please check your connection.");
                return;
            }

            // Wait until projectId exists (for new albums)
            if (!currentData.projectId) {
                let tries = 0;
                while (!albumDataRef.current.projectId && tries < 10) {
                    await new Promise((r) => setTimeout(r, 200));
                    tries++;
                }
            }

            navigate("/album-selection", { state: { albumSaved: true } });
        } finally {
            setIsSaving(false);
        }
    };


     // #endregion

    // #region Shape Functions
    const [selectedShapeId, setSelectedShapeId] = React.useState<string | null>(null);
    // for the Names in Greek
    const shapeLabels: Record<ShapeType, string> = {
        rect: "Rectangle",
        star: "Star",
        triangle: "Triangle",
        ellipse: "Ellipse"
    };
   
    const handleAddShape = (type: ShapeType) => {
        if (!selectedPageId) return;

        const newShapeId = uuidv4();

        const newShape: ShapeElement = {
            id: newShapeId,
            type,
            x: stageSize.width / 2,
            y: 250,
            width: 150,
            height: 150,
            rotation: 0,
            fill: '#e0e0e0',
            opacity: 1,
            strokeColor: '#000000',
            strokeWidth: 0,
        };

        updatePages(prevPages => {
            return prevPages.map(page => {
                if (page.id !== selectedPageId) return page;

                const maxZIndex = page.elements.length > 0
                    ? Math.max(...page.elements.map((e: AlbumElement) => e.zIndex))
                    : 0;

                const newElement: AlbumElement = {
                    id: newShapeId,
                    type: 'shape',
                    zIndex: maxZIndex + 1,
                };

                return {
                    ...page,
                    shapes: [...page.shapes, newShape],
                    elements: [...page.elements, newElement],
                };
            });
        });

        setSelectedShapeId(newShapeId);
        setSelectedTextId(null);
        setSelectedSlotIds([]);
        setSelectedElement({ type: 'shape', id: newShapeId });
    };

    const handleSelectShape = (pageId: string, shapeId: string) => {
        if (pageId !== selectedPageId) return;
        setSelectedShapeId(shapeId);
        setSelectedTextId(null);      // clear text selection
        setSelectedSlotIds([]);       // clear image selection
        setSelectedElement({ type: 'shape', id: shapeId });  // update selectedElement
    };

    function handleShapeChange(pageId: string, shapeId: string, changes: Partial<ShapeElement>) {
        updatePages(prev =>
            prev.map(page => {
                if (page.id !== pageId) return page;
                return {
                    ...page,
                    shapes: page.shapes.map(shape =>
                        shape.id === shapeId ? { ...shape, ...changes } : shape
                    ),
                };
            })
        );
    }

    // #endregion

    // #region Text Functions
    const [selectedTextId, setSelectedTextId] = useState<string | null>(null); // Initialize with null
    const [isEditingText, setIsEditingText] = useState<boolean>(false); // NEW STATE for editing mode
    const selectedText = selectedPage?.texts.find((el: TextElement) => el.id === selectedElement?.id) || null;

    const handleAddText = () => {
        if (!selectedPageId) return;

        const newTextId = `text-${Date.now()}`;
        const newText: TextElement = {
            id: newTextId,
            text: "New Text",
            x: stageSize.width / 2-100,
            y: 180,
            fontSize: 28,
            fontFamily: "Arial",
            fill: "#000000",
            fontWeight: "normal",
            align: "left",
            width: 200,
            rotation: 0,
            opacity: 1,
            strokeWidth: 2,         // <-- must have width
            shadowColor: "#000000",

        };

  
        updatePages(prevPages => {
            return prevPages.map(page => {
                if (page.id !== selectedPageId) return page;

                const maxZIndex = page.elements.length > 0
                    ? Math.max(...page.elements.map(e => e.zIndex))
                    : 0;

                const newElement: AlbumElement = {
                    id: newTextId,
                    type: 'text',
                    zIndex: maxZIndex + 1,
                };

                return {
                    ...page,
                    texts: [...page.texts, newText],
                    elements: [...page.elements, newElement],
                };
            });
        });

        setSelectedTextId(newTextId);
        setSelectedShapeId(null);
        setSelectedSlotIds([]);
        setSelectedElement({ type: 'text', id: newTextId });      
    };

     
    // Modify handleSelectText to accept string | null
    const handleSelectText = (textId: string | null) => {
        setSelectedTextId(textId);
        setSelectedSlotIds([]);     // clear slot selection
        setSelectedShapeId(null);   // clear shape selection

        if (textId) {
            setSelectedElement({ type: 'text', id: textId });
        } else {
            setSelectedElement(null);
        }
    };

    // Function to update any attribute of a text element
    const handleTextChange = (textId: string, newAttrs: Partial<TextElement>) => {
        if (newAttrs.fontSize !== undefined) {
            // Round to 2 decimal places
            newAttrs.fontSize = Math.round(newAttrs.fontSize * 100) / 100;
        }
        updatePages((prevPages) =>
            prevPages.map((page) =>
                page.id === selectedPageId
                    ? {
                        ...page,
                        texts: page.texts.map((text) =>
                            text.id === textId ? { ...text, ...newAttrs } : text
                        ),
                    }
                    : page
            )
        );
    };


    const handleTextDragEnd = (textId: string, newPos: { x: number; y: number }) => {
        updatePages((prevPages) =>
            prevPages.map((page) =>
                page.id === selectedPageId
                    ? {
                        ...page,
                        texts: page.texts.map((text) =>
                            text.id === textId ? { ...text, x: newPos.x, y: newPos.y } : text
                        ),
                    }
                    : page
            )
        );

        if (!selectedPageId) return;

        // Clear alignment guides only for the selected page
        setGuidesByPage(prev => ({
            ...prev,
            [selectedPageId]: [],
        }));
    };

    const SNAP_TOLERANCE = 10;
    const handleTextDragMove = (
        draggedTextId: string,
        pos: { x: number; y: number; width: number; height: number }
    ) => {
        if (!selectedPageId) return;

        const page = pages.find(p => p.id === selectedPageId);
        if (!page) return;

        const draggedText = page.texts.find(t => t.id === draggedTextId);
        if (!draggedText) return;

        const movingRect = {
            x: pos.x,
            y: pos.y,
            width: pos.width,
            height: pos.height,
        };

        // Build referenceRects using the same utility function
        const referenceRects = [
            // Slots
            ...page.slots.map(s => ({
                x: s.x,
                y: s.y,
                width: s.width,
                height: s.height,
            })),
            // Texts except self
            ...page.texts
                .filter(t => t.id !== draggedTextId)
                .map(t => ({
                    x: t.x,
                    y: t.y,
                    width: t.width ?? 100,
                    height: (t.fontSize ?? 20) * 1.2,
                })),
            // Shapes
            ...page.shapes.map(sh => ({
                x: sh.x,
                y: sh.y,
                width: sh.width,
                height: sh.height,
            })),
        ];

        const { x: snappedX, y: snappedY, guides } = calculateSnapping({
            movingRect,
            referenceRects,
            stageSize: { width: stageSize.width, height: stageSize.height },
            snapThreshold: SNAP_TOLERANCE,
        });

        handleTextChange(draggedTextId, { x: snappedX, y: snappedY });
        onUpdateGuides?.(guides);

        return { x: snappedX, y: snappedY };
    };

        // #endregion   
  
    // #region Background
   
    // URLs for rendering small previews or the original image
    const [backgroundImageSrcs, setBackgroundImageSrcs] = useState<Record<string, string | null>>({});
    const [backgroundOriginalPaths, setBackgroundOriginalPaths] = useState<Record<string, string | null>>({});

    // HTMLImageElements for actual drawing on canvas/stage (UI only)
    const [, setBackgroundImages] = useState<Record<string, HTMLImageElement | null>>({});

    // Other background properties
    const [backgroundOpacities, setBackgroundOpacities] = useState<Record<string, number>>({});
    const [backgroundZooms, setBackgroundZooms] = useState<Record<string, number>>({});
    const [showBackgrounds, setShowBackgrounds] = useState<Record<string, boolean>>({});
    const [backgroundOffsetsX, setBackgroundOffsetsX] = useState<Record<string, number>>({});
    const [backgroundOffsetsY, setBackgroundOffsetsY] = useState<Record<string, number>>({});
 
    const [backgroundImageOriginalSizes, setBackgroundImageOriginalSizes] = useState<Record<string, { width: number; height: number } | null>>({});
    const [, setBackColor] = useState(backgroundColor ?? "#ffffff");
    const backgroundImagesRef = useRef<{ [pageId: string]: HTMLImageElement | null }>({});
    const didLoadBackgroundsRef = useRef(false);



    // file input ref
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const setShowBackground = (pageId: string, value: boolean) => {
        setShowBackgrounds(prev => ({ ...prev, [pageId]: value }));
        updatePages(prev =>
            prev.map(p => (p.id === pageId ? { ...p, showBackground: value } : p))
        );
    };

    const setBackgroundOpacity = (pageId: string, value: number) => {
        setBackgroundOpacities(prev => ({ ...prev, [pageId]: value }));
        updatePages(prev =>
            prev.map(p => (p.id === pageId ? { ...p, backgroundOpacity: value } : p))
        );
    };

    const setBackgroundZoom = (pageId: string, value: number) => {
        setBackgroundZooms(prev => ({ ...prev, [pageId]: value }));
        updatePages(prev =>
            prev.map(p => (p.id === pageId ? { ...p, backgroundZoom: value } : p))
        );
    };

    const setBackgroundImageSrc = (pageId: string, value: string | null) => {
        setBackgroundImageSrcs(prev => ({ ...prev, [pageId]: value }));
        updatePages(prev =>
            prev.map(p => (p.id === pageId ? { ...p, backgroundImageSrc: value } : p))
        );
    };

   
    const setBackgroundOffsetX = (pageId: string, value: number) => {
        setBackgroundOffsetsX(prev => ({ ...prev, [pageId]: value }));
        updatePages(prev =>
            prev.map(p => p.id === pageId ? { ...p, backgroundOffsetX: value } : p)
        );
    };

    const setBackgroundOffsetY = (pageId: string, value: number) => {
        setBackgroundOffsetsY(prev => ({ ...prev, [pageId]: value }));
        updatePages(prev =>
            prev.map(p => p.id === pageId ? { ...p, backgroundOffsetY: value } : p)
        );
    };



    const setPageBackgroundImage = (
        pageId: string,
        smallSrc: string | null,        // small image for UI
        opacity: number = 1,
        zoom: number = 1,
        offsetX: number = 0,
        offsetY: number = 0
    ) => {
        if (!pageId) return;

        // Compute original path
        let originalSrc: string | null = null;
        if (smallSrc) {
            // Keep the smallSrc as-is for UI
            // For export, compute absolute URL
            const relativeOriginal = smallSrc.replace("/small/", "/originals/");
            originalSrc = relativeOriginal.startsWith("http")
                ? relativeOriginal
                : window.location.origin + relativeOriginal;
        }

        // --- Load image for UI rendering ---
        if (smallSrc) {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = smallSrc;

            img.onload = () => {
                backgroundImagesRef.current[pageId] = img;
                setBackgroundImages(prev => ({ ...prev, [pageId]: img }));
                setBackgroundImageOriginalSizes(prev => ({
                    ...prev,
                    [pageId]: { width: img.width, height: img.height },
                }));
            };

            img.onerror = () => {
                backgroundImagesRef.current[pageId] = null;
                setBackgroundImages(prev => ({ ...prev, [pageId]: null }));
                setBackgroundImageOriginalSizes(prev => ({ ...prev, [pageId]: null }));
            };
        } else {
            backgroundImagesRef.current[pageId] = null;
            setBackgroundImages(prev => ({ ...prev, [pageId]: null }));
            setBackgroundImageOriginalSizes(prev => ({ ...prev, [pageId]: null }));
        }

        // --- Update UI state ---
        setBackgroundImageSrc(pageId, smallSrc);
        setBackgroundOriginalPaths(prev => ({ ...prev, [pageId]: originalSrc }));
        setBackgroundOpacities(prev => ({ ...prev, [pageId]: opacity }));
        setBackgroundZooms(prev => ({ ...prev, [pageId]: zoom }));
        setBackgroundOffsetsX(prev => ({ ...prev, [pageId]: offsetX }));
        setBackgroundOffsetsY(prev => ({ ...prev, [pageId]: offsetY }));
        setShowBackground(pageId, !!smallSrc);

        // --- Update albumData.pages for export ---
        setAlbumData(prev => ({
            ...prev,
            pages: prev.pages.map(page =>
                page.id === pageId
                    ? {
                        ...page,
                        backgroundImageSrc: smallSrc,      // small image for UI
                        backgroundOriginalSrc: originalSrc, // original image for export
                        backgroundOpacity: opacity,
                        backgroundZoom: zoom,
                        backgroundOffsetX: offsetX,
                        backgroundOffsetY: offsetY,
                        showBackground: !!smallSrc,
                    }
                    : page
            ),
        }));

        // --- Update separate pages state if used ---
        updatePages(prev =>
            prev.map(page =>
                page.id === pageId
                    ? {
                        ...page,
                        backgroundImageSrc: smallSrc,
                        backgroundOriginalSrc: originalSrc,
                        backgroundOpacity: opacity,
                        backgroundZoom: zoom,
                        backgroundOffsetX: offsetX,
                        backgroundOffsetY: offsetY,
                        showBackground: !!smallSrc,
                    }
                    : page
            )
        );
    };


    // --- Background image upload handler ---
    const handleBackgroundImageUpload = async (pageId: string, file: File) => {
        try {
            const { originalUrl, smallUrl } = await uploadAndResizeImage(
                file,
                albumName,
                true,  // isBackgroundImage
                true   // saveOriginal
            );

            // --- Normalize URLs to current frontend origin --- 
            // Background images only: force full URL to match the server
            const normalizeBackgroundUrl = (url: string) =>
                url?.startsWith("http")
                    ? url
                    : `https://app.seldex.com.au${url}`;

            const safeOriginalUrl = normalizeBackgroundUrl(originalUrl);
            const safeSmallUrl = normalizeBackgroundUrl(smallUrl);

            // Update page state
            updatePages(prevPages =>
                prevPages.map(page =>
                    page.id === pageId
                        ? {
                            ...page,
                            backgroundImageSrc: safeSmallUrl,
                            backgroundOriginalSrc: safeOriginalUrl,
                        }
                        : page
                )
            );

            // Update refs + state for immediate rendering
            setPageBackgroundImage(pageId, safeSmallUrl);

        } catch (err: any) {
            alert(`Background upload failed for ${file.name}: ${err.message}`);
        }
    };




    // --- Preload background images on album load ---
    useEffect(() => {
        if (!albumData.pages || didLoadBackgroundsRef.current) return;

        albumData.pages.forEach((page, idx) => {
            const fn = () => {            
                setPageBackgroundImage(
                    page.id,
                    page.backgroundImageSrc ?? null,
                    page.backgroundOpacity ?? 1,
                    page.backgroundZoom ?? 1,
                    page.backgroundOffsetX ?? 0,
                    page.backgroundOffsetY ?? 0,
                );
            };

            if (idx === 0) {
                // Delay the first page a tiny bit
                setTimeout(fn, 50);
            } else {
                fn();
            }
        });

        didLoadBackgroundsRef.current = true;
    }, [albumData.pages]);


    useEffect(() => {
        if (!selectedPageId) return;

        const currentPage = pages.find(p => p.id === selectedPageId);
        if (currentPage?.backgroundColor) {
            setBackColor(currentPage.backgroundColor);
        } else {
            setBackColor("#ffffff");
        }
    }, [selectedPageId, pages]);



    const handleRemoveBackground = (pageId: string) => {
        if (!pageId) return;

        // Clear small and original image paths
        setBackgroundImageSrc(pageId, null);
        setBackgroundOriginalPaths(prev => ({ ...prev, [pageId]: null }));

        // Immediately remove the HTMLImageElement
        backgroundImagesRef.current[pageId] = null;
        setBackgroundImages(prev => ({ ...prev, [pageId]: null }));
        setBackgroundImageOriginalSizes(prev => ({ ...prev, [pageId]: null }));

        // Hide background
        setShowBackground(pageId, false);

        // Reset derived values
        setBackgroundOpacities(prev => ({ ...prev, [pageId]: 1 }));
        setBackgroundZooms(prev => ({ ...prev, [pageId]: 1 }));
        setBackgroundOffsetsX(prev => ({ ...prev, [pageId]: 0 }));
        setBackgroundOffsetsY(prev => ({ ...prev, [pageId]: 0 }));

        // Also update albumData.pages for autosave
        setAlbumData(prev => ({
            ...prev,
            pages: prev.pages.map(page =>
                page.id === pageId
                    ? {
                        ...page,
                        backgroundImageSrc: null,
                        backgroundOriginalSrc: null,
                        showBackground: false,
                        backgroundOpacity: 1,
                        backgroundZoom: 1,
                        backgroundOffsetX: 0,
                        backgroundOffsetY: 0,
                    }
                    : page
            ),
        }));

        // Update your "pages" state if you have a separate one
        updatePages(prev =>
            prev.map(page =>
                page.id === pageId
                    ? {
                        ...page,
                        backgroundImageSrc: null,
                        backgroundOriginalSrc: null,
                        showBackground: false,
                        backgroundOpacity: 1,
                        backgroundZoom: 1,
                        backgroundOffsetX: 0,
                        backgroundOffsetY: 0,
                    }
                    : page
            )
        );
    };

    //Update the background images after add 1
    React.useEffect(() => {
        if (!albumData.pages) return;

        const newSrcs: Record<string, string | null> = {};
        albumData.pages.forEach(page => {
            newSrcs[page.id] = page.backgroundImageSrc ?? null;
        });
        setBackgroundImageSrcs(newSrcs);
    }, [albumData.pages]);

      // #endregion
  
    // #region UseEffect

    // images upload
    useEffect(() => {
        const stateImages = location.state?.uploadedImages;      
        if (stateImages && Array.isArray(stateImages)) {
            setUploadedImages(stateImages);
        }
    }, [location.state]);


    useEffect(() => {
        if (selectedTemplate) {
            setTemplateSlots(selectedTemplate.slots);
        } else {
            setTemplateSlots([]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTemplate]);


    // Adjust pages count if pageCount changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        updatePages(prevPages => {
            const prevCount = prevPages.length;

            if (pageCount === prevCount) return prevPages;

            if (pageCount > prevCount) {
                const newPages = CreateInitialSpreads(
                    pageCount - prevCount,
                    prevCount,
                    backgroundColor ?? '#ffffff'
                );
                return [...prevPages, ...newPages];
            } else {
                return prevPages.slice(0, pageCount);
            }
        });
    }, [pageCount, backgroundColor]);


    useEffect(() => {
        const preventDefaults = (e: DragEvent) => {
            e.preventDefault();
        };

        window.addEventListener("dragover", preventDefaults);
        window.addEventListener("drop", preventDefaults);

        return () => {
            window.removeEventListener("dragover", preventDefaults);
            window.removeEventListener("drop", preventDefaults);
        };
    }, []);


    // Helper to get a clean snapshot of album data
    function getSerializableAlbumData(data: AlbumSaveData) {
        return {
            ...data,
            pages: data.pages.map(page => ({
                ...page,
                backgroundOriginalSrc: page.backgroundOriginalSrc ?? null,
                backgroundImageSrc: page.backgroundImageSrc ?? null,
                backgroundColor: page.backgroundColor ?? "#ffffff",
                backgroundOpacity: page.backgroundOpacity ?? 1,
                backgroundZoom: page.backgroundZoom ?? 1,
                backgroundOffsetX: page.backgroundOffsetX ?? 1,
                backgroundOffsetY: page.backgroundOffsetY ?? 1,
                showBackground: page.showBackground ?? false,
                slots: [...page.slots],
                texts: [...page.texts],
                shapes: [...page.shapes],
                elements: [...page.elements],
            })),
            uploadedImages: [...data.uploadedImages],
        };
    }

    // Utility to deep compare
    function isDifferent(a: any, b: any) {
        return JSON.stringify(a) !== JSON.stringify(b);
    }

    // Autosave effect
    useEffect(() => {
        let saveTimeout: NodeJS.Timeout | null = null;
    
        const saveIfChanged = async (force = false) => {
            const snapshot = albumData;

            if (isSavingRef.current) return;
            isSavingRef.current = true;

            const isNew = !snapshot.projectId;

            if (isNew) {
                if (isCreatingRef.current) return;
                isCreatingRef.current = true;
            }

            try {
                const currentData = getSerializableAlbumData(snapshot);
                const lastSaved = getSerializableAlbumData(lastSavedDataRef.current);

                if (!force && !isDifferent(currentData, lastSaved)) return;

                setSaveStatus("saving");

                const success = await saveAlbumToServer(
                    currentData,
                    setAlbumData,
                    token,
                    lastSavedDataRef
                );

                if (success) {
                    lastSavedDataRef.current = JSON.parse(JSON.stringify(currentData));

                    if (force) didInitialSaveRef.current = true;

                    setSaveStatus("saved");
                    setTimeout(() => setSaveStatus("idle"), 1000);
                }
            } finally {
                isSavingRef.current = false;

                if (!snapshot.projectId) {
                    isCreatingRef.current = false;
                }
            }
        };
        //  initial force-save (once)
        if (!didInitialSaveRef.current && !isCreatingRef.current) {
            const ready =
                albumData.albumName &&
                albumData.albumType &&
                albumData.size &&
                albumData.pages?.length > 0 &&
                albumData.layoutSize.width > 0 &&
                albumData.layoutSize.height > 0;

            if (ready) saveIfChanged(true);
        }

        //  debounce saves — only save after 2 seconds of no further edits
        if (saveTimeout) clearTimeout(saveTimeout);
        saveTimeout = setTimeout(() => saveIfChanged(false), 1000);

        //  also do a safety autosave every 10s in case no edits trigger debounce
        const interval = setInterval(() => saveIfChanged(false), 10000);

        return () => {
            if (saveTimeout) clearTimeout(saveTimeout);
            clearInterval(interval);
        };
    }, [albumData, token]); //  safe now — debounce prevents infinite loop


    // Save on app closing
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const handleBeforeUnload = () => {
            const currentData = getSerializableAlbumData(albumData);
            const lastSaved = getSerializableAlbumData(lastSavedDataRef.current);

            if (isDifferent(currentData, lastSaved)) {
                const payload = {
                    albumName: albumData.albumName,
                    albumType: albumData.albumType,
                    albumSize: albumData.size,
                    layoutSize: albumData.layoutSize,
                    pageCount: albumData.pageCount,
                    backgroundColor: albumData.backgroundColor,
                    cutArea: albumData.cutArea,
                    spineWidth: albumData.spineWidth,
                    opening: albumData.opening ?? null,
                    handtornMode: albumData.handtornMode ?? null,

                    data: currentData.pages,
                };

                const url = albumData.projectId
                    ? `${API_URL}/albums/${albumData.projectId}`
                    : `${API_URL}/albums`;

                // Use navigator.sendBeacon with token in headers
                const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });

                if (token) {
                    // synchronous fetch to ensure token is sent before unload
                    navigator.sendBeacon(url, blob);
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);



    // Update the refs array to match pages length in scroll
    useEffect(() => {
        pageRefs.current = pageRefs.current.slice(0, pages.length);
    }, [pages.length]);


    // Sync the refs
    useEffect(() => {
        if (containerRef.current) {
            divRef.current = containerRef.current;
        }
    }, []);


  
    // Scroll functionality in pages
    useEffect(() => {
        const handleScroll = () => {
            if (!containerRef.current || pageRefs.current.length === 0) return;

            const containerTop = containerRef.current.scrollTop;
            const containerHeight = containerRef.current.clientHeight;

            const visibleThreshold = 0.80;

            let bestMatchId: string | null = null;
            let bestVisibility = 0;

            pageRefs.current.forEach((pageEl, index) => {
                if (!pageEl) return;

                const pageTop = pageEl.offsetTop;
                const pageHeight = pageEl.clientHeight;

                const visibleTop = Math.max(pageTop, containerTop);
                const visibleBottom = Math.min(pageTop + pageHeight, containerTop + containerHeight);

                const visibleHeight = visibleBottom - visibleTop;
                const visibilityRatio = visibleHeight / pageHeight;

                if (visibilityRatio >= visibleThreshold && visibilityRatio > bestVisibility) {
                    bestMatchId = pages[index].id;
                    bestVisibility = visibilityRatio;
                }
            });

            if (bestMatchId && bestMatchId !== selectedPageId) {
                // Use startTransition to mark this update as non-urgent
                startTransition(() => {
                    setSelectedPageId(bestMatchId);
                });
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener("scroll", handleScroll, { passive: true });
        }

        return () => {
            if (container) {
                container.removeEventListener("scroll", handleScroll);
            }
        };
    }, [pages, selectedPageId]);



      // #endregion

    // #region Duplicate-Delete
    const handleDuplicate = useCallback(() => {
        if (!selectedPageId) return;

        let duplicatedSlotIds: string[] = [];
        let duplicatedTextIds: string[] = [];
        let duplicatedShapeIds: string[] = [];

        //  Build new pages with duplicated items
        const newPages = pages.map(page => {
            if (page.id !== selectedPageId) return page;

            const newSlots: Slot[] = [];
            const newTexts: TextElement[] = [];
            const newShapes: ShapeElement[] = [];
            const newElements = [...page.elements];

            let zIndexCounter = newElements.length;

            // Collect all currently selected IDs
            const effectiveSelectedIds = new Set([
                ...selectedIds, // slot IDs for this page
                ...(selectedTextId ? [selectedTextId] : []),
                ...(selectedShapeId ? [selectedShapeId] : []),
            ]);

            for (const id of effectiveSelectedIds) {
                const slot = page.slots.find(s => s.id === id);
                const text = page.texts.find(t => t.id === id);
                const shape = page.shapes.find(sh => sh.id === id);
                const newId = uuidv4();

                if (slot) {
                    newSlots.push({ ...slot, id: newId, x: slot.x + 20, y: slot.y + 20 });
                    newElements.push({ id: newId, type: 'slot', zIndex: zIndexCounter++ });
                    duplicatedSlotIds.push(newId);
                } else if (text) {
                    newTexts.push({ ...text, id: newId, x: text.x + 20, y: text.y + 20 });
                    newElements.push({ id: newId, type: 'text', zIndex: zIndexCounter++ });
                    duplicatedTextIds.push(newId);
                } else if (shape) {
                    newShapes.push({ ...shape, id: newId, x: shape.x + 20, y: shape.y + 20 });
                    newElements.push({ id: newId, type: 'shape', zIndex: zIndexCounter++ });
                    duplicatedShapeIds.push(newId);
                }
            }

            return {
                ...page,
                slots: [...page.slots, ...newSlots],
                texts: [...page.texts, ...newTexts],
                shapes: [...page.shapes, ...newShapes],
                elements: newElements,
            };
        });

        // Update pages
        updatePages(() => newPages);

        // Update selection
        if (duplicatedSlotIds.length > 0) {
            requestAnimationFrame(() => {
                setSelectedSlotIdsByPage(prev => ({
                    ...prev,
                    [selectedPageId]: duplicatedSlotIds
                }));
            });
        }

        setSelectedTextId(duplicatedTextIds[0] ?? null);
        setSelectedShapeId(duplicatedShapeIds[0] ?? null);
    }, [
        selectedPageId,
        pages,
        selectedIds,
        selectedTextId,
        selectedShapeId,
        updatePages,
        setSelectedSlotIdsByPage
    ]);

    // Function to delete items from the selected page
    const deleteSelectedShapesAndTexts  = () => {
        if (!selectedPageId) return;

        updatePages(prevPages =>
            prevPages.map(page => {
                if (page.id !== selectedPageId) return page;

                // Filter out deleted items          
                const updatedTexts = page.texts.filter(text => text.id !== selectedTextId);
                const updatedShapes = page.shapes?.filter(shape =>
                    shape.id !== selectedShapeId && !selectedIds.includes(shape.id)
                ) ?? [];

                // Filter out elements matching the deleted IDs
                const updatedElements = page.elements.filter(el => {
                    if (el.type === "text") return el.id !== selectedTextId;
                    if (el.type === "shape") return el.id !== selectedShapeId;
                    return true;
                })
                    // Recalculate zIndex sequentially
                    .map((el, index) => ({
                        ...el,
                        zIndex: index,
                    }));

                return {
                    ...page,                
                    texts: updatedTexts,
                    shapes: updatedShapes,
                    elements: updatedElements,
                };
            })
        );

        // Clear all selections
        setSelectedTextId(null);
        setSelectedShapeId(null);
    };

        //Delete button call
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleDeleteButton = useCallback(() => {
        // Delete only slots if selected and the Album is Montage
        if (albumType === "MONTAGE") return;

        if (selectedIds.length > 0) {
            deleteSelectedSlots();
        }

        // Delete text or shape if selected
        if (selectedTextId || selectedShapeId) {
            deleteSelectedShapesAndTexts();
        }
    }, [selectedIds, selectedTextId, selectedShapeId, deleteSelectedShapesAndTexts]);

    const deleteSelectedSlots = () => {
        if (!selectedPageId) return;

        const page = pages.find(p => p.id === selectedPageId);
        if (!page) return;

        const isManual = editorMode === "manual";

        updatePages(prevPages =>
            prevPages.map(p => {
                if (p.id !== selectedPageId) return p;

                // --- Remove selected slots ---
                const remainingSlots = p.slots.filter(slot => !selectedIds.includes(slot.id));

                // --- Keep non-slot elements, remove only slot elements that were deleted ---
                let updatedElements = p.elements.filter(el =>
                    el.type !== "slot" || !selectedIds.includes(el.id)
                );

                // --- Collect remaining images from remaining slots ---
                type SlotImage = { originalSrc: string; imageSrc?: string; orientation: "H" | "V" };
                const remainingImages: SlotImage[] = remainingSlots
                    .filter(slot => slot.originalSrc)
                    .map(slot => ({
                        originalSrc: slot.originalSrc!,
                        imageSrc: slot.imageSrc,
                        orientation: slot.width >= slot.height ? "H" : "V",
                    }));

                let updatedSlots: typeof p.slots = remainingSlots;

                if (!isManual && remainingImages.length > 0) {
                    // --- Auto mode: reload template based on remaining images ---
                    const countH = remainingImages.filter(img => img.orientation === "H").length;
                    const countV = remainingImages.filter(img => img.orientation === "V").length;                            
                    const templateKey = `${openAR}_${String(remainingImages.length).padStart(2, "0")}_H${countH}_V${countV}`;
                    const moduleTemplates = allTemplatesMap[templateKey];

                    if (moduleTemplates && moduleTemplates.length > 0) {
                        setTemplateThumbnails(moduleTemplates);
                        const templateSlots = moduleTemplates[0].slots;
                        const used = new Set<string>();
                        const scale = getTemplateScale(moduleTemplates[0].size, stageSize);

                        updatedSlots = templateSlots.map(slot => {
                            const slotOrientation: "H" | "V" = slot.width >= slot.height ? "H" : "V";
                            let match = remainingImages.find(img => !used.has(img.originalSrc) && img.orientation === slotOrientation);
                            if (!match) {
                                match = remainingImages.find(img => !used.has(img.originalSrc));
                            }
                            if (match) used.add(match.originalSrc);

                            const rawName = match?.originalSrc?.split("/").pop() || "";
                            const name = decodeURIComponent(rawName).split("?")[0];
                            const meta = metadataRef.current.get(name);

                            const newSlot = {
                                ...slot,
                                id: uuidv4(),

                                x: (slot.x ?? 0) * scale,
                                y: (slot.y ?? 0) * scale,
                                width: (slot.width ?? 100) * scale,
                                height: (slot.height ?? 100) * scale,

                                imageSrc: match?.imageSrc || match?.originalSrc,
                                originalSrc: match?.originalSrc,

                                imageWidth: meta?.width,
                                imageHeight: meta?.height,

                                imageX: 0,
                                imageY: 0,
                                imageRotation: 0,
                            };

                            return calculateSlotQuality(newSlot, pixelsPerInch);
                        });

                        // RESET GAP BASELINE AFTER TEMPLATE REBUILD
                        originalSlotsRef.current[p.id] =
                            updatedSlots.map(s => ({ ...s }));

                        // Add new slot elements for auto mode
                        updatedElements = [
                            ...updatedElements.filter(el => el.type !== "slot"), // keep texts/shapes
                            ...updatedSlots.map((slot, idx) => ({
                                id: slot.id,
                                type: "slot" as const,
                                zIndex: idx + 1,
                            }))
                        ];
                    }
                }

                return { ...p, slots: updatedSlots, elements: updatedElements };
            })
        );

        // --- Update marked images ---
        const usedImageSrcs = new Set(
            pages.flatMap(page => page.slots.filter(slot => slot.imageSrc).map(slot => slot.imageSrc))
        );

        setMarkedImages(prev =>
            prev.map(img => ({
                ...img,
                used: usedImageSrcs.has(img.originalUrl) || usedImageSrcs.has(img.smallUrl),
            }))
        );

        // --- Clear selections ---
        setSelectedSlotIds([]);
    };


      // #endregion

    // #region Layer Order Functions

    // Normalize zIndex to be sequential from 0
    function normalizeZIndices(elements: typeof pages[0]['elements']) {
        //  Create a new array sorted by zIndex
        const sorted = [...elements].sort((a, b) => a.zIndex - b.zIndex);

        //  Return a new array with normalized zIndex (from 0 up)
        return sorted.map((el, i) => ({
            ...el,
            zIndex: i,
        }));
    }

    // Bring element forward by one step
    function bringForward(elementId: string) {
        updatePages(prevPages => {
            const pageIndex = prevPages.findIndex(page => page.id === selectedPageId);
            if (pageIndex === -1) return prevPages;

            const page = prevPages[pageIndex];
            let newElements = [...page.elements];

            newElements = normalizeZIndices(newElements);

            const index = newElements.findIndex(el => el.id === elementId);
            if (index === -1 || index === newElements.length - 1) return prevPages;

            // Swap zIndex with the next element
            [newElements[index].zIndex, newElements[index + 1].zIndex] =
                [newElements[index + 1].zIndex, newElements[index].zIndex];

            newElements = normalizeZIndices(newElements);

            const newPages = [...prevPages];
            newPages[pageIndex] = { ...page, elements: newElements };
            return newPages;
        });
    }

    // Send element backward by one step
    function sendBackward(elementId: string) {
        updatePages(prevPages => {
            const pageIndex = prevPages.findIndex(page => page.id === selectedPageId);
            if (pageIndex === -1) return prevPages;

            const page = prevPages[pageIndex];
            let newElements = [...page.elements];

            newElements = normalizeZIndices(newElements);

            const index = newElements.findIndex(el => el.id === elementId);
            if (index <= 0) return prevPages;

            // Swap zIndex with the previous element
            [newElements[index].zIndex, newElements[index - 1].zIndex] =
                [newElements[index - 1].zIndex, newElements[index].zIndex];

            newElements = normalizeZIndices(newElements);

            const newPages = [...prevPages];
            newPages[pageIndex] = { ...page, elements: newElements };
            return newPages;
        });
    }


    // Bring element to the front (top)
    function bringToFront(elementId: string) {
        updatePages(prevPages => {
            const pageIndex = prevPages.findIndex(p => p.id === selectedPageId);
            if (pageIndex === -1) return prevPages;

            const page = prevPages[pageIndex];
            let newElements = [...page.elements];

            newElements = normalizeZIndices(newElements);

            const element = newElements.find(el => el.id === elementId);
            if (!element) return prevPages;

            const maxZ = newElements.length - 1;
            element.zIndex = maxZ + 1;

            newElements = normalizeZIndices(newElements);

            const newPages = [...prevPages];
            newPages[pageIndex] = { ...page, elements: newElements };
            return newPages;
        });
    }


    // Send element to the back (bottom)
    function sendToBack(elementId: string) {
        updatePages(prevPages => {
            const pageIndex = prevPages.findIndex(p => p.id === selectedPageId);
            if (pageIndex === -1) return prevPages;

            const page = prevPages[pageIndex];
            let newElements = [...page.elements];

            newElements = normalizeZIndices(newElements);

            const element = newElements.find(el => el.id === elementId);
            if (!element) return prevPages;

            element.zIndex = -1;

            newElements = normalizeZIndices(newElements);

            const newPages = [...prevPages];
            newPages[pageIndex] = { ...page, elements: newElements };
            return newPages;
        });
    }

     // #endregion

    // #region Slot
    const AUTO_SLOT_ALBUMS = [
        "PRINTS",
        "FMOUNTS",
        "HIPSTER",
        "CANVAS",
        "FCANVAS",
        "ARCH",
        "FPRINTS",
        "HANDTORN",
    ];


    useEffect(() => {
        if (!isPagesReady) return;

        if (!AUTO_SLOT_ALBUMS.includes(albumType)) return;

        const hasEmptyPages = pages.some(page => page.slots.length === 0);

        if (!hasEmptyPages) return;

        updatePages(prevPages =>
            prevPages.map(page => {
                if (page.slots.length > 0) return page;

                return addFullSlotToPage(page, albumType);
            })
        );

    }, [isPagesReady, albumType, pages]);




    function addFullSlotToPage(page: PageType, albumType: string): PageType {
        const id = uuidv4();

        const maxZ =
            page.elements.length
                ? Math.max(...page.elements.map(e => e.zIndex))
                : 0;

        let newSlot: Slot;

        const scale = stageSize.width / albumData.size.width;

        //  HIPSTER
        if (albumType === "HIPSTER") {
            const slotSize = 3.323;

            const width = slotSize * scale;
            const height = slotSize * scale;

            newSlot = {
                id,
                x: (stageSize.width - width) / 2,
                y: 0.34 * scale,
                width,
                height,
                rotation: 0,
                imageRotation: 0,
                opacity: 1,
                imageId: undefined,
                imageSrc: undefined,
                originalSrc: undefined,
                imageScale: 1,
                fill: "#e0e0e0",
                strokeColor: "#000",
                strokeWidth: 0,
                shadowBlur: 0,
            };
        }

        //  FMOUNTS-PRINTS
        else if (
            albumType === "ARCH" ||
            albumType === "FMOUNTS" ||
            albumType === "FPRINTS" ||
            albumType === "HANDTORN"
        ) {
            const opening = selectedOpening ?? albumData.opening ?? openings?.[0];
            const { w, h } = parseSize(opening);

            const slotW = w ;
            const slotH = h ;

            const width = slotW * scale;
            const height = slotH * scale;

            newSlot = {
                id,
                x: (stageSize.width - width) / 2,
                y: (stageSize.height - height) / 2,
                width,
                height,
                rotation: 0,
                imageRotation: 0,
                opacity: 1,
                imageId: undefined,
                imageSrc: undefined,
                originalSrc: undefined,
                imageScale: 1,
                fill: "#e0e0e0",
                strokeColor: "#000",
                strokeWidth: 0,
                shadowBlur: 0,
            };
        }
      
        //  DEFAULT
        else {
            newSlot = {
                id,
                x: 0,
                y: 0,
                width: stageSize.width,
                height: stageSize.height,
                rotation: 0,
                imageRotation: 0,
                opacity: 1,
                imageId: undefined,
                imageSrc: undefined,
                originalSrc: undefined,
                imageScale: 1,
                fill: "#e0e0e0",
                strokeColor: "#000",
                strokeWidth: 0,
                shadowBlur: 0,
            };
        }

        const newElement: AlbumElement = {
            id,
            type: "slot",
            zIndex: maxZ + 1,
        };

        return {
            ...page,
            slots: [...page.slots, newSlot],
            elements: [...page.elements, newElement],
        };
    }



    const handleAddSlot = (
        orientation: "horizontal" | "vertical" | "square" | "full"
    ) => {
        if (!selectedPageId) return;

        let width: number, height: number, x: number, y: number;

        const CenterX = stageSize.width / 2;
        const CenterY = stageSize.height / 2;

        if (orientation === "full") {

            if (
                albumType === "ARCH" ||
                albumType === "FMOUNTS" ||
                albumType === "FPRINTS" ||
                albumType === "HANDTORN"
            ) {
                const opening = selectedOpening ?? openings[0];
                if (!opening) return;

                const { w, h } = parseSize(opening);

                const scale = stageSize.width / albumData.size.width;
                width = w * scale;
                height = h * scale;

                x = (stageSize.width - width) / 2;
                y = (stageSize.height - height) / 2;
           
            } else {
                width = stageSize.width;
                height = stageSize.height;
                x = 0;
                y = 0;
            }
           
        
        } else {

            [width, height] =
                orientation === "horizontal"
                    ? [300, 200]
                    : orientation === "vertical"
                        ? [200, 300]
                        : [250, 250]; // square

            x = CenterX - width / 2;
            y = CenterY - height / 2;
        }

        const newSlotId = uuidv4();

        const newSlot: Slot = {
            id: newSlotId,
            x,
            y,
            width,
            height,
            rotation: 0,
            imageRotation: 0,
            opacity: 1,
            imageId: undefined,
            imageSrc: undefined,
            originalSrc: undefined,
            imageScale: 1,
            fill: "#e0e0e0",
            strokeColor: "#000000",
            strokeWidth: 0,
            shadowColor: "#000000",
            shadowBlur: 0,
            shadowOpacity: 0,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            scaleX: 1,
            scaleY: 1,
            blackWhite: false,        
        };

        updatePages(prevPages =>
            prevPages.map(page => {
                if (page.id !== selectedPageId) return page;

                const maxZIndex =
                    page.elements.length > 0
                        ? Math.max(...page.elements.map(e => e.zIndex))
                        : 0;

                const newElement: AlbumElement = {
                    id: newSlotId,
                    type: "slot",
                    zIndex: maxZIndex + 1,
                };

                return {
                    ...page,
                    slots: [...page.slots, newSlot],
                    elements: [...page.elements, newElement],
                };
            })
        );


        setSelectedSlotIds([newSlotId]);
        setSelectedShapeId(null);
        setSelectedTextId(null);
        setSelectedElement({ type: "slot", id: newSlotId });
    };



    // Function to set selected slot IDs for current page
    const setSelectedSlotIds = (ids: string[]) => {
        if (!selectedPageId) return;
        setSelectedSlotIdsByPage(prev => ({
            ...prev,
            [selectedPageId]: ids,
        }));
    };


  


    function handleSlotChange(pageId: string, slotId: string, changes: Partial<Slot>) {
        updatePages((prevPages) =>
            prevPages.map((page) => {
                if (page.id !== pageId) return page;

                const newSlots = page.slots.map((slot) => {
                    // if multiple are selected, apply to all selected ones
                    const shouldUpdate =
                        selectedIds.length > 1 ? selectedIds.includes(slot.id) : slot.id === slotId;

                    if (!shouldUpdate) return slot;

                    let nextSlot: Slot = {
                        ...slot,
                        ...changes,
                    };

                    // explicit clearing semantics
                    if ("originalSrc" in changes && changes.originalSrc === null) {
                        delete (nextSlot as any).originalSrc;
                    }

                    if ("imageSrc" in changes && changes.imageSrc === null) {
                        delete (nextSlot as any).imageSrc;
                    }

                    if ("imageId" in changes && changes.imageId === null) {
                        delete (nextSlot as any).imageId;
                    }

                    if ("originalImage" in changes && changes.originalImage === null) {
                        delete (nextSlot as any).originalImage;
                    }

                    // keep your opacity fallback logic
                    nextSlot.opacity = changes.opacity ?? slot.opacity ?? 1;

                    nextSlot = calculateSlotQuality(
                        nextSlot,
                        pixelsPerInch
                    );

                    return nextSlot;
                });

                return {
                    ...page,
                    slots: newSlots,
                };
            })
        );
    }



    //change ths slots properties
    const onChange = (updatedSlots: Slot[]) => {
    updatePages(prev => {
        const page = prev.find(p => p.id === selectedPageId);
        if (!page) return prev;

        const updatedMap = new Map(updatedSlots.map(s => [s.id, s]));

        const newSlots = page.slots.map(slot => {
            const u = updatedMap.get(slot.id);
            if (!u) return slot;

            const updatedSlot: Slot = {
                ...slot,
                ...u,

                scaleX: u.scaleX ?? slot.scaleX ?? 1,
                scaleY: u.scaleY ?? slot.scaleY ?? 1,
            };

            return calculateSlotQuality(
                updatedSlot,
                pixelsPerInch
            );
        });

        originalSlotsRef.current[page.id] =
            newSlots.map(s => ({ ...s }));

        return prev.map(p =>
            p.id === selectedPageId
                ? { ...p, slots: newSlots }
                : p
        );
    
    });
};


    const updatePageById = (pageId: string, changes: Partial<PageType>) => {
        updatePages(prev =>
            prev.map(p => (p.id === pageId ? { ...p, ...changes } : p))
        );
    };



    const handleSelectSlot = (
        slotId: string,
        pageId: string,
        e?: Konva.KonvaEventObject<MouseEvent | TouchEvent>
    ) => {
        setSelectedPageId(pageId);
        setSelectedTextId(null);
        setSelectedShapeId(null);

        const isShift = e?.evt?.shiftKey;
        const isRightClick =
            e && 'button' in e.evt && e.evt.button === 2;

        setSelectedSlotIdsByPage((prev) => {
            const prevSelected = prev[pageId] || [];
            let newSelection: string[];

            if (isRightClick) {
                // Right-click: keep previous selection, add slot if not already included
                newSelection = prevSelected.includes(slotId)
                    ? prevSelected
                    : [...prevSelected, slotId];
            } else if (isShift) {
                // Shift-click: toggle slot
                newSelection = prevSelected.includes(slotId)
                    ? prevSelected.filter((id) => id !== slotId)
                    : [...prevSelected, slotId];
            } else {
                // Normal left-click: single select
                newSelection = [slotId];
            }

            return {
                ...prev,
                [pageId]: newSelection,
            };
        });

        setSelectedElement({ type: "slot", id: slotId });
    };


    // Update a single slot in the selected page
    const updateSlot = (slotId: string, updates: Partial<Slot>) => {
        if (!selectedPageId) return;

        updatePages(prevPages =>
            prevPages.map(page => {
                if (page.id !== selectedPageId) return page;

                const newSlots = page.slots.map(slot => {
                    if (slot.id !== slotId) return slot;

                    const updatedSlot: Slot = {
                        ...slot,
                        ...updates,
                    };

                    return calculateSlotQuality(
                        updatedSlot,
                        pixelsPerInch
                    );
                });

                return {
                    ...page,
                    slots: newSlots,
                };
            })
        );
    };

    //Helper for the swap to past all properites when the image is swapped
    function copyImageState(target: Slot, source: Slot): Slot {
        return {
            ...target,

            imageId: source.imageId,
            imageSrc: source.imageSrc,
            originalSrc: source.originalSrc,

            imageWidth: source.imageWidth,
            imageHeight: source.imageHeight,

            imageZoom: source.imageZoom,
            imageCenter: source.imageCenter,
            imageRotation: source.imageRotation,

            blackWhite: source.blackWhite,
            opacity: source.opacity,

            strokeColor: source.strokeColor,
            strokeWidth: source.strokeWidth,

            shadowOpacity: source.shadowOpacity,
            shadowColor: source.shadowColor,
            shadowBlur: source.shadowBlur,
            shadowOffsetX: source.shadowOffsetX,
            shadowOffsetY: source.shadowOffsetY,
        };
    }

    // change images between slots
    const handleSwapSelectedSlots = () => {
        if (!selectedPageId) {        
            return;
        }
        // get currently selected ids (prefer per-page array, fallback to single id)
        const selectedIds =
            (selectedSlotIdsByPage && selectedSlotIdsByPage[selectedPageId] && selectedSlotIdsByPage[selectedPageId].slice()) ||
            (selectedSlotId ? [selectedSlotId] : []);

        if (!selectedIds || selectedIds.length !== 2) {         
            return;
        }

        const [idA, idB] = selectedIds;

        updatePages((prevPages: PageType[]) =>
            prevPages.map((page) => {
                if (page.id !== selectedPageId) return page;

                const slotA = page.slots.find((s) => s.id === idA);
                const slotB = page.slots.find((s) => s.id === idB);
                if (!slotA || !slotB) {                 
                   return page;
                }

                // swap image-related fields (adjust names if your slot stores them differently)
                const newSlots = page.slots.map((s) => {
                    if (s.id === idA) {
                        return calculateSlotQuality(
                            copyImageState(s, slotB),
                            pixelsPerInch
                        );
                    }

                    if (s.id === idB) {
                        return calculateSlotQuality(
                            copyImageState(s, slotA),
                            pixelsPerInch
                        );
                    }
                    return s;
                });

                return { ...page, slots: newSlots };
            })
        );
    };


    //Divide Slots
    function divideIntWithFixedGap(total: number, parts: number, gap: number) {
        const totalGap = gap * (parts - 1);
        const spaceForSlots = total - totalGap;

        const base = Math.floor(spaceForSlots / parts);
        const remainder = spaceForSlots - base * parts;

        // First `remainder` slots get +1px so everything sums perfectly
        const sizes = Array(parts)
            .fill(base)
            .map((value, i) => (i < remainder ? value + 1 : value));

        return sizes;
    }

    // ---------------------------------------------------------
    const handleDivideSelectedSlot = (
        direction: "horizontal" | "vertical",
        parts: number,
        gap: number
    ) => {
        if (!selectedPageId) return;

        const currentlySelected =
            selectedSlotIdsByPage?.[selectedPageId] ??
            (selectedSlotId ? [selectedSlotId] : []);

        if (!currentlySelected || currentlySelected.length === 0) return;

        const newCreatedIds: string[] = [];

        updatePages(prevPages =>
            prevPages.map(page => {
                if (page.id !== selectedPageId) return page;

                // Remove selected slots first
                let updatedSlots = page.slots.filter(
                    s => !currentlySelected.includes(s.id)
                );
                let updatedElements = page.elements.filter(
                    e => !currentlySelected.includes(e.id)
                );

                for (const slotId of currentlySelected) {
                    const slot = page.slots.find(s => s.id === slotId);
                    if (!slot) continue;

                    if (direction === "vertical") {
                        // Divide WIDTH
                        const widths = divideIntWithFixedGap(slot.width, parts, gap);
                        let currentX = slot.x;

                        widths.forEach((w, i) => {
                            const id = uuidv4();
                            const newSlot: Slot = {
                                ...slot,
                                id,
                                x: currentX,
                                width: w,
                                imageId: undefined,
                                imageSrc: undefined,
                                originalSrc: undefined,
                            };

                            updatedSlots.push(newSlot);
                            updatedElements.push({
                                id,
                                type: "slot",
                                zIndex: page.elements.length + updatedElements.length + 1,
                            });

                            newCreatedIds.push(id);

                            currentX += w + gap;
                        });
                    } else {
                        // Divide HEIGHT
                        const heights = divideIntWithFixedGap(slot.height, parts, gap);
                        let currentY = slot.y;

                        heights.forEach((h, i) => {
                            const id = uuidv4();
                            const newSlot: Slot = {
                                ...slot,
                                id,
                                y: currentY,
                                height: h,
                                imageId: undefined,
                                imageSrc: undefined,
                                originalSrc: undefined,
                            };

                            updatedSlots.push(newSlot);
                            updatedElements.push({
                                id,
                                type: "slot",
                                zIndex: page.elements.length + updatedElements.length + 1,
                            });

                            newCreatedIds.push(id);

                            currentY += h + gap;
                        });
                    }
                }

                return {
                    ...page,
                    slots: updatedSlots,
                    elements: updatedElements,
                };
            })
        );

        if (newCreatedIds.length > 0) {
            requestAnimationFrame(() => {
                setSelectedSlotIds(newCreatedIds);
            });
        }
    };
  
    //    // Merge Slots
    const handleMergeSelectedSlots = () => {
        let newMergedId: string | null = null;
         updatePages(prevPages =>
            prevPages.map(page => {
                if (page.id !== selectedPageId) return page;
              
                const selectedSlots = page.slots.filter(s =>
                    selectedIds.includes(s.id)
                );

                if (selectedSlots.length < 2) return page;

                // Compute merged bounds
                const minX = Math.min(...selectedSlots.map(s => s.x));
                const minY = Math.min(...selectedSlots.map(s => s.y));
                const maxX = Math.max(...selectedSlots.map(s => s.x + s.width));
                const maxY = Math.max(...selectedSlots.map(s => s.y + s.height));

                const mergedSlot = {
                    ...selectedSlots[0],
                    id: uuidv4(),
                    x: minX,
                    y: minY,
                    width: maxX - minX,
                    height: maxY - minY,
                    imageId: undefined,
                    imageSrc: undefined,
                    originalSrc: undefined,
                };

                newMergedId = mergedSlot.id;

                return {
                    ...page,
                    slots: [
                        ...page.slots.filter(s => !selectedIds.includes(s.id)),
                        mergedSlot,
                    ],
                    elements: [
                        ...page.elements.filter(e => !selectedIds.includes(e.id)),
                        {
                            id: mergedSlot.id,
                            type: "slot",
                            zIndex: page.elements.length + 1,
                        },
                    ],
                };
            })
        );

        if (newMergedId !== null) {
            requestAnimationFrame(() => {
                setSelectedSlotIdsByPage(prev => ({
                    ...prev,
                    [selectedPageId as string]: [newMergedId as string],
                }));
            });
        }
    };

    



    // --- State ---
    const [gap, setGap] = useState(8);
    const [isGapPopupOpen, setIsGapPopupOpen] = useState(false);
    const GAP_THRESHOLD = 40;
    const [horizontalGap, setHorizontalGap] = useState(8); // default horizontal gap
    const [verticalGap, setVerticalGap] = useState(8);
    const originalSlotsRef = useRef<Record<string, Slot[]>>({});
    const toInternal = (ui: number) => ui - 4;
    const toUI = (internal: number) => internal + 4;
  
    const templateGap = 8;
   
    const [gapScope, setGapScope] = useState<"page" | "all">("page");

    //for the gap to save the value 
    useEffect(() => {
        if (!selectedPageId) return;

        // When applying to all spreads, keep the current slider value.
        if (gapScope === "all") return;

        const page = pages.find(
            p => p.id === selectedPageId
        );

        if (!page) return;

        setGap(page.gap ?? 8);
    }, [selectedPageId, pages, gapScope]);
    

    // --- Ref for button to position popup ---
    const gapButtonRef = useRef<HTMLButtonElement>(null);
    const [popupPos, setPopupPos] = useState({ left: 0, bottom: 0 });

    // --- Open popup ---
    const openGapPopup = () => {
        if (!gapButtonRef.current) return;
        const rect = gapButtonRef.current.getBoundingClientRect();
        setPopupPos({
            left: rect.left-95,
            bottom: window.innerHeight - rect.top + 4, // 8px above button
        });
        setIsGapPopupOpen(true);
    };
    
  const rangesOverlap = (
    aStart: number,
    aEnd: number,
    bStart: number,
    bEnd: number
) => {
    return !(aEnd <= bStart || bEnd <= aStart);
};

    type Neighbors = {
        left?: Slot;
        right?: Slot;
        top?: Slot;
        bottom?: Slot;
    };


    const findNeighbors = (slot: Slot, slots: Slot[], threshold: number): Neighbors => {
        let neighbors: Neighbors = {};

        for (const other of slots) {
            if (other.id === slot.id) continue;
            // LEFT neighbor
            if (
                slot.x - (other.x + other.width) <= threshold &&
                slot.x - (other.x + other.width) >= 0 &&
                rangesOverlap(slot.y, slot.y + slot.height, other.y, other.y + other.height)
            ) {
                neighbors.left = other;
            }
            // RIGHT neighbor
            if (
                other.x - (slot.x + slot.width) <= threshold &&
                other.x - (slot.x + slot.width) >= 0 &&
                rangesOverlap(slot.y, slot.y + slot.height, other.y, other.y + other.height)
            ) {
                neighbors.right = other;
            }
            // TOP neighbor
            if (
                slot.y - (other.y + other.height) <= threshold &&
                slot.y - (other.y + other.height) >= 0 &&
                rangesOverlap(slot.x, slot.x + slot.width, other.x, other.x + other.width)
            ) {
                neighbors.top = other;
            }
            // BOTTOM neighbor
            if (
                other.y - (slot.y + slot.height) <= threshold &&
                other.y - (slot.y + slot.height) >= 0 &&
                rangesOverlap(slot.x, slot.x + slot.width, other.x, other.x + other.width)
            ) {
                neighbors.bottom = other;
            }
        }
        return neighbors;
    };

    const applySmartGap = (slots: Slot[], gap: number): Slot[] => {
        return slots.map(slot => {
            const neighbors = findNeighbors(slot, slots, GAP_THRESHOLD);

            let dx = 0;
            let dy = 0;
            let dWidth = 0;
            let dHeight = 0;

            const half = gap /2 ;
            // LEFT edge
            if (neighbors.left) {
                dx += half;
                dWidth -= half;
            }
            // RIGHT edge
            if (neighbors.right) {
                dWidth -= half;
            }
            // TOP edge
            if (neighbors.top) {
                dy += half;
                dHeight -= half;
            }
            // BOTTOM edge
            if (neighbors.bottom) {
                dHeight -= half;
            }
            return {
                ...slot,
                x: slot.x + dx,
                y: slot.y + dy,
                width: slot.width + dWidth,
                height: slot.height + dHeight,
            };
        });
    };


    const handleSmartGap = (
        sliderValue: number,
        scope: "page" | "all"
    ) => {
        if (!selectedPageId) return;

        updatePages(prevPages =>
            prevPages.map(page => {

                // Current spread only
                if (scope === "page" && page.id !== selectedPageId) {
                    return page;
                }

                if (!originalSlotsRef.current[page.id]) {
                    originalSlotsRef.current[page.id] =
                        page.slots.map(s => ({ ...s }));
                }

                const baseSlots = originalSlotsRef.current[page.id];

                const slotsToAdjust =
                    scope === "page" && selectedIds.length > 0
                        ? baseSlots.filter(s =>
                            selectedIds.includes(s.id)
                        )
                        : baseSlots;

                if (slotsToAdjust.length === 0) {
                    return page;
                }

                const actualGap = templateGap + sliderValue;

                let result = slotsToAdjust;

                for (let i = 0; i < 2; i++) {
                    result = applySmartGap(
                        result,
                        actualGap - templateGap
                    );
                }

                const map = new Map(
                    result.map(s => [s.id, s])
                );

                return {
                    ...page,

                    // SAVE THE GAP ON THIS PAGE
                    gap: sliderValue,

                    slots: page.slots.map(currentSlot => {

                        const updated = map.get(currentSlot.id);

                        if (!updated) {
                            return currentSlot;
                        }

                        return {
                            ...currentSlot,
                            x: updated.x,
                            y: updated.y,
                            width: updated.width,
                            height: updated.height,
                        };
                    })
                };
            })
        );
    };





     // #endregion

    // #region Flip Image
    const handleFlip = (direction: "horizontal" | "vertical") => {
        if (!selectedPageId) return;

        const selectedPage = pages.find(p => p.id === selectedPageId);
        if (!selectedPage) return;

        const slotId =
            (selectedElement?.type === "slot" ? selectedElement.id : undefined) ??
            selectedSlotIdsByPage[selectedPageId]?.[0];

        if (!slotId) return;

        const slot = selectedPage.slots.find(s => s.id === slotId);
        if (!slot) return;

        const curScaleX = slot.scaleX ?? 1;
        const curScaleY = slot.scaleY ?? 1;

        updateSlot(slotId, {
            scaleX: direction === "horizontal" ? -curScaleX : curScaleX,
            scaleY: direction === "vertical" ? -curScaleY : curScaleY,
        });

        setSelectedElement({ type: "slot", id: slotId });
    };

     // #endregion

    // #region Global color

    // Get the currently selected items based on pages state
    const getSelectedItems = () => {
        const page = pages.find(p => p.id === selectedPageId);
        if (!page) return [];

        const items: (ShapeElement | TextElement | Slot)[] = [];

        if (selectedShapeId) {
            const shape = page.shapes.find(s => s.id === selectedShapeId);
            if (shape) items.push(shape);
        }

        if (selectedTextId) {
            const text = page.texts.find(t => t.id === selectedTextId);
            if (text) items.push(text);
        }

        page.slots.forEach(sl => {
            if (selectedIds.includes(sl.id)) items.push(sl);
        });

        return items;
    };

    // Get the current color for fill/stroke/shadow
    const getSelectedColor = (type: 'fill' | 'stroke' | 'shadow') => {
        const page = pages.find(p => p.id === selectedPageId);
        if (!page) return '#000000';

        const items = getSelectedItems();

        if (items.length === 0) {
            // No selection  return page background for fill, fallback black for others
            return type === 'fill' ? page.backgroundColor ?? '#ffffff' : '#000000';
        }

        const first = items[0];
        switch (type) {
            case 'fill': return first.fill ?? '#000000';
            case 'stroke': return (first as any).strokeColor ?? (first as any).stroke ?? '#000000';
            case 'shadow': return first.shadowColor ?? '#000000';
        }
    };

    // Update color for selected items or page background if none selected
    const handleColorChange = (type: 'fill' | 'stroke' | 'shadow', newColor: string) => {
        updatePages(prev =>
            prev.map(page => {
                if (page.id !== selectedPageId) return page;

                // No items selected  change page background
                if (selectedIds.length === 0 && !selectedShapeId && !selectedTextId) {
                    return { ...page, backgroundColor: newColor };
                }

                return {
                    ...page,
                    shapes: page.shapes.map(s =>
                        (selectedShapeId === s.id || selectedIds.includes(s.id))
                            ? {
                                ...s,
                                ...(type === 'fill' ? { fill: newColor } : {}),
                                ...(type === 'stroke' ? { strokeColor: newColor } : {}),
                                ...(type === 'shadow' ? { shadowColor: newColor } : {}),
                            }
                            : s
                    ),
                    texts: page.texts.map(t =>
                        (selectedTextId === t.id || selectedIds.includes(t.id))
                            ? {
                                ...t,
                                ...(type === 'fill' ? { fill: newColor } : {}),
                                ...(type === 'stroke' ? { strokeColor: newColor } : {}), // <-- fix here
                                ...(type === 'shadow' ? { shadowColor: newColor } : {}),
                            }
                            : t
                    ),
                    slots: page.slots.map(sl =>
                        selectedIds.includes(sl.id)
                            ? {
                                ...sl,
                                //...(type === 'fill' ? { fill: newColor } : {}),
                                ...(type === 'stroke' ? { strokeColor: newColor } : {}),
                                ...(type === 'shadow' ? { shadowColor: newColor } : {}),
                            }
                            : sl
                    ),
                };
            })
        );
    };


     // #endregion

    // #region Template

    //  Keep this helper (same as used in applyTemplateToPage)
    const lastPageSlotsRef = useRef<Record<string, Slot[]>>({});

    function findPreviousSlotState(
        page: PageType,
        originalUrl: string | undefined,
        lastPageSlotsRef: React.MutableRefObject<Record<string, Slot[]>>
    ): Slot | undefined {
        if (!originalUrl) return undefined;
        const prevSlots = lastPageSlotsRef.current[page.id];
        return prevSlots?.find(s => s.originalSrc === originalUrl);
    }

    //  Keep the ref in sync with current page state
    useEffect(() => {
        if (selectedPageId) {
            const currentPage = pages.find(p => p.id === selectedPageId);
            if (currentPage) {
                lastPageSlotsRef.current[selectedPageId] = currentPage.slots;
            }
        }
    }, [pages, selectedPageId]);


    const getTemplateScale = (templateSize: [number, number], stageSize: { width: number, height: number }) => {
        const [templateWidth, templateHeight] = templateSize;
        const scaleX = stageSize.width / templateWidth;
        const scaleY = stageSize.height / templateHeight;
        const scale = Math.min(scaleX, scaleY); // preserve aspect ratio
        return scale;
    };

    const applyTemplate = (template: Template) => {
        if (!template || !selectedPageId) return;

        const scale = getTemplateScale(template.size, {
            width: stageSize.width,
            height: stageSize.height,
        });

        updatePages(prevPages =>
            prevPages.map(page => {
                if (page.id !== selectedPageId) return page;

                const currentMaxZ = page.elements.length > 0
                    ? Math.max(...page.elements.map(e => e.zIndex))
                    : 0;

                let nextZ = currentMaxZ + 1;

                // --- SLOTS ---
                const newSlots = template.slots.map(templateSlot => ({
                    ...templateSlot,
                    id: uuidv4(),
                    x: (templateSlot.x ?? 0) * scale,
                    y: (templateSlot.y ?? 0) * scale,
                    width: (templateSlot.width ?? 100) * scale,
                    height: (templateSlot.height ?? 100) * scale,
                    rotation: templateSlot.rotation ?? 0,
                    imageRotation: templateSlot.imageRotation ?? 0,
                    opacity: templateSlot.opacity ?? 1,
                }));

                const slotElements = newSlots.map(slot => ({
                    id: slot.id,
                    type: "slot" as const,
                    zIndex: nextZ++,
                }));

                // --- TEXTS ( NEW PART) ---
                const newTexts = (template.texts || []).map(templateText => ({
                    ...templateText,
                    id: uuidv4(),
                    x: (templateText.x ?? 0) * scale,
                    y: (templateText.y ?? 0) * scale,
                    width: (templateText.width ?? 100) * scale,
                    fontSize: (templateText.fontSize ?? 20) * scale,
                    rotation: templateText.rotation ?? 0,
                    opacity: templateText.opacity ?? 1,
                }));

                const textElements = newTexts.map(text => ({
                    id: text.id,
                    type: "text" as const,
                    zIndex: nextZ++,
                }));

                return {
                    ...page,
                    slots: newSlots,
                    texts: [...(page.texts || []), ...newTexts], //  ADD TEXTS
                    elements: [...page.elements, ...slotElements, ...textElements], //  ADD TO LAYER STACK
                };
            })
        );
    };


    function applyTemplateToPage(
        page: PageType,
        template: Template,
        images: {
            id: string;
            originalUrl: string;
            smallUrl?: string;
            orientation?: "H" | "V";
        }[],
        stageSize: { width: number; height: number }
    ): PageType {

        const [templateWidth, templateHeight] = template.size;

        const scaleX = stageSize.width / templateWidth;
        const scaleY = stageSize.height / templateHeight;
        const scale = Math.min(scaleX, scaleY);

        const usedImages = new Set<string>();

        const updatedSlots = template.slots.map(slot => {

            const slotOrientation: "H" | "V" =
                slot.width >= slot.height ? "H" : "V";

            let match = images.find(
                img =>
                    !usedImages.has(img.id) &&
                    (!img.orientation || img.orientation === slotOrientation)
            );

            if (!match) {
                match = images.find(
                    img => !usedImages.has(img.id)
                );
            }

            if (match) {
                usedImages.add(match.id);
            }

            const prevSlot = findPreviousSlotState(
                page,
                match?.originalUrl,
                lastPageSlotsRef
            );

            const rawName = match?.originalUrl?.split("/").pop() || "";
            const name = decodeURIComponent(rawName).split("?")[0];
            const meta = metadataRef.current.get(name);

            return calculateSlotQuality(
                {
                    ...slot,
                    id: uuidv4(),

                    x: (slot.x ?? 0) * scale,
                    y: (slot.y ?? 0) * scale,
                    width: (slot.width ?? 100) * scale,
                    height: (slot.height ?? 100) * scale,

                    imageSrc: match?.smallUrl || match?.originalUrl,
                    originalSrc: match?.originalUrl,

                    imageWidth: meta?.width,
                    imageHeight: meta?.height,

                    imageRotation: prevSlot?.imageRotation ?? 0,
                    scaleX: prevSlot?.scaleX ?? 1,
                    scaleY: prevSlot?.scaleY ?? 1,

                    blackWhite: prevSlot?.blackWhite ?? false,
                    opacity: prevSlot?.opacity ?? 1,

                    strokeColor: prevSlot?.strokeColor ?? "#000000",
                    strokeWidth: prevSlot?.strokeWidth ?? 0,

                    shadowOpacity: prevSlot?.shadowOpacity ?? 0,
                    shadowColor: prevSlot?.shadowColor ?? "#000000",
                    shadowBlur: prevSlot?.shadowBlur ?? 0,
                    shadowOffsetX: prevSlot?.shadowOffsetX ?? 0,
                    shadowOffsetY: prevSlot?.shadowOffsetY ?? 0,
                },
                pixelsPerInch
            );
        });

        // --- TEXTS ( ADD THIS BLOCK) ---
        const updatedTexts = (template.texts || []).map(templateText => ({
            ...templateText,
            id: uuidv4(),
            x: (templateText.x ?? 0) * scale,
            y: (templateText.y ?? 0) * scale,
            width: (templateText.width ?? 100) * scale,
            fontSize: (templateText.fontSize ?? 20) * scale,
            rotation: templateText.rotation ?? 0,
            opacity: templateText.opacity ?? 1,
        }));

        // --- ELEMENTS ---
        const currentMaxZ = page.elements.length > 0
            ? Math.max(...page.elements.map(e => e.zIndex))
            : 0;

        let nextZ = currentMaxZ + 1;

        const slotElements = updatedSlots.map(slot => ({
            id: slot.id,
            type: "slot" as const,
            zIndex: nextZ++,
        }));

        const textElements = updatedTexts.map(text => ({
            id: text.id,
            type: "text" as const,
            zIndex: nextZ++,
        }));

        return {
            ...page,
            slots: updatedSlots,
            texts: [...(page.texts || []), ...updatedTexts], //  ADD TEXTS HERE
            elements: [...page.elements, ...slotElements, ...textElements], //  ADD TO RENDER STACK
        };
    }

   

      // Template Change
    const handleTemplateChange = (newTemplate: Template) => {
        if (!selectedPageId) return;

        const scale = getTemplateScale(newTemplate.size, {
            width: stageSize.width,
            height: stageSize.height,
        });

        updatePages(prevPages =>
            prevPages.map(page => {
                if (page.id !== selectedPageId) return page;

                const existingImages = page.slots
                    .filter(slot => slot.originalSrc)
                    .map(slot => ({
                        originalUrl: slot.originalSrc!,
                        smallUrl: slot.imageSrc,
                        orientation: slot.width >= slot.height ? "H" : "V",
                    }));
       
                const used = new Set<string>();

                const updatedSlots = newTemplate.slots.map(slot => {
                    const slotOrientation = slot.width >= slot.height ? "H" : "V";

                    let img = existingImages.find(i =>
                        !used.has(i.originalUrl) &&
                        i.orientation === slotOrientation
                    );

                    if (!img) {
                        img = existingImages.find(i => !used.has(i.originalUrl));
                    }

                    if (img) used.add(img.originalUrl);

                    const prevSlot = findPreviousSlotState(page, img?.originalUrl, lastPageSlotsRef);
                    const rawName = img?.originalUrl?.split("/").pop() || "";
                    const name = decodeURIComponent(rawName).split("?")[0];
                    const meta = metadataRef.current.get(name);

                    return calculateSlotQuality(
                        {
                            ...slot,
                            id: uuidv4(),
                            x: (slot.x ?? 0) * scale,
                            y: (slot.y ?? 0) * scale,
                            width: (slot.width ?? 100) * scale,
                            height: (slot.height ?? 100) * scale,

                            imageSrc: img?.smallUrl || img?.originalUrl,
                            originalSrc: img?.originalUrl,

                            imageWidth: meta?.width,
                            imageHeight: meta?.height,

                            imageRotation: prevSlot?.imageRotation ?? 0,
                            scaleX: prevSlot?.scaleX ?? 1,
                            scaleY: prevSlot?.scaleY ?? 1,
                            blackWhite: prevSlot?.blackWhite ?? false,
                            opacity: prevSlot?.opacity ?? 1,
                            strokeColor: prevSlot?.strokeColor ?? "#000000",
                            strokeWidth: prevSlot?.strokeWidth ?? 0,
                            shadowOpacity: prevSlot?.shadowOpacity ?? 0,
                            shadowColor: prevSlot?.shadowColor ?? "#000000",
                            shadowBlur: prevSlot?.shadowBlur ?? 0,
                            shadowOffsetX: prevSlot?.shadowOffsetX ?? 0,
                            shadowOffsetY: prevSlot?.shadowOffsetY ?? 0,
                        },
                        pixelsPerInch
                    );
                });

                const updatedTexts = (newTemplate.texts || []).map(text => ({
                    ...text,
                    id: uuidv4(),
                    x: (text.x ?? 0) * scale,
                    y: (text.y ?? 0) * scale,
                    width: (text.width ?? 200) * scale,
                    fontSize: (text.fontSize ?? 20) * scale,
                }));

                let nextZ = 1;

                const slotElements = updatedSlots.map(slot => ({
                    id: slot.id,
                    type: "slot" as const,
                    zIndex: nextZ++,
                }));

                const textElements = updatedTexts.map(text => ({
                    id: text.id,
                    type: "text" as const,
                    zIndex: nextZ++,
                }));

                const updatedElements = [...slotElements, ...textElements];

                //  RESET originalSlotsRef for this page
                originalSlotsRef.current[page.id] = updatedSlots.map(s => ({ ...s }));

                return {
                    ...page,
                    slots: updatedSlots,
                    texts: [...(page.texts || []), ...updatedTexts], // <- append new template texts
                    elements: updatedElements,
                };
            })
        );
    };

    //Reload templates button
    const reloadTemplatesForCurrentPage = () => {
        if (!selectedPageId) return;

        const page = pages.find(p => p.id === selectedPageId);
        if (!page) return;

        // Use EXISTING slots on page
        const slotImages = page.slots.filter(s => s.imageSrc);

        if (slotImages.length === 0) {
            setTemplateThumbnails([]);
            return;
        }

        // Count orientations FROM CURRENT SLOTS
        const countH = slotImages.filter(s => s.width >= s.height).length;
        const countV = slotImages.filter(s => s.width < s.height).length;

        const templateKey =
            `${openAR}_${String(slotImages.length).padStart(2, "0")}_H${countH}_V${countV}`;

        const moduleTemplates = allTemplatesMap[templateKey] || [];

        setTemplateThumbnails(moduleTemplates);
    };


      // #endregion

    // #region Page Buttons 
    const handleAddPageAtIndex = (index: number) => {
        updatePages(prev => {
            const newPage: PageType = {
                id: uuidv4(),
                order: 0, // will be set below
                backgroundColor: "#ffffff",
                showBackground: false,
                backgroundImageSrc: null,
                backgroundOriginalSrc: null,
                backgroundOpacity: 1,
                backgroundZoom: 1,
                backgroundOffsetX: 0,
                backgroundOffsetY: 0,
                slots: [],
                texts: [],
                shapes: [],
                elements: [],
            };

            const updated = [...prev];
            updated.splice(index + 1, 0, newPage);

            const renumbered = updated.map((page, i) => ({ ...page, order: i }));

            setSelectedPageId(newPage.id);
            return renumbered;
        });
    };
 

    const handleRemovePageAtIndex = (index: number) => {
        setDialogMessage(`Are you sure you want to delete spread ${index + 1}?`);
        setDialogTitle("Confirm");

        setDialogCallback(() => () => {
            updatePages(prevPages => {
                if (prevPages.length <= 1) return prevPages;

                const pageToDelete = prevPages[index];

                handleRemoveBackground(pageToDelete.id);

                const imagesInPage = pageToDelete.slots?.filter(s => s.originalSrc).map(slot => ({
                    originalUrl: slot.originalSrc!,
                    smallUrl: slot.imageSrc
                })) || [];

                setMarkedImages(prev =>
                    prev.map(img => ({
                        ...img,
                        used: !imagesInPage.some(
                            slotImg =>
                                slotImg.originalUrl === img.originalUrl ||
                                slotImg.smallUrl === img.smallUrl
                        ),
                    }))
                );

                const updated = [...prevPages];
                updated.splice(index, 1);

                const renumbered = updated.map((page, i) => ({ ...page, order: i }));

                if (selectedPageId === pageToDelete.id) {
                    const newIndex = Math.min(index, renumbered.length - 1);
                    setSelectedPageId(renumbered[newIndex].id);
                }

                return renumbered;
            });
        });

        setDialogSingleButton(false);
        setDialogOpen(true);
    };


    // Duplicate a page and insert it after the current one
    const handleDuplicatePageAtIndex = (index: number) => {
        updatePages(prevPages => {
            const pageToCopy = prevPages[index];
            if (!pageToCopy) return prevPages;

            const newPageId = uuidv4();

            // Clone page object
            const duplicatedPage: PageType = {
                ...JSON.parse(JSON.stringify(pageToCopy)),
                id: newPageId,
                order: (pageToCopy.order ?? index) + 1
            };

            // Remove images for products that should not duplicate them
            if (disableImage.has(albumData.albumType)) {
                duplicatedPage.slots = duplicatedPage.slots.map(slot => ({
                    ...slot,
                    imageId: undefined,
                    imageSrc: undefined,
                    originalSrc: undefined,
                    originalImage: undefined,
                    imageScale: undefined,
                    imageRotation: 0,
                    imageZoom: 1,
                    imageCenter: undefined,
                    quality: undefined,
                    imageWidth: undefined,
                    imageHeight: undefined,
                }));
            }

            // Insert duplicate after original
            const updatedPages = [...prevPages];
            updatedPages.splice(index + 1, 0, duplicatedPage);

            // Renumber orders
            const renumberedPages = updatedPages.map((page, i) => ({
                ...page,
                order: i
            }));

            // Duplicate all background state
            const bgSrc = backgroundImageSrcs[pageToCopy.id] ?? null;
            const bgOriginal = backgroundOriginalPaths[pageToCopy.id] ?? null;
            const bgOpacity = backgroundOpacities[pageToCopy.id] ?? 1;
            const bgZoom = backgroundZooms[pageToCopy.id] ?? 1;
            const bgOffsetX = backgroundOffsetsX[pageToCopy.id] ?? 0;
            const bgOffsetY = backgroundOffsetsY[pageToCopy.id] ?? 0;
            const showBg = showBackgrounds[pageToCopy.id] ?? false;

            setBackgroundImageSrcs(prev => ({ ...prev, [newPageId]: bgSrc }));
            setBackgroundOriginalPaths(prev => ({ ...prev, [newPageId]: bgOriginal }));
            setBackgroundOpacities(prev => ({ ...prev, [newPageId]: bgOpacity }));
            setBackgroundZooms(prev => ({ ...prev, [newPageId]: bgZoom }));
            setBackgroundOffsetsX(prev => ({ ...prev, [newPageId]: bgOffsetX }));
            setBackgroundOffsetsY(prev => ({ ...prev, [newPageId]: bgOffsetY }));
            setShowBackgrounds(prev => ({ ...prev, [newPageId]: showBg }));

            // **IMPORTANT:** actually load the image for the new page
            if (bgSrc) {
                setPageBackgroundImage(
                    newPageId,
                    bgSrc,       // small
                    bgOpacity,
                    bgZoom,
                    bgOffsetX,
                    bgOffsetY
                );
            }

            setSelectedPageId(newPageId);

            // Save to server if needed
            saveAlbumToServer({ ...albumData, pages: renumberedPages }, setAlbumData, token);

            return renumberedPages;
        });
    };



    const swapPages = (index1: number, index2: number) => {
        updatePages(prevPages => {
            const newPages = [...prevPages];
            [newPages[index1], newPages[index2]] = [newPages[index2], newPages[index1]];

            // Update order only
            const reordered = newPages.map((page, i) => ({
                ...page,
                order: i
            }));

            saveAlbumToServer({ ...albumData, pages: reordered }, setAlbumData, token);
            return reordered;
        });
    };


    const movePageUp = (index: number) => {
        if (index > 0) swapPages(index, index - 1);
    };

    const movePageDown = (index: number) => {
        if (index < pages.length - 1) swapPages(index, index + 1);
    };


    const flipPage = (pageId: string, type: "horizontal" | "vertical") => {
        updatePages(prev =>
            prev.map(page => {
                if (page.id !== pageId) return page;

                const pageWidth = stageSize.width;
                const pageHeight = stageSize.height;

                // Flip slots
                const newSlots = page.slots.map(slot => {
                    if (type === "horizontal") {
                        return { ...slot, x: pageWidth - slot.x - slot.width };
                    } else {
                        return { ...slot, y: pageHeight - slot.y - slot.height };
                    }
                });

                // Flip shapes
                const newShapes = page.shapes.map(shape => {
                    if (shape.type === "rect" || shape.type === "triangle") {
                        if (type === "horizontal") {
                            return { ...shape, x: pageWidth - shape.x - shape.width };
                        } else {
                            return { ...shape, y: pageHeight - shape.y - shape.height };
                        }
                    }
                    if (shape.type === "ellipse" || shape.type === "star") {
                        if (type === "horizontal") {
                            return { ...shape, x: pageWidth - shape.x };
                        } else {
                            return { ...shape, y: pageHeight - shape.y };
                        }
                    }
                    return shape;
                });

                // Flip text
                const newTexts = page.texts.map(text => {
                    const height = text.height ?? 0;
                    if (type === "horizontal") {
                        return { ...text, x: pageWidth - text.x - text.width };
                    } else {
                        return { ...text, y: pageHeight - text.y - height };
                    }
                });

                return { ...page, slots: newSlots, shapes: newShapes, texts: newTexts };
            })
        );
    };

    //Auto or Manual mode for the page
 
    const toggleEditorMode = () => {
        setEditorMode(prev => (prev === "manual" ? "auto" : "manual"));
    };

     // #endregion

    // #region On Drop
    const [templateThumbnails, setTemplateThumbnails] = useState<Template[]>([]);
    const isWallArtDisabled = disabledWallArtAlbums.has(albumType);
    const pixelsPerInch = stageSize.width / albumData.size.width;


    const isImageGood = (
        image: { width: number; height: number },
        physical: { w: number; h: number }
    ) => {
        const dpiX = image.width / physical.w;
        const dpiY = image.height / physical.h;

        const minDpi = Math.min(dpiX, dpiY);

        return {
            ok: minDpi >= 200,
            dpi: minDpi,
        };
    };


    function calculateSlotQuality(
        slot: Slot,
        pixelsPerInch: number,
        selectedOpening?: string
    ): Slot {

        if (!slot.imageSrc || !slot.imageWidth || !slot.imageHeight) {
            return {
                ...slot,
                quality: undefined,
            };
        }

        let physical = {
            w: slot.width / pixelsPerInch,
            h: slot.height / pixelsPerInch,
        };

        if (selectedOpening) {
            const [w, h] = selectedOpening.split("x").map(Number);

            physical = {
                w,
                h,
            };
        }

        const zoom = slot.imageZoom ?? 1;

        const quality = isImageGood(
            {
                width: slot.imageWidth / zoom,
                height: slot.imageHeight / zoom,
            },
            physical
        );

        return {
            ...slot,
            quality: {
                dpi: Math.round(quality.dpi),
                ok: quality.ok,
            },
        };
    }


    const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!divRef.current || !selectedPageId) return;

        // --- Parse dropped images ---
        type DroppedImage = {
            id: string;
            name: string;
            originalUrl: string;
            smallUrl?: string;
            orientation: "horizontal" | "vertical";
            width: number;
            height: number;
        };

        let droppedImages: DroppedImage[] = [];
        try {
            const data = e.dataTransfer.getData("application/json");
            if (!data) return;
            const parsed = JSON.parse(data);
            droppedImages = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            return;
        }

        droppedImages = droppedImages.slice(0, 7);
        if (droppedImages.length === 0) return;

        const page = pages.find(p => p.id === selectedPageId);
        if (!page) return;

        // --- Load images and compute orientation --
        type LoadedImage = {
            id: string;
            originalUrl: string;
            smallUrl?: string;
            orientation: "H" | "V";
            width?: number;
            height?: number;
        };

        const loadedImages: LoadedImage[] = droppedImages.map(imgData => ({
            id: imgData.id,
            originalUrl: imgData.originalUrl,
            smallUrl: imgData.smallUrl,
            orientation: imgData.orientation === "vertical" ? "V" : "H",
            width: imgData.width,
            height: imgData.height,
        }));

        // Already valid LoadedImage[]
        const validLoadedImages = loadedImages;

        if (validLoadedImages.length === 0) return;

        const pageNode = document.getElementById(selectedPageId);
        if (!pageNode) return;

        const pageRect = pageNode.getBoundingClientRect();
        const relativeX = e.clientX - pageRect.left;
        const relativeY = e.clientY - pageRect.top;

        let slotIdToSelect: string | null = null;
        // ---------------------------------------------------
        //  COMBINE EXISTING + NEW IMAGES (IMPORTANT FIX)
        // ---------------------------------------------------
        type SlotImage = {
            id: string;
            originalSrc: string;
            imageSrc?: string;
            orientation: "H" | "V";
        };

        const existingImages: SlotImage[] = page.slots
            .filter(s => s.originalSrc)
            .map(s => ({
	        id: s.originalSrc!,
                originalSrc: s.originalSrc!.replace(/^http:/, 'https:'), // normalize
                imageSrc: s.imageSrc?.replace(/^http:/, 'https:'),      // normalize small URL too
                orientation: s.width > s.height ? "H" : "V",
            }));

        const newImages: SlotImage[] = validLoadedImages
            .filter(img => !existingImages.some(s => s.originalSrc === img.originalUrl))
            .map(img => ({
                id: img.id,
                originalSrc: img.originalUrl,
                imageSrc: img.smallUrl,
                orientation: img.orientation,
            }));


        const allImages: SlotImage[] = [...existingImages, ...newImages];
        // ---------------------------------------------------
        // --- Single image drop on existing slot ---
        // ---------------------------------------------------
        if (validLoadedImages.length === 1) {
            const img = validLoadedImages[0];

            updatePages(prev =>
                prev.map(p => {
                    if (p.id !== selectedPageId) return p;

                    const updatedSlots = p.slots.map(slot => {
                        const insideX = relativeX >= slot.x && relativeX <= slot.x + slot.width;
                        const insideY = relativeY >= slot.y && relativeY <= slot.y + slot.height;

                        if (!insideX || !insideY) return slot;                   

                        slotIdToSelect = slot.id;

                        const rawName = img.originalUrl.split("/").pop() || "";
                        const name = decodeURIComponent(rawName).split("?")[0];
                        const meta = metadataRef.current.get(name);
                 
                        if (!meta) return slot;                

                        const updatedSlot: Slot = {
                            ...slot,

                            imageSrc: img.smallUrl || img.originalUrl,
                            originalSrc: img.originalUrl,

                            imageX: 0,
                            imageY: 0,
                            imageRotation: 0,

                            blackWhite: false,

                            imageWidth: meta.width,
                            imageHeight: meta.height,
                        };

                        return calculateSlotQuality(
                            updatedSlot,
                            pixelsPerInch
                        );
                    });
             

                    return {
                        ...p,
                        slots: updatedSlots,
                    };
                })
            );

            if (slotIdToSelect) {
                setSelectedSlotIds([slotIdToSelect]);
            }
           if (editorMode === "manual" && slotIdToSelect) return;
        
        }
        // ---------------------------------------------------
        // --- Auto mode: apply template ---
 
        if (editorMode === "auto") {
            if (isWallArtDisabled) {
                return; //  block template system completely
            }
            const shouldApplyTemplate =
                validLoadedImages.length > 1 ||
                (validLoadedImages.length === 1 && !slotIdToSelect);

            if (!shouldApplyTemplate) return;

            // Count horizontal and vertical images
            const countH = allImages.filter(img => img.orientation === "H").length;
            const countV = allImages.filter(img => img.orientation === "V").length;

            const templateKey =
                `${openAR}_${String(allImages.length).padStart(2, "0")}_H${countH}_V${countV}`;

            const moduleTemplates = allTemplatesMap[templateKey] || [];

            if (moduleTemplates.length === 0) return;

            const template = moduleTemplates[0];

            // Assign images to template slots
            const used = new Set<string>();

            const imagesToApply = template.slots.map(slot => {
                const slotOrientation: "H" | "V" =
                    slot.width >= slot.height ? "H" : "V";

                let match = allImages.find(
                    img =>
                        !used.has(img.id) &&
                        img.orientation === slotOrientation
                );

                if (!match) {
                    match = allImages.find(
                        img => !used.has(img.id)
                    );
                }

                if (match) {
                    used.add(match.id);
                }

                return {
                    id: match?.id || "",
                    originalUrl: match?.originalSrc || "",
                    smallUrl: match?.imageSrc || "",
                    orientation: match?.orientation,
                };
            });

            // Apply template to the page
            const updatedPage = applyTemplateToPage(page, template, imagesToApply, stageSize);

            originalSlotsRef.current[updatedPage.id] =
                updatedPage.slots.map(s => ({ ...s }));

            // Update page and thumbnails in one state transition
            startTransition(() => {
                updatePages(prev =>
                    prev.map(p => (p.id === selectedPageId ? updatedPage : p))
                );
                setTemplateThumbnails(moduleTemplates);
            });
        }
        // --- Manual mode: add new slots at drop position ---
    
        else if (editorMode === "manual") {
            const SLOT_SIZES = {
                H: { width: 300, height: 200 },
                V: { width: 200, height: 300 },
            };

            const precomputedSlots = validLoadedImages.map((img, index) => {
                const { width: slotWidth, height: slotHeight } =
                    SLOT_SIZES[img.orientation];

                const offset = index * 10;
                const x = relativeX - slotWidth / 2 + offset;
                const y = relativeY - slotHeight / 2 + offset;

                const newSlotId = uuidv4();

                const rawName = img.originalUrl.split("/").pop() || "";
                const name = decodeURIComponent(rawName).split("?")[0];
                const meta = metadataRef.current.get(name);

                const slot = calculateSlotQuality(
                    {
                        id: newSlotId,
                        x,
                        y,
                        width: slotWidth,
                        height: slotHeight,
                        rotation: 0,
                        imageRotation: 0,

                        imageSrc: img.smallUrl || img.originalUrl,
                        originalSrc: img.originalUrl,

                        imageWidth: meta?.width,
                        imageHeight: meta?.height,

                        opacity: 1,
                        scaleX: 1,
                        scaleY: 1,
                        blackWhite: false,
                    },
                    pixelsPerInch
                );

                return {
                    slot,
                    element: {
                        id: newSlotId,
                        type: "slot" as const,
                        zIndex: index,
                    },
                };
            });

            startTransition(() => {
                updatePages(prev =>
                    prev.map(p =>
                        p.id === selectedPageId
                            ? {
                                ...p,
                                slots: [...p.slots, ...precomputedSlots.map(s => s.slot)],
                                elements: [
                                    ...p.elements,
                                    ...precomputedSlots.map((s, i) => ({
                                        ...s.element,
                                        zIndex: p.elements.length + i,
                                    })),
                                ],
                            }
                            : p
                    )
                );
            });

            const lastSlotId = precomputedSlots.at(-1)?.slot.id;
            if (lastSlotId) setSelectedSlotIds([lastSlotId]);
        }
    };

    

     // #endregion

    // #region Auto Build

    type AutoImage = {
        originalUrl: string;
        smallUrl?: string;
    };


    type AlbumMetadataItem = { name: string; orientation: "horizontal" | "vertical" };
    const usedTemplatesPerKey = new Map<string, Set<string>>();
  

    const handleAutoCreateConfirm = async (options: AutoCreateAlbumOptions) => {

        if (!token) {
            alert("You must be logged in.");
            return;
        }

        let username = "";
        try {
            const decoded = jwtDecode<TokenPayload>(token);
            username = decoded.username;

        } catch {
            alert("Invalid token.");
            return;
        }

        if (!username || !albumName) {
            alert("Missing username or album name.");
            return;
        }
        //clear all page content
      //  clearAlbumPagesInternal();

        // 1️ Fetch metadata
        let metadata: AlbumMetadataItem[] = [];
        try {
            const res = await fetch(`${API_URL}/albums/${username}/${albumName}/metadata`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to fetch metadata");

            metadata = (await res.json()) as AlbumMetadataItem[];
            if (metadata.length === 0) {
                alert("No images found in album metadata.");
                return;
            }
        } catch (err) {
            console.error(err);
            alert("Error fetching album metadata.");
            return;
        }
        const SERVER_URL = `${window.location.protocol}//${window.location.host}/eda_online`;
        const baseUrl = `${SERVER_URL}/uploads/${username}/${albumName}`;
           // 2️ Prepare images
        const images: AutoImage[] = metadata.map(img => ({
            originalUrl: `${baseUrl}/originals/${img.name}`,
            smallUrl: `${baseUrl}/small/${img.name}`,
        }));
   

        // Shuffle images if "random" order
        const orderedImages =
            options.order === "random"
                ? [...images].sort(() => Math.random() - 0.5)
                : [...images].sort((a, b) =>
                    a.originalUrl
                        .split("/")
                        .pop()!
                        .localeCompare(b.originalUrl.split("/").pop()!, undefined, { numeric: true })
                );

        // Clear all page content locally before Auto Build
        const clearedPages = clearAlbumPageContents(pages);
        const pagesToFill = editorMode === "auto" ? clearedPages : [];

        // 3️ Compute images per page
        const totalImages = orderedImages.length;
        const totalPages = pagesToFill.length;
        const maxPerPage = 7;
        const minPerPage = 1;

        let imagesPerPageArray: number[] = [];

        if (options.imagesPerPage === "auto") {
            // Auto-fill pages up to max 7 per page
            let baseCount = Math.min(maxPerPage, Math.floor(totalImages / totalPages));
            let remainder = totalImages - baseCount * totalPages;

            for (let i = 0; i < totalPages; i++) {
                let count = baseCount;

                if (remainder > 0) {
                    count += 1;
                    remainder -= 1;
                }

                count = Math.max(count, minPerPage);
                count = Math.min(count, maxPerPage);

                imagesPerPageArray.push(count);
            }
        } else if (typeof options.imagesPerPage === "number") {
            const fixedCount = Math.min(
                Math.max(options.imagesPerPage, minPerPage),
                maxPerPage
            );

            imagesPerPageArray = Array(totalPages).fill(fixedCount);
        }

        // 4️ Fill pages
        const newPages = [...clearedPages];
        let imageIndex = 0;

        for (let pageIdx = 0; pageIdx < pagesToFill.length; pageIdx++) {
            const page = pagesToFill[pageIdx];
            const numImages = imagesPerPageArray[pageIdx];
            if (numImages <= 0) continue; // skip empty page

            const pageImages = orderedImages.slice(imageIndex, imageIndex + numImages);

            await autoCreateSinglePage(
                page,
                pageImages,
                metadata,
                albumType,
                stageSize,
                updatedPage => {
                    originalSlotsRef.current[updatedPage.id] =
                        updatedPage.slots.map(s => ({ ...s }));

                    const idx = newPages.findIndex(p => p.id === updatedPage.id);

                    if (idx !== -1) {
                        newPages[idx] = updatedPage;
                    }
                }
            );

            imageIndex += numImages;
        }

        // 5️ Commit updated pages to state
        updatePages?.(() => newPages);

        // Collect all used URLs from page slots
        const usedImageSrcs = new Set(
            newPages.flatMap(page =>
                page.slots
                    .map(slot => [slot.imageSrc, slot.originalSrc])
                    .flat()
                    .filter(Boolean)
            )
        );


        // Update markedImages reliably
        setMarkedImages?.(prev =>
            prev.map(img => ({
                ...img,
                used: usedImageSrcs.has(img.smallUrl) || usedImageSrcs.has(img.originalUrl),
            }))
        );
    };


    async function autoCreateSinglePage(
        page: PageType,
        images: AutoImage[],
        metadata: AlbumMetadataItem[],
        albumType: string,
        stageSize: { width: number; height: number },
        updatePage: (updated: PageType) => void
    ) {
        if (editorMode !== "auto" || images.length === 0) return;
     
        const imagesForPage = images.slice(0, 7);   
        // Map filename  orientation H/V
        const orientationMap = new Map(
            metadata.map(m => [m.name, m.orientation === "horizontal" ? "H" : "V"])
        );

        const loadedImages = imagesForPage.map(img => {
            const name = img.originalUrl.split("/").pop() || "";
            return {
                ...img,
                orientation: orientationMap.get(name) as "H" | "V",
            };
        });

        const countH = loadedImages.filter(i => i.orientation === "H").length;
        const countV = loadedImages.filter(i => i.orientation === "V").length;
        const templateKey =
            `${openAR}_${String(loadedImages.length).padStart(2, "0")}_H${countH}_V${countV}`;
    
        const templates = allTemplatesMap[templateKey];
        if (!templates || templates.length === 0) {
          //  console.warn("No template found for:", templateKey);
            return;
        }

        if (!usedTemplatesPerKey.has(templateKey)) usedTemplatesPerKey.set(templateKey, new Set());
        const usedSet = usedTemplatesPerKey.get(templateKey)!;

        const availableTemplates = templates.filter(t => !usedSet.has(t.id));
        const template = availableTemplates.length > 0
            ? availableTemplates[Math.floor(Math.random() * availableTemplates.length)]
            : templates[Math.floor(Math.random() * templates.length)];

        usedSet.add(template.id);

        // Separate queues
        const hQueue = loadedImages.filter(img => img.orientation === "H");
        const vQueue = loadedImages.filter(img => img.orientation === "V");

        const [templateWidth, templateHeight] = template.size;
        const scale = Math.min(stageSize.width / templateWidth, stageSize.height / templateHeight);

        const SERVER_URL = "https://app.seldex.com.au/eda_online";

        function normalizeUrl(url: string) {
            if (!url) return url;

            if (url.startsWith("http")) {
                return url.replace(/^http:/, "https:");
            }

            return `${SERVER_URL}${url}`;
        }

        const updatedSlots = template.slots.map(slot => {
            const slotOrientation: "H" | "V" = slot.width >= slot.height ? "H" : "V";

            const match = slotOrientation === "H" ? hQueue.shift() : vQueue.shift();

            if (!match) {
                console.error("Orientation mismatch – this should NEVER happen", {
                    templateKey,
                    slotOrientation,
                });
                return null;
            }

            const smallUrlSafe = match.smallUrl && match.smallUrl.trim() !== ""
                ? normalizeUrl(match.smallUrl)
                : normalizeUrl(match.originalUrl);

            const rawName = match.originalUrl.split("/").pop() || "";
            const name = decodeURIComponent(rawName).split("?")[0];
            const meta = metadataRef.current.get(name);

            let newSlot: Slot = {
                ...slot,
                id: uuidv4(),
                x: (slot.x ?? 0) * scale,
                y: (slot.y ?? 0) * scale,
                width: (slot.width ?? 100) * scale,
                height: (slot.height ?? 100) * scale,

                imageSrc: smallUrlSafe,
                originalSrc: normalizeUrl(match.originalUrl),

                imageRotation: 0,
                scaleX: 1,
                scaleY: 1,

                blackWhite: false,
                opacity: 1,
                strokeColor: "#000000",
                strokeWidth: 0,
                shadowOpacity: 0,
                shadowColor: "#000000",
                shadowBlur: 0,
                shadowOffsetX: 0,
                shadowOffsetY: 0,

                imageWidth: meta?.width,
                imageHeight: meta?.height,
            };

            if (meta) {
                newSlot = calculateSlotQuality(newSlot, pixelsPerInch);
            }

            return newSlot;
        }).filter(Boolean) as PageType["slots"];

        const currentMaxZ = page.elements.length > 0 ? Math.max(...page.elements.map(e => e.zIndex)) : 0;
        const newElements = updatedSlots.map((slot, i) => ({
            id: slot.id,
            type: "slot" as const,
            zIndex: currentMaxZ + 1 + i,
        }));

        updatePage({
            ...page,
            slots: updatedSlots,
            elements: [...page.elements, ...newElements],
        });
    }


      // #endregion

    // #region KeyDown events

        // Move items with the arrow keys
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const isInput = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName || '');
            if (isInput) return;

            // --- Compute move step dynamically ---
            const MOVE_STEP = e.shiftKey ? 10 : e.ctrlKey || e.metaKey ? 5 : 1;

            // Arrow keys: move items
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();

                const moveItem = <T extends { x: number; y: number }>(item: T): T => {
                    let { x, y } = item;
                    switch (e.key) {
                        case 'ArrowUp': y -= MOVE_STEP; break;
                        case 'ArrowDown': y += MOVE_STEP; break;
                        case 'ArrowLeft': x -= MOVE_STEP; break;
                        case 'ArrowRight': x += MOVE_STEP; break;
                    }
                    return { ...item, x, y };
                };

                updatePages(prev =>
                    prev.map(page => {
                        if (page.id !== selectedPageId) return page;

                        return {
                            ...page,
                            shapes: page.shapes.map(s =>
                                s.id === selectedShapeId ? moveItem(s) : s
                            ),
                            texts: page.texts.map(t =>
                                t.id === selectedTextId ? moveItem(t) : t
                            ),
                            slots: page.slots.map(sl =>
                                selectedIds.includes(sl.id) ? moveItem(sl) : sl
                            ),
                        };
                    })
                );
            }

            // Delete key
         else if (e.key === "Delete" || e.key === "Backspace") {
                e.preventDefault();
                handleDeleteButton();
            }
            // Ctrl/Cmd + Z = Undo
            else if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                if (canUndo) undo();
            }
            // Ctrl/Cmd + Y or Ctrl+Shift+Z = Redo
            else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) {
                e.preventDefault();
                if (canRedo) redo();
            }
            // Ctrl/Cmd + D = Duplicate
            else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
                e.preventDefault();
                handleDuplicate();
            }
            // S key = Swap function
            else if (e.key.toLowerCase() === 's') {
                e.preventDefault();
                handleSwapSelectedSlots(); // <-- your function
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [
        selectedPageId,
        selectedShapeId,
        selectedTextId,
        selectedIds,
        handleDeleteButton,
        handleDuplicate,
        undo,
        redo,
        canUndo,
        canRedo,
    ]);
   
      // #endregion

    // #region Trim Area function
    const cutGapMM = cutGap; // e.g., 0.5 mm
    function getCutMarginPx(
        unit: string,
        pageWidth: number,
        cutMarginMM: number,
        stageWidth: number
    ): number {
        const normalized = unit.toLowerCase().includes('inch') ? 'inch' : 'cm';
        if (normalized === 'cm') {
            return cutMarginMM * (stageWidth / pageWidth);
        } else {
            return (cutMarginMM / 2.54) * (stageWidth / pageWidth);
        }
    }
   
    const cutMarginPx = getCutMarginPx(unit, pageWidth, cutGapMM, stageSize.width);
    const cutAreaWidthPx = stageSize.width - cutMarginPx * 2;
    const cutAreaHeightPx  = stageSize.height - cutMarginPx * 2;
       // #endregion

    // #region Drop Down Topbar menus
  
    const dropdownStyle: React.CSSProperties = {
        position: "absolute",
        top: 48,
        left: 0,
        background: "#fff",
        border: "1px solid #ccc",
        borderRadius: 6,
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)", // subtle drop shadow
        padding: 10,
        zIndex: 10,
        minWidth: 120,       
    };

    const iconSize = 24;

    // Add "strokeShadow" to the union type
    const [openDropdown, setOpenDropdown] = useState<
        "effects" | "shapes" | "opacity" | "layerOrder" | "strokeShadow" | "textOptions" | "slot" | null
    >(null);

    // Update toggleDropdown accordingly
    const toggleDropdown = (
        name: "effects" | "shapes" | "opacity" | "layerOrder" | "strokeShadow" | "textOptions" | "slot" | null
    ) => {
        setOpenDropdown((prev) => (prev === name ? null : name));
    };


    // Inside your component
    useEffect(() => {
        if (!selectedElement && openDropdown === 'opacity') {
            setOpenDropdown(null); // close the dropdown if nothing is selected
        }
    }, [selectedElement, openDropdown]);


    useEffect(() => {
        if (!selectedElement || (selectedPage?.elements.length ?? 0) < 2) {
            setOpenDropdown(prev => (prev === "layerOrder" ? null : prev));
        }                       // close the dropdown if nothing is selected
    }, [selectedElement, selectedPage?.elements.length]);


      // #endregion

    // #region Mark Used Images

    const [, setMarkedImages] = useState<AlbumImage[]>([]);
    // Whenever pages or uploadedImages change, recalc usedImages

    const normalizeImagePath = (src?: string) => {
        if (!src) return "";
        return src.split("?")[0].split("/").pop() || "";
    };


    useEffect(() => {
        const usedImageNames = new Set(
            pages.flatMap(page =>
                page.slots
                    .filter(slot => slot.imageSrc)
                    .map(slot => normalizeImagePath(slot.imageSrc))
            )
        );

        setMarkedImages(
            albumData.uploadedImages.map(img => ({
                ...img,
                used: usedImageNames.has(img.name),
            }))
        );
    }, [pages, albumData.uploadedImages]);


    // Collect all imageSrc URLs actually used in pages
    const usedImageNames = new Set(
        pages.flatMap(page =>
            page.slots
                .filter(slot => slot.imageSrc)
                .map(slot => normalizeImagePath(slot.imageSrc))
        )
    );

    // Filter uploaded images to only mark used ones
    const usedImages: AlbumImage[] = albumData.uploadedImages
        .filter(img => usedImageNames.has(img.name))
        .map(img => ({ ...img, used: true }));

    // call this to update the used images
    useEffect(() => {
        recalcMarkedImages(pages);
    }, [pages]);

    const recalcMarkedImages = (pagesToUse?: PageType[]) => {
        const pagesForCalc = pagesToUse ?? pages;

        const usedImageNames = new Set(
            pagesForCalc.flatMap(page =>
                page.slots
                    .filter(slot => slot.imageSrc)
                    .map(slot => normalizeImagePath(slot.imageSrc))
            )
        );

        const newMarkedImages = albumData.uploadedImages.map(img => ({
            ...img,
            used: usedImageNames.has(img.name),
        }));

        setMarkedImages(newMarkedImages);
    };

       // #endregion

    // #region Export
    const [exporting, setExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [, setExportId] = useState<string | null>(null);
    const [, setTotalPages] = useState(0);
    type ExportMode = "print" | "proof" | "web-proof" | "slideshow" | null;
    const [exportMode, setExportMode] = useState<ExportMode>(null);
    const EXPORT_API_URL = process.env.REACT_APP_API_URL!;
    const [isPrintExported, setIsPrintExported] = useState(loadedProjectData?.printExported ?? false);
    const [webProofUrl, setWebProofUrl] = useState<string | null>(null);
    const [showOrderModal, setShowOrderModal] = useState(false);

    const [exportOrderId, setExportOrderId] = useState("");
    const [exportOrderRef, setExportOrderRef] = useState("");
    const [orderError, setOrderError] = useState("")
    const [showWebProofModal, setShowWebProofModal] = useState(false);
    const [isCutMergeOpen, setIsCutMergeOpen] = useState(false);


    type ClientInfo = {
        groomName: string;
        groomEmail: string;
        brideName: string;
        brideEmail: string;
        sendInvitation: boolean;
        sendCopy: boolean;
    };

    const [clientInfo, setClientInfo] = useState<ClientInfo>({
        groomName: "",
        groomEmail: "",
        brideName: "",
        brideEmail: "",
        sendInvitation: true,
        sendCopy: false,
    });



    const isValidEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const hasValidClient =
        (clientInfo.groomName.trim() !== "" &&
            isValidEmail(clientInfo.groomEmail)) ||
        (clientInfo.brideName.trim() !== "" &&
            isValidEmail(clientInfo.brideEmail));


    const startPrintExport = useCallback(async (orderId: string, orderRef?: string) => {
        const source = albumData ?? loadedProjectData;
        if (!source) return;

        setExportMode("print");
        setExporting(true);
        setExportProgress(0);
        setTotalPages(source.pages.length);

        try {
            // --- Start export on backend ---
            const res = await fetch(`${EXPORT_API_URL}/export`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },

                body: JSON.stringify({
                    albumName: source.albumName,
                    albumWidth: source.size.width,
                    albumType: source.albumType, //  ADD THIS
                    albumHeight: source.size.height,
                    size: source.size,
                    unit: source.size.unit || "cm",
                    mode: "print",
                    orderId,   //  ADD
                    orderRef,   //  ADD
                    editorStageWidth: stageSize.width,
                    editorStageHeight: stageSize.height,
                    //for the Fmount and ARCH
                    opening: source.opening ?? null,
                    handtornMode: source.handtornMode ?? null,

                    albumOverlay: source.overlay ?? null,  //  new
                    pagesOverlay: source.pages.map(p => p.overlay ?? null), // optional, per page
                }),
                credentials: "include" // <-- ensures cookies / auth headers are sent with cross-origin
            });

            if (!res.ok) throw new Error("Export for Print failed");

            const { exportId } = await res.json();
            if (!exportId) throw new Error("No export ID returned");
            setExportId(exportId);

            const failedPages: number[] = [];
            let dialogShown = false;

            const interval = setInterval(async () => {
                try {
                    const progressRes = await fetch(`${EXPORT_API_URL}/export/progress/${exportId}`, {
                        headers: { "Authorization": `Bearer ${token}` },
                        credentials: "include",
                    });
                    if (!progressRes.ok) throw new Error("Progress not found");
                    const data = await progressRes.json();

                    const percent = Math.round((data.completed / data.total) * 100);
                    setExportProgress(percent);

                    if (data.failedPages && Array.isArray(data.failedPages)) {
                        failedPages.splice(0, failedPages.length, ...data.failedPages);
                    }

                    if (data.completed >= data.total && !dialogShown) {
                        dialogShown = true;
                        clearInterval(interval);

                        const finalOrderId = orderId;
                        const finalOrderRef = orderRef || "N/A";

                        setExporting(false);
                        setExportId(null);
                        setExportProgress(100);
                        setIsPrintExported(true);

                        setDialogTitle("Export");
                        setDialogMessage(
                            failedPages.length === 0
                                ? `All spreads were exported successfully for order ${finalOrderId}.`
                                : `Export completed with errors (${failedPages.length} spread):\n` +
                                failedPages.join(", ")
                        );

                        setDialogCallback(null);
                        setDialogSingleButton(true);
                        setDialogOpen(true);
                    }
                } catch (err) {
                    console.error("Progress polling failed:", err);
                    clearInterval(interval);
                    setExporting(false);
                    setDialogTitle("Error");
                    setDialogMessage("The export failed during the progress.");
                    setDialogCallback(null);
                    setDialogSingleButton(true);
                    setDialogOpen(true);
                }
            }, 500);

        } catch (err) {
            console.error(err);
            setExporting(false);
            setExportProgress(0);  

            setDialogTitle("Error");
            setDialogMessage("Export failed. Please try again or contact support.");
            setDialogCallback(null);
            setDialogSingleButton(true);
            setDialogOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [albumData, loadedProjectData, stageSize, token, EXPORT_API_URL]);

    // This is the function called from your MenuItem
    const exportAlbumForPrint = useCallback(() => {
        const source = loadedProjectData || albumData;
        if (!source) return;

        setDialogTitle("Final Export For Print");
        setDialogMessage(
            "Once you export this order, your files will be sent directly to Seldex and linked to a live order." + 
            "No further changes can be made after this point.\n\n" +
            "Do you want to continue?"
        );
        setDialogSingleButton(false);

        setDialogCallback(() => () => {
            setDialogOpen(false);

            //  OPEN ORDER MODAL INSTEAD OF REUSING DIALOG
            setExportOrderId("");
            setExportOrderRef("");
            setOrderError("");
            setShowOrderModal(true);
        });

        setDialogOpen(true);
    }, [loadedProjectData, albumData]);
   

    //proof
    const exportAlbumProof = async () => {
        if (!albumData) return;
        setExportMode("proof");
        setExporting(true);
        setExportProgress(0);
        setTotalPages(albumData.size?.pages || albumData.pages.length); // total pages for progress

        try {
            // Start the export and get exportId
            const res = await fetch(`${EXPORT_API_URL}/export`, {

                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    albumName,
                    size: albumData.size,
                    albumType: albumType, //  ADD THIS
                    unit: "cm",
                    mode: "proof",
                    editorStageWidth: stageSize.width,
                    editorStageHeight: stageSize.height
                }),
            });

            if (!res.ok) throw new Error("Export failed");

            const { exportId } = await res.json();
            if (!exportId) throw new Error("No export ID returned");
            setExportId(exportId);

            //  Poll backend for progress
            const interval = setInterval(async () => {
                try {
                    const progressRes = await fetch(`${EXPORT_API_URL}/export/progress/${exportId}`);
                    if (!progressRes.ok) throw new Error("Progress check failed");

                    const data = await progressRes.json();
                    const completed = data.completed || 0;
                    const total = data.total || 1;
                    const percent = (completed / total) * 100;

                    setExportProgress(percent);

                    if (completed >= total) {
                        // Stop polling immediately
                        clearInterval(interval);
                        setExporting(false);
                        setExportId(null);
                        setExportProgress(100); // ensure it stays at 100%

                        // Trigger PDF download here (proof export)
                        const pdfRes = await fetch(`${EXPORT_API_URL}/export/download/proof/${exportId}`);
                        if (pdfRes.ok) {
                            const blob = await pdfRes.blob();
                            const blobUrl = window.URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = blobUrl;
                            link.download = `${albumName || "proof"}_proof.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(blobUrl);
                        }
                    }
                } catch (err) {
                    //  console.error("Progress polling failed:", err);
                    clearInterval(interval);
                    setExporting(false);
                }
            }, 500);

        } catch (err) {
            console.error(err);
            setDialogMessage("Export Fail. Try again or contact the support.");
            setDialogTitle("Error");
            setDialogCallback(null);
            setDialogSingleButton(true);
            setDialogOpen(true);

            setExporting(false);
            setExportProgress(0);
        }
    };



    //export web proof
    const handleExportWebProof = async (clients: ClientInfo) => {
        if (!albumData) return;
        setExportMode("web-proof");
        setExporting(true);
        setExportProgress(0);

        const decoded = jwtDecode<TokenPayload>(token!);

        try {   
            // Trigger backend export
            const res = await fetch(`${EXPORT_API_URL}/export`, {          
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    albumName,
                    size: albumData.size,
                    unit: "cm",
                    mode: "web-proof",
                    webOnly: true,
                    editorStageWidth: stageSize.width,
                    editorStageHeight: stageSize.height,
                    clients: {
                        ...clients,
                        userId: decoded.id
                    }
                }),
            });

            const { exportId: backendExportId } = await res.json();
            if (!backendExportId) throw new Error("No export ID returned");
            
            setExportId(backendExportId);
  
            // Poll export progress
            const interval = setInterval(async () => {
                try {
                    const progressRes = await fetch(`${EXPORT_API_URL}/export/progress/${backendExportId}`);
                    const data = await progressRes.json();
                    const percent = (data.completed / data.total) * 100;
                    setExportProgress(percent);

                    if (data.completed >= data.total) {
                        clearInterval(interval);
                        setExportProgress(100);

                        if (!token) return;
                        const decoded = jwtDecode<TokenPayload>(token);
                        const username = decoded.username;

                        // Use the exact backend exportId for the folder
                        const albumFolder = `${albumData.albumName}_${backendExportId}`; // 110_ALB-C15BDA2
                        const webProofUrl = `/eda_online/proof/${username}/${albumFolder}`;

                        setWebProofUrl(webProofUrl);
                    }
                

                } catch (err) {
                    console.error(err);
                    clearInterval(interval);
                    setExporting(false);
                }
            }, 500);

        } catch (err) {
            console.error(err);
            setExporting(false);
            alert("The Web-Proof export fail.");
        }
    };

  
    const [showNamesDialog, setShowNamesDialog] = useState(false);
    const [coupleNames, setCoupleNames] = useState(""); // single line of text
    const [musicFile, setMusicFile] = useState<File | null>(null);
    const handleRequestSlideshowNames = () => setShowNamesDialog(true);
    const [videoProgress, setVideoProgress] = useState(0);
    const [creatingVideo, setCreatingVideo] = useState(false); // new progress
    const videoPhaseStartedRef = useRef(false);

    // Called when the user confirms the dialog
    const handleConfirmSlideshow = async () => {
        setShowNamesDialog(false);
        await exportAlbumSlideshow(musicFile ?? undefined, coupleNames); // pass both
    };
  
   
    // Export pages & video
    const exportAlbumSlideshow = async (musicFile?: File, coupleNames?: string) => {
        if (!albumData) return;

        setExportMode("slideshow");
        setExporting(true);
        setCreatingVideo(false); // spinner off initially
        setExportProgress(0);
        setTotalPages(albumData.pages.length);

        // Reset the ref for this export
        videoPhaseStartedRef.current = false;

        try {
            const formData = new FormData();
            formData.append("albumName", albumData.albumName);
            formData.append("editorStageWidth", stageSize.width.toString());
            formData.append("editorStageHeight", stageSize.height.toString());
            if (musicFile) formData.append("music", musicFile);
            if (coupleNames) formData.append("coupleNames", coupleNames);

            const res = await fetch(`${EXPORT_API_URL}/export/slideshow`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            if (!res.ok) throw new Error(await res.text());

            const { exportId } = await res.json();
            if (!exportId) throw new Error("No exportId");

            setExportId(exportId);

            // Polling interval
            const interval = setInterval(async () => {
                try {
                    const progressRes = await fetch(`${EXPORT_API_URL}/export/progress/${exportId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                        cache: "no-store",
                    });
                    const data = await progressRes.json();

                    // --- Page export progress ---
                    setExportProgress(Math.round((data.completed / data.total) * 100));

                    // --- Video progress ---
                    setVideoProgress(data.videoProgress ?? 0);

                    if (!data.videoReady) {
                        // Show video creation overlay if all pages are done
                        const creating = data.completed >= data.total;
                        setCreatingVideo(creating);
                        setExporting(true);
                    } else {
                        // Video done
                        clearInterval(interval);
                        setCreatingVideo(false);
                        setExporting(false);
                        setExportId(null);

                        // Download MP4
                        const mp4Res = await fetch(`${EXPORT_API_URL}/export/download/slideshow/${exportId}`);
                        if (mp4Res.ok) {
                            const blob = await mp4Res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${albumData.albumName}_slideshow.mp4`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            URL.revokeObjectURL(url);
                        }
                    }
                } catch (err) {
                    console.error("[FRONTEND] Polling error:", err);
                    clearInterval(interval);
                    setCreatingVideo(false);
                    setExporting(false);
                }
            }, 500);


        } catch (err) {
            setCreatingVideo(false);
            setExporting(false);
            console.error(err);
        }
    };

       // #endregion

    // #region Clear Pages

    const clearAlbumPageContents = (pages: PageType[]): PageType[] => {
        return pages.map(page => ({
            ...page,
            slots: [],      // remove all slots
            elements: [],   // remove all elements
            backgroundImageSrc: null,
            backgroundOriginalSrc: null,
            showBackground: false,
            backgroundOpacity: 1,
            backgroundZoom: 1,
            backgroundOffsetX: 0,
            backgroundOffsetY: 0,
            backgroundColor: "#fff",
        }));
    };
    // Clear pages when user clicks a button
    const handleClearAlbumContents = () => {
        setDialogTitle("Confirm");
        setDialogMessage(
            "The content of all spreads will be erased. Do you want to continue?"
        );

        setDialogCallback(() => () => {
            clearAlbumPagesInternal();
        });

        setDialogSingleButton(false);
        setDialogOpen(true);
    };

    const clearAlbumPagesInternal = () => {
        const newBackgroundImages: Record<string, null> = {};
        const newBackgroundSizes: Record<string, null> = {};
        const newBackgroundOriginals: Record<string, null> = {};
        const newOpacities: Record<string, number> = {};
        const newZooms: Record<string, number> = {};
        const newOffsetsX: Record<string, number> = {};
        const newOffsetsY: Record<string, number> = {};

        albumData.pages.forEach(page => {
            const pageId = page.id;

            backgroundImagesRef.current[pageId] = null;
            newBackgroundImages[pageId] = null;
            newBackgroundSizes[pageId] = null;
            newBackgroundOriginals[pageId] = null;
            newOpacities[pageId] = 1;
            newZooms[pageId] = 1;
            newOffsetsX[pageId] = 0;
            newOffsetsY[pageId] = 0;
        });

        setBackgroundImages(newBackgroundImages);
        setBackgroundImageOriginalSizes(newBackgroundSizes);
        setBackgroundOriginalPaths(newBackgroundOriginals);
        setBackgroundOpacities(newOpacities);
        setBackgroundZooms(newZooms);
        setBackgroundOffsetsX(newOffsetsX);
        setBackgroundOffsetsY(newOffsetsY);
        setBackColor("#fff");

        updatePages(prev => clearAlbumPageContents(prev));
    };
      // #endregion

    // #region Export CSS

    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
    };

    const modalStyle: React.CSSProperties = {
        backgroundColor: "#fff",
        padding: "20px 40px",
        borderRadius: 10,
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 300,
    };

    const modalTextStyle: React.CSSProperties = {
        marginBottom: 12,
        fontSize: 16,
        color: "#333",
        textAlign: "center",
    };

    // #endregion

    // #region Spread Manager

    const handleApplyPages = (newPages: PageType[]) => {
        const updatedPages = newPages.map((page, index) => ({
            ...page,
            order: index,
        }));


        setPages(updatedPages);

        setAlbumData(prev => ({
            ...prev,
            pages: updatedPages,
            pageCount: updatedPages.length,
        }));

        saveAlbumToServer(
            {
                ...albumData,
                pages: updatedPages,
                pageCount: updatedPages.length,
            },
            setAlbumData,
            token
        );
    };

    const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

    const openPageManager = async () => {

        setShowCenterLine(false);
        // wait for React render
        await new Promise(requestAnimationFrame);

        // force Konva redraw flush
        stageRefs.current[pages[0].id]?.batchDraw?.();

        // wait one more frame so Konva commits visually
        await new Promise(requestAnimationFrame);

        const newThumbs: Record<string, string> = {};

        pages.forEach((page) => {
            const stage = stageRefs.current[page.id];

            if (!stage) return;

            newThumbs[page.id] = stage.toDataURL({
                pixelRatio: 0.7,
            });
        });

        setThumbnails(newThumbs);

        setShowCenterLine(true);
        setShowPageManager(true);
    };

    // #endregion

    const [hoveredSlotId, setHoveredSlotId] = useState<string | null>(null);

    const scale = Math.min(
        stageSize.width / albumData.size.width,
        stageSize.height / albumData.size.height
    );
   

    const closeGapPopup = () => {
        setHorizontalGap(0);
        setVerticalGap(0);
        setIsGapPopupOpen(false);
    };


    const cutMergeButtonRef = useRef<HTMLButtonElement | null>(null);
    const [cutMergePos, setCutMergePos] = useState({
        left: 0,
        bottom: 0
    });

      

    return (
        <div
            className="album-page-designer"
            onContextMenu={(e) => e.preventDefault()}
        >

        <div
            style={{
                display: "grid",
                gridTemplateColumns: `168px minmax(0, 1fr) ${photoUploaderWidth}px`,
                gridTemplateRows: "auto 1fr auto",      // top bar, main area, bottom bar
                height: "100vh",
                width: "100vw",
                overflow: "hidden",
                minWidth: 0, // 
            }}
            >

            {/* Left Panel (full height) */}
            <div
                style={{
                    gridRow: "1 / 3", // only spans top + main
                    gridColumn: "1 / 2",
                    background: "#fafafa",
                    borderRight: "1px solid #ccc",
                    padding: "1px",
                    width:"210",
                }}
                
            >
                {/*<h3>Templates</h3>*/}
                {/*   {LA_03_H2_V1.map((t) => (*/}
                {/*    <button*/}
                {/*        key={t.id}*/}
                {/*        onClick={() => applyTemplate(t)} // pass template directly*/}
                        
                {/*        style={{ display: "block", marginBottom: 10, width: "100%" }}*/}
                {/*    >*/}
                {/*        {t.name}*/}
                {/*    </button>*/}
                {/*))}*/}
    
               <MessageDialog
                    open={dialogOpen}
                    title={dialogTitle}
                    message={dialogMessage}  // just a string 
                    singleButton={dialogSingleButton}
                    onClose={() => {
                        setDialogOpen(false);
                    }}
                    onConfirm={async () => {
                        if (dialogCallback) await dialogCallback();
                        setDialogOpen(false);
                    }}
                />
                    {albumType === "PRINTS" ? (
                        <PrintSelector
                            products={printThumbnails}
                            orientation={orientation}
                            onSelect={handlePrintSelect}
                            onGoBack={handleGoBackClick}
                            isSaving={isSaving}
                        />
                    ) : (
                        <TemplateLoader
                            albumType={albumType}
                            templates={templateThumbnails}
                            onTemplateSelect={handleTemplateChange}
                            onGoBack={handleGoBackClick}
                            isSaving={isSaving}
                        />
                    )}

            </div>


            {/* Top Bar (only above center) */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    background: "#fff",
                    borderBottom: "1px solid #bbb",
                    gap: 11,
                    height: 46,
                }}
                
            >

                <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file && selectedPageId) {
                            await handleBackgroundImageUpload(selectedPageId, file);
                            setShowBackgrounds(prev => ({
                                ...prev,
                                [selectedPageId]: true,
                            }));
                        }
                    }}
                />


                {/* Left-side buttons */}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {/* Undo / Redo buttons */}
                        <Tooltip title="Undo last action (Ctrl + Z / ⌘ + Z)" arrow>
                        <span>
                            <button
                                className="toolbar-button"
                                onClick={undo}
                                disabled={!canUndo}
                            >
                                <img src={`${process.env.PUBLIC_URL}/undo_icon.png`} alt="Undo" height={iconSize} />
                            </button>
                        </span>
                    </Tooltip>

                        <Tooltip title="Redo last action (Ctrl + Shift + Z / ⌘ + Shift + Z)" arrow>
                        <span>
                            <button
                                className="toolbar-button"
                                onClick={redo}
                                disabled={!canRedo}
                            >
                                <img src={`${process.env.PUBLIC_URL}/redo_icon.png`} alt="Redo" height={iconSize} />
                            </button>
                        </span>
                    </Tooltip>

                        {/* NEW Global color picker */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {/* Fill Color */}                        
                            <ColorPicker
                                color={getSelectedColor("fill")}
                                onChange={(newColor) => handleColorChange("fill", newColor)}
                                disabled={disabledProducts.has(albumType)}
                            />                                                                   

                        {/* Always show Effects button */}
                        <div style={{ position: "relative" }}>
                                <Tooltip
                                    title="Add Effects"
                                    arrow
                                >                               
                                    <button
                                    style={{
                                        background: openDropdown === "effects" ? "rgba(25, 118, 210, 0.1)" : "#fff",
                                        border: openDropdown === "effects" ? "1px solid #59b2d8" : "1px solid #fff",
                                        borderRadius: 4,
                                        padding: "4px 14px",
                                        cursor: "pointer",
                                       // height: 44,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!e.currentTarget.disabled) {
                                            e.currentTarget.style.borderColor = "#59b3d8";
                                            e.currentTarget.style.backgroundColor = "rgba(25, 118, 210, 0.1)";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!e.currentTarget.disabled) {
                                            e.currentTarget.style.borderColor = openDropdown === "effects" ? "#59b3d8" : "#fff";
                                            e.currentTarget.style.backgroundColor = openDropdown === "effects" ? "rgba(25, 118, 210, 0.1)" : "#fff";
                                        }
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation(); // prevent stage click
                                        toggleDropdown("effects");
                                    }}
                                >
                                    <span>
                                        <img src={`${process.env.PUBLIC_URL}/effects.png`} alt="Effects" height={iconSize} />
                                    </span>
                                </button>
                            </Tooltip>

                            {openDropdown === "effects" && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 53,
                                        left: -112,
                                        zIndex: 1000,
                                        background: "#fff",
                                        borderRadius: 6,
                                        boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                                        padding: "8px 16px",
                                        minWidth: 350,
                                        border: "1px solid #b2b2b2",
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {selectedElement ? (
                                        <EffectsControl
                                            selectedElement={selectedElement}
                                            slots={selectedPage?.slots ?? []}
                                            shapes={selectedPage?.shapes ?? []}
                                                texts={selectedPage?.texts ?? []}
                                                albumType={albumType}
                                            onChange={(updatedProps) => {
                                                if (!selectedPageId) return;
                                                if (selectedElement.type === "slot")
                                                    handleSlotChange(selectedPageId, selectedElement.id, updatedProps);
                                                if (selectedElement.type === "text")
                                                    handleTextChange(selectedElement.id, updatedProps);
                                                if (selectedElement.type === "shape")
                                                    handleShapeChange(selectedPageId, selectedElement.id, updatedProps);
                                            }}
                                        />
                                    ) : (
                                        <div style={{ padding: 16 }}>Select an item to see effects</div>
                                    )}
                                </div>
                            )}
                        </div>


                        {selectedText ? (
                            // Text top bar
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {/* Font Family */}
                                {selectedText && (
                                    <FontDropdown
                                        selectedTextId={selectedText.id}
                                        selectedFont={selectedText.fontFamily ?? "Arial"}
                                        handleTextChange={handleTextChange}
                                        preloadedFonts={Array.from(
                                            new Set(pages.flatMap(p => p.texts.map(t => t.fontFamily ?? "Arial")))
                                        )}
                                    />
                                )}                           
                                                         
                                {/* Font Size */}
                                <Tooltip title="Font Size" arrow>
                                    <span>
                                <input
                                    type="number"
                                    className="font-size"
                                    value={selectedText.fontSize ?? 16}
                                    step={1}
                                    onChange={(e) =>
                                        handleTextChange(selectedText.id, { fontSize: Number(e.target.value) })
                                    }
                                    style={{
                                        width: 50,
                                       // height: 28,
                                        fontSize: 14,
                                        borderRadius: 4,
                                       // border: "1px solid #ccc",
                                        textAlign: "center",
                                    }}
                                        />
                                    </span>
                                </Tooltip>

                                {/* Bold button */}
                                <button
                                    style={{
                                        fontWeight: "bold",
                                        background: selectedText.fontWeight === "bold" ? "#e0e0e0" : "#fff",
                                        border: "1px solid #ccc",
                                        borderRadius: 4,
                                        padding: "4px 10px",
                                        cursor: "pointer",
                                        height: 32,
                                        fontSize: 14,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = "#59b3d8";
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = "#ccc";
                                    }}
                                    onClick={() =>
                                        handleTextChange(selectedText.id, {
                                            fontWeight: selectedText.fontWeight === "bold" ? "normal" : "bold",
                                        })
                                    }
                                >
                                    B
                                </button>

                                {/* Italic button */}
                                <button
                                    style={{
                                        fontStyle: "italic",
                                        background: selectedText.fontStyle === "italic" ? "#e0e0e0" : "#fff",
                                        border: "1px solid #ccc",
                                        borderRadius: 4,
                                        padding: "4px 14px",
                                        cursor: "pointer",
                                        height: 32,
                                        fontSize: 14,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = "#59b3d8";
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = "#ccc";
                                    }}
                                    onClick={() =>
                                        handleTextChange(selectedText.id, {
                                            fontStyle: selectedText.fontStyle === "italic" ? "normal" : "italic",
                                        })
                                    }
                                >
                                    I
                                </button>


                                {/* Align buttons */}
                                <div style={{ display: "flex", gap: 4 }}>
                                    {[
                                        { align: "left", icon: `${process.env.PUBLIC_URL}/align_left.png` },
                                        { align: "center", icon: `${process.env.PUBLIC_URL}/align_center.png` },
                                        { align: "right", icon: `${process.env.PUBLIC_URL}/align_right.png` },
                                    ].map(({ align, icon }) => (
                                        <button
                                            key={align}
                                            onClick={() =>
                                                handleTextChange(selectedText.id, { align: align as any })
                                            }
                                            className={`toolbar-button ${selectedText.align === align ? "active" : ""}`}
                                            style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: 4,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: selectedText.align === align ? "#e0e0e0" : "#fff",
                                                border: "1px solid #ccc",
                                                cursor: "pointer",
                                                padding: 0,
                                                transition: "border 0.2s, background 0.2s",
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!selectedText || selectedText.align !== align)
                                                    (e.currentTarget as HTMLButtonElement).style.border = "1px solid #59b3d8";
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!selectedText || selectedText.align !== align)
                                                    (e.currentTarget as HTMLButtonElement).style.border = "1px solid #ccc";
                                            }}
                                        >
                                            <img
                                                src={icon}
                                                alt={align}
                                                style={{ width: 28, height: 28, pointerEvents: "none" }}
                                            />
                                        </button>
                                    ))}
                                </div>
                            
                            </div>
                        ) : (
                            // Default buttons: Effects + Shapes
                                <>     
                                  
                                    {/*Shapes dropdown with icons */}
                                    <div style={{ position: "relative" }}>                                     
                                            <Tooltip
                                                title="Add Shapes"
                                                arrow
                                                disableHoverListener={disabledProducts.has(albumType)}
                                                disableFocusListener={disabledProducts.has(albumType)}
                                                disableTouchListener={disabledProducts.has(albumType)}
                                            >
                                                <button className="toolbar-button"
                                                   disabled={disabledProducts.has(albumType)}
                                                    onClick={() => toggleDropdown("shapes")}>
                                                    
                                                <span>
                                                    <img src={`${process.env.PUBLIC_URL}/shapes.png`} alt="Add Shape" height={iconSize} />
                                                </span>
                                            </button>
                                        </Tooltip>

                                        {openDropdown === "shapes" && (
                                            <div style={dropdownStyle}>                                               
                                                {[
                                                    { type: "ellipse", icon: `${process.env.PUBLIC_URL}/ellipse.png` },
                                                    { type: "rect", icon: `${process.env.PUBLIC_URL}/rect.png` },
                                                    { type: "triangle", icon: `${process.env.PUBLIC_URL}/triangle.png` },
                                                    { type: "star", icon: `${process.env.PUBLIC_URL}/star.png` },
                                                ].map((shape) => (
                                                    <button
                                                        key={shape.type}
                                                        className="toolbar-button dropdown-item"
                                                        onClick={() => {
                                                            handleAddShape(shape.type as ShapeType);
                                                            toggleDropdown(null); // close after picking
                                                        }}
                                                    >
                                                        <img src={shape.icon} alt={shape.type} height={20} width={20} style={{ marginRight: 8 }} />
                                                        <span>{shapeLabels[shape.type as ShapeType]}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/*Add text */}
                                    {!selectedText && (                                
                                            <Tooltip
                                                title="Add Text"
                                                arrow
                                                disableHoverListener={disabledProducts.has(albumType)}
                                                disableFocusListener={disabledProducts.has(albumType)}
                                                disableTouchListener={disabledProducts.has(albumType)}
                                            >
                                                <button className="toolbar-button"
                                                    disabled={disableText.has(albumType)}
                                                    onClick={() => handleAddText()}>                                                 
                                                <span>
                                                    <img src={`${process.env.PUBLIC_URL}/text.png`} alt="Add Text" height={iconSize} />
                                                </span>
                                            </button>
                                        </Tooltip>
                                        
                                    )}
                                    {/*Add Slot */}
                                    <div style={{ position: "relative" }}>
                                            <Tooltip
                                                title="Add Slot"
                                                arrow
                                                disableHoverListener={disabledProducts.has(albumType)}
                                                disableFocusListener={disabledProducts.has(albumType)}
                                                disableTouchListener={disabledProducts.has(albumType)}
                                            >
                                            <button
                                                    className="toolbar-button"
                                                    disabled={disabledProducts.has(albumType)}
                                                onClick={() => toggleDropdown("slot")}
                                            >
                                                <span>
                                                    <img src={`${process.env.PUBLIC_URL}/slot.png`} alt="Add Slot" height={iconSize} />
                                                </span>
                                            </button>
                                        </Tooltip>

                                        {openDropdown === "slot" && (
                                            <div style={dropdownStyle}>
                                                <button
                                                    className="toolbar-button dropdown-item"
                                                    onClick={() => { handleAddSlot("horizontal"); toggleDropdown(null); }}
                                                >
                                                    <img src={`${process.env.PUBLIC_URL}/slot_horizontal.png`} alt="Horizontal" height={20} width={20} style={{ marginRight: 8 }} />
                                                    <span>Horizontal</span>
                                                </button>

                                                <button
                                                    className="toolbar-button dropdown-item"
                                                    onClick={() => { handleAddSlot("vertical"); toggleDropdown(null); }}
                                                >
                                                    <img src={`${process.env.PUBLIC_URL}/slot_vertical.png`} alt="Vertical" height={20} width={20} style={{ marginRight: 8 }} />
                                                    <span>Vertical</span>
                                                    </button>

                                                    <button
                                                        className="toolbar-button dropdown-item"
                                                        onClick={() => { handleAddSlot("square"); toggleDropdown(null); }}
                                                    >
                                                        <img src={`${process.env.PUBLIC_URL}/slot_square.png`} alt="Square" height={20} width={20} style={{ marginRight: 8 }} />
                                                        <span>Square</span>
                                                    </button>

                                                    <button
                                                        className="toolbar-button dropdown-item"
                                                        onClick={() => { handleAddSlot("full"); toggleDropdown(null); }}
                                                    >
                                                        <img src={`${process.env.PUBLIC_URL}/slot_full.png`} alt="Full Size" height={20} width={20} style={{ marginRight: 8 }} />
                                                        <span>Full Size</span>
                                                    </button>
                                                </div>

                                        )}

                                        </div>


                            </>
                        )}
                    </div>              
                </div>

                    {/*Center buttons */}
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <Tooltip
                            title="Clear Spreads"
                            arrow
                            disableHoverListener={disabledProducts.has(albumType)}
                            disableFocusListener={disabledProducts.has(albumType)}
                            disableTouchListener={disabledProducts.has(albumType)}
                        >
                        <span>
                                <button
                                 disabled={isAlbumType}
                                className="toolbar-button"
                                onClick={() => handleClearAlbumContents()}
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/clear_pages.png`}
                                    alt="Clear Spreads"
                                    height={iconSize}
                                />
                            </button>
                        </span>
                    </Tooltip>

                        <Tooltip
                            title="Auto fill spreads"
                            arrow
                            disableHoverListener={disabledProducts.has(albumType)}
                            disableFocusListener={disabledProducts.has(albumType)}
                            disableTouchListener={disabledProducts.has(albumType)}
                        >
                    <span>
                  <button
                            disabled={!supportsReviews}
                            className="toolbar-button"
                            onClick={() => setAutoDialogOpen(true)}
                        >
                            <img
                                src={`${process.env.PUBLIC_URL}/auto_fill_icon.png`}
                                alt="Auto fill spreads"
                                height={iconSize}
                            />                          
                        </button>
                    </span>
                </Tooltip>

                {autoDialogOpen && (
                    <AutoCreateAlbumDialog
                        open={autoDialogOpen}
                        totalImages={albumData.uploadedImages.length}
                        totalPages={pages.length}
                        albumType={albumType}
                        albumName={albumName}
                        token={token}
                        pages={pages}
                        stageSize={stageSize}
                        updatePages={updatePages}
                        setMarkedImages={setMarkedImages}
                        onConfirm={handleAutoCreateConfirm}
                        onClose={() => setAutoDialogOpen(false)}
                    />
                )}
                    </div>
                    <MessageDialog
                        open={showNamesDialog}
                        title="Type the couple's names and choose music"
                        onClose={() => {
                            setShowNamesDialog(false);
                            setMusicFile(null);      // clear file on close
                            setCoupleNames("");       // clear couple name
                        }}
                        onConfirm={handleConfirmSlideshow}
                        confirmText="Export"
                        cancelText="Cancel"
                        singleButton={false}
                        // Pass a prop to disable confirm button dynamically
                        confirmDisabled={!musicFile}  // <-- key addition
                    >
                        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
                            {/* Couple name input */}
                            <input
                                type="text"
                                placeholder="Couple's name (e.g. Johan & Mary)"
                                value={coupleNames}
                                onChange={e => setCoupleNames(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    fontSize: 14,
                                    borderRadius: 4,
                                    border: "1px solid #ccc",
                                    outline: "none",
                                    boxSizing: "border-box",
                                }}
                            />

                            {/* Music file chooser */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <button
                                    type="button"
                                    style={{
                                        padding: "0 12px",
                                        fontSize: 14,
                                        backgroundColor: "#7da9d6",
                                        color: "#fff",
                                        border: "2px solid #4b6698",
                                        borderRadius: 4,
                                        cursor: "pointer",
                                        height: "32px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        alignSelf: "flex-start",
                                    }}
                                    onClick={() => document.getElementById("musicFileInput")?.click()}
                                >
                                    Choose music file
                                </button>

                                <input
                                    id="musicFileInput"
                                    type="file"
                                    accept="audio/*"
                                    style={{ display: "none" }}
                                    onChange={e => setMusicFile(e.target.files?.[0] ?? null)}
                                />

                                <span style={{ fontSize: 13, color: "#555", wordBreak: "break-word" }}>
                                    {musicFile?.name || "Please select a music to export the slideshow."}
                                </span>
                            </div>
                        </div>
                    </MessageDialog>


                    {/*Save spread image*/}
                    {/*<button onClick={exportSelectedStage}>*/}
                    {/*    Save Selected Spread*/}
                    {/*</button>*/}
               
                  
                    <Tooltip
                        title={
                            albumType === "AC" || albumType === "ASC"
                                ? "Spread Manager is not available for this album type"
                                : "Open Spread Manager"
                        }
                        arrow
                    >
                        <span>
                            <button
                                className="toolbar-button"
                                onClick={openPageManager}
                                disabled={albumType === "AC" || albumType === "ASC"}
                                style={{
                                    opacity: albumType === "AC" || albumType === "ASC" ? 0.5 : 1,
                                    cursor:
                                        albumType === "AC" || albumType === "ASC"
                                            ? "not-allowed"
                                            : "pointer",
                                }}
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/grid_icon.png`}
                                    alt="Open Spread Manager"
                                    height={iconSize}
                                />
                            </button>
                        </span>
                    </Tooltip>
                   

                    {/* Right-side buttons */}
                   <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

                    {/*  Layer order*/}
                    <Tooltip title="Change layer order" arrow>
                        <div style={{ position: "relative" }}>
                            <span>
                                <button
                                    className="toolbar-button"
                                    onClick={() => toggleDropdown("layerOrder")}
                                    disabled={
                                        !selectedElement || (selectedPage?.elements.length ?? 0) < 2
                                    }
                                >
                                    <img src={`${process.env.PUBLIC_URL}/layer_order.png`} alt="Layer Order" height={iconSize} />
                                </button>
                            </span>
                            {openDropdown === "layerOrder" && selectedElement && (selectedPage?.elements.length ?? 0) > 1 && (
                                <div style={{ ...dropdownStyle, minWidth: 130, border: "1px solid #b2b2b2" }}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 4px" }}>
                                        <button className="toolbar-button" onClick={() => bringForward(selectedElement.id)}>
                                            Bring Forward
                                        </button>
                                        <button className="toolbar-button" onClick={() => sendBackward(selectedElement.id)}>
                                            Send Backward
                                        </button>
                                        <button className="toolbar-button" onClick={() => bringToFront(selectedElement.id)}>
                                            Bring to Front
                                        </button>
                                        <button className="toolbar-button" onClick={() => sendToBack(selectedElement.id)}>
                                            Send to Back
                                        </button>
                                    </div>
                                </div>
                            )}

                        </div>
                    </Tooltip>

                    {/*Swap Images beetwwen slots*/}
                        <Tooltip title="Select two slots, then press the button or S to swap images" arrow>
                        <span>
                            <button
                                className="toolbar-button"
                                onClick={handleSwapSelectedSlots}
                                disabled={
                                    !selectedPageId || !(
                                        (selectedSlotIdsByPage && selectedSlotIdsByPage[selectedPageId] && selectedSlotIdsByPage[selectedPageId].length === 2) ||
                                        (!!selectedSlotId && false) // keep fallback minimal; single selectedSlotId alone can't swap
                                    )
                                }
                            >
                                <img src={`${process.env.PUBLIC_URL}/swap_icon.png`} alt="Swap Selected Slots" height={iconSize} />
                            </button>
                        </span>
                    </Tooltip>


                    {/*Flip image Horizontal - Vertical*/}
                    <Tooltip title="Flip image horizontally" arrow>
                        <span>
                            <button
                                className="toolbar-button"
                                onClick={() => handleFlip("horizontal")}
                                disabled={!(selectedElement && selectedElement.type === "slot")}
                            >
                                <img src={`${process.env.PUBLIC_URL}/flip_horizontal.png`} alt="Flip Horizontal" height={iconSize} />
                            </button>
                        </span>
                    </Tooltip>

                    <Tooltip title="Flip image vertically" arrow>
                        <span>
                            <button
                                className="toolbar-button"
                                onClick={() => handleFlip("vertical")}
                                disabled={!(selectedElement && selectedElement.type === "slot")}
                            >
                                <img src={`${process.env.PUBLIC_URL}/flip_vertical.png`} alt="Flip Vertical" height={iconSize} />
                            </button>
                        </span>
                    </Tooltip>



                    {/* Opacity dropdown */}
                    <div style={{ position: "relative" }}>
                        <Tooltip title="Change opacity" arrow>
                            <span>
                                <button
                                    className="toolbar-button"
                                    onClick={() => toggleDropdown("opacity")}
                                    disabled={!selectedElement}
                                >
                                    <img src={`${process.env.PUBLIC_URL}/opacity.png`} alt="Opacity" height={iconSize} />
                                </button>
                            </span>
                        </Tooltip>

                              {openDropdown === "opacity" && (
                                <div style={{ ...dropdownStyle, minWidth: 160, padding: "8px 16px", border: "1px solid #b2b2b2" }}>
                                    <OpacityControl
                                        selectedElement={selectedElement}
                                        texts={selectedPage?.texts ?? []}
                                        slots={selectedPage?.slots ?? []}
                                        shapes={selectedPage?.shapes ?? []}
                                        onChangeOpacity={(element) => {
                                            if (!selectedPageId) return;

                                            if (element.type === "text") {
                                                handleTextChange(element.id, { opacity: element.opacity });
                                            } else if (element.type === "slot") {
                                                handleSlotChange(selectedPageId, element.id, { opacity: element.opacity });
                                            } else if (element.type === "shape") {
                                                handleShapeChange(selectedPageId, element.id, { opacity: element.opacity });
                                            }
                                        }}
                                    />
                               </div>                       
                             )}
                       </div>

                    {/*Duplicate button*/}
                        <Tooltip title="Duplicate selected items (Ctrl + D / ⌘ + D)" arrow>
                        <span>
                            <button
                                className="toolbar-button"
                                onClick={handleDuplicate}
                                disabled={selectedIds.length === 0 && !selectedTextId && !selectedShapeId}
                            >
                                <img src={`${process.env.PUBLIC_URL}/duplicate.png`} alt="Duplicate" height={iconSize} />
                            </button>
                        </span>
                    </Tooltip>

                    {/*Delete button*/}
                        <Tooltip title="Delete selected items (Delete / Backspace)" arrow>
                            <span>
                                <button
                                    className="toolbar-button"
                                    onClick={handleDeleteButton}
                                    disabled={
                                        albumType === "MONTAGE" ||
                                        (selectedIds.length === 0 && !selectedTextId && !selectedShapeId)
                                    }
                                >
                                    <img
                                        src={`${process.env.PUBLIC_URL}/delete.png`}
                                        alt="Delete"
                                        height={iconSize}
                                    />
                                </button>
                            </span>
                        </Tooltip>

                        {/*Show  slot numbers*/}
                        <Tooltip
                            title={
                            "Show Slot Numbers"
                            }
                            arrow
                        >
                            <span>
                                <button
                                    className="toolbar-button"
                                    style={{
                                        background: showSlotNumbers ? "#e0e0e0" : "#fff",
                                        border: "1px solid #ccc",
                                        borderRadius: 4,
                                        padding: "4px 8px",
                                        cursor: albumType === "AC" ? "not-allowed" : "pointer", //  proper cursor
                                        height: 32,
                                        width: 32,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        opacity: albumType === "AC" ? 0.5 : 1, //  faded look when disabled

                                    }}
                                    onMouseEnter={(e) => {
                                        if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = "#59b3d8";
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = "#ccc";
                                    }}
                                    onClick={async () => {
                                        const newValue = !showSlotNumbers;
                                        setShowSlotNumbers(newValue);

                                        if (!albumData.projectId) {
                                            console.warn("Album ID not available, cannot update server.");
                                            return;
                                        }

                                        const token = localStorage.getItem("token");
                                        if (!token) return alert("Not authenticated");

                                        try {
                                            const response = await fetch(`${API_URL}/albums/${albumData.projectId}`, {
                                                method: "PUT",
                                                headers: {
                                                    "Content-Type": "application/json",
                                                    Authorization: `Bearer ${token}`,
                                                },
                                                body: JSON.stringify({ showSlotNumbers: newValue }),
                                            });

                                            if (!response.ok) {
                                                const errorData = await response.json();
                                              //  console.error("Failed to update showCenterLine:", errorData);
                                            }
                                        } catch (err) {
                                           // console.error("Error updating showCenterLine:", err);
                                        }
                                    }}
                                >

                                    <img
                                        src={`${process.env.PUBLIC_URL}/slot_number.png`} //
                                        alt="Show Slot Numbers"
                                        style={{
                                            width: 28,
                                            height: 28,
                                            objectFit: "contain",
                                            pointerEvents: "none", // so clicks register on the button, not the image
                                        }}
                                    />
                                </button>
                            </span>
                        </Tooltip>




                       {/*Center line*/}
                    <Tooltip
                        title={
                            albumType === "AC"
                                ? "Center line not available for cover"
                                : "Show Center Line"
                        }
                        arrow
                    >
                        <span>
                            <button
                                className="toolbar-button"
                                disabled={albumType === "AC"} //  disable for covers
                        style={{
                            background: showCenterLine ? "#e0e0e0" : "#fff",
                            border: "1px solid #ccc",
                            borderRadius: 4,
                            padding: "4px 8px",
                            cursor: albumType === "AC" ? "not-allowed" : "pointer", //  proper cursor
                            height: 32,
                            width: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: albumType === "AC" ? 0.5 : 1, //  faded look when disabled

                        }}
                        onMouseEnter={(e) => {
                            if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = "#59b3d8";
                        }}
                        onMouseLeave={(e) => {
                            if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = "#ccc";
                        }}
                                onClick={async () => {
                                    const newValue = !showCenterLine;
                                    setShowCenterLine(newValue);

                                    if (!albumData.projectId) {
                                        console.warn("Album ID not available, cannot update server.");
                                        return;
                                    }

                                    const token = localStorage.getItem("token");
                                    if (!token) return alert("Not authenticated");

                                    try {
                                        const response = await fetch(`${API_URL}/albums/${albumData.projectId}`, {
                                            method: "PUT",
                                            headers: {
                                                "Content-Type": "application/json",
                                                Authorization: `Bearer ${token}`,
                                            },
                                            body: JSON.stringify({ showCenterLine: newValue }),
                                        });

                                        if (!response.ok) {
                                            const errorData = await response.json();
                                            console.error("Failed to update showCenterLine:", errorData);
                                        }
                                    } catch (err) {
                                        console.error("Error updating showCenterLine:", err);
                                    }
                                }}
                            >
                    
                        <img
                            src={`${process.env.PUBLIC_URL}/middle_line.png`} //  
                            alt="Center Line"
                            style={{
                                width: 28,
                                height: 28,
                                objectFit: "contain",
                                pointerEvents: "none", // so clicks register on the button, not the image
                            }}
                        />
                            </button>
                        </span>
                </Tooltip>

                  

                        <BackgroundControls
                            pageId={selectedPageId ?? ""}
                            showBackground={selectedPageId ? showBackgrounds[selectedPageId] ?? false : false}
                            setShowBackground={value => selectedPageId && setShowBackground(selectedPageId, value)}
                            backgroundOpacity={selectedPageId ? backgroundOpacities[selectedPageId] ?? 1 : 1}
                            setBackgroundOpacity={value => selectedPageId && setBackgroundOpacity(selectedPageId, value)}
                            backgroundZoom={selectedPageId ? backgroundZooms[selectedPageId] ?? 1 : 1}
                            setBackgroundZoom={value => selectedPageId && setBackgroundZoom(selectedPageId, value)}
                            backgroundImageSrc={selectedPageId ? backgroundImageSrcs[selectedPageId] ?? null : null}
                            setPageBackgroundImage={setPageBackgroundImage}
                            backgroundOffsetX={selectedPageId ? backgroundOffsetsX[selectedPageId] ?? 0 : 0}
                            setBackgroundOffsetX={value => selectedPageId && setBackgroundOffsetX(selectedPageId, value)}
                            backgroundOffsetY={selectedPageId ? backgroundOffsetsY[selectedPageId] ?? 0 : 0}
                            setBackgroundOffsetY={value => selectedPageId && setBackgroundOffsetY(selectedPageId, value)}
                            backgroundImageOriginalSize={selectedPageId ? backgroundImageOriginalSizes[selectedPageId] : null}

                            updatePageById={updatePageById}
                            fileInputRef={fileInputRef}
                            stageWidth={stageSize.width}
                            stageHeight={stageSize.height}
                            onRemoveBackground={handleRemoveBackground}
                        />
                </div>

            </div>

            {/* Right Panel (full height) */}
            <div
                style={{
                    gridRow: "1 / 3", // only spans top + main
                    gridColumn: "3 / 4",
                    background: "#fafafa",
                    borderLeft: "1px solid #ccc",
                    padding: "1px",                 
                }}
                
            >
                <PhotoUploader
                    albumName={albumName}
                    disableWallArts={disableWallArts}
                    defaultImages={albumData.uploadedImages}
                    usedImages={usedImages}
                    onExportPrint={exportAlbumForPrint}
                    onExportProof={exportAlbumProof}
                    onExportWebProof={() => setShowWebProofModal(true)}               
                    printExported={isPrintExported} // always boolean
                    onRequestSlideshowNames={handleRequestSlideshowNames}
                    onImagesUploaded={(images) =>
                        setAlbumData(prev => ({ ...prev, uploadedImages: images }))
                    }
                        columns={thumbnailColumns}
                        onThumbnailColumnsChange={handleThumbnailColumnsChange}
                        width={photoUploaderWidth}
                        setWidth={setPhotoUploaderWidth}
                        onImagesSaved={async (metadataPayload) => {

                        if (metadataPayload.length > 0) {
                            await saveMetadata(metadataPayload); // POST
                        }
                        await refreshMetadata(); // GET (optional but good)
                        }}
                />
                </div>

                <PageManagerPanel
                    show={showPageManager}
                    pages={pages}
                    selectedPageId={selectedPageId}
                    setSelectedPageId={setSelectedPageId}
                    setShowPageManager={setShowPageManager}
                    onApply={handleApplyPages}
                    thumbnails={thumbnails}
                    stageWidth={stageSize.width}
                    stageHeight={stageSize.height}
                />



            {/* Center (main working area) */}
            <div
                style={{
                    gridRow: "2 / 3",
                    gridColumn: "2 / 3",
                    background: "#fff",
                    padding: "0px",
                    overflow: "auto",

                }}
                >

                {/* Center Panel (PhotoUploader / Pages) */}
                <div
                    ref={containerRef}
                    style={{
                        flexGrow: 1,
                        height: "100%",
                        background: "#e7e7e7",
                        overflowY: "auto",
                        padding: 25,
                        boxSizing: "border-box",
                    }}              
                        onDragOver={(e) => {
                            e.preventDefault();           
                            e.dataTransfer.dropEffect = "copy";
                        }}
                    >


                    {pages.map((page, index) => (
                        <React.Fragment key={page.id}>
                            {/* Page Canvas Wrapper */}
                            <div
                                id={page.id}
                                onDragEnter={() => {
                                    if (selectedPageId !== page.id) setSelectedPageId(page.id);
                                }}
                                onDrop={onDrop}
                                onDragOver={(e) => {
                                    e.preventDefault();        // MUST for Safari
                                    e.dataTransfer.dropEffect = "copy";
                                }}
                                ref={(el) => {
                                    pageRefs.current[index] = el;
                                }}
                                style={{
                                    marginBottom: 45, // space for page controls + padding
                                    width: stageSize.width,
                                    marginLeft: `calc(50% - ${stageSize.width/ 2}px)`,
                                    cursor: "default",
                                }}
                            >

                                {/* Page container */}
                                {/* HEADER */}
                                <div
                                    style={{
                                        position: "relative",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        padding: "0 10px",
                                        marginBottom: 6,
                                        minHeight: 30,
                                        zIndex: 20,
                                    }}
                                >
                                    {/* LEFT: Label */}
                                    <div
                                        style={{
                                            fontSize: 16,
                                            fontWeight: "bold",
                                            color: "#7f7f7f",
                                        }}
                                    >
                                        {albumType === "ARCH"
                                            ? `Arch ${index + 1}`
                                            : albumType === "FMOUNTS"
                                                ? `Frame Mount ${index + 1}`
                                                : albumType === "CANVAS" || albumType === "FCANVAS"
                                                    ? `Canvas ${index + 1}`
                                                    : albumType === "HIPSTER"
                                                        ? `Hipster ${index + 1}`
                                                        : albumType === "HANDTORN"
                                                            ? `Hand Torn ${index + 1}`
                                                            : albumType === "FPRINTS"
                                                                ? `Framed Print ${index + 1}`
                                                                : albumType === "PRINTS"
                                                                    ? `Print ${index + 1}`
                                                                    : albumType === "MONTAGE"
                                                                        ? `Montage ${index + 1}`
                                                                        : albumType === "AC"
                                                                            ? `Cover ${index + 1}`
                                                                            : albumType === "ASC"
                                                                                ? `Slipcase ${index + 1}`
                                                                 : `Spread ${index + 1}`}
                                    </div>


                                    {/* CENTER: Reload Templates Button */}
                                    <div
                                        style={{
                                            position: "absolute",
                                            left: "50%",
                                            top: "50%",
                                            transform: "translate(-50%, -50%)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            zIndex: 21,
                                        }}
                                    >

                                        {supportsTemplateReload && page.slots?.length > 0 && (
                                            <Tooltip title="Load Similar Layouts" arrow>
                                                <span>
                                                    <button
                                                        onClick={reloadTemplatesForCurrentPage}
                                                        style={{
                                                            padding: 4,
                                                            borderRadius: 6,
                                                        //    transform: "translateX(-80%)",
                                                            border: "1px solid #e7e7e7",
                                                            cursor: "pointer",
                                                            background: "#e7e7e7",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <img
                                                            src={`${process.env.PUBLIC_URL}/template_icon.png`}
                                                            alt="Change Layout"
                                                            width={30}
                                                            height={30}
                                                        />
                                                    </button>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>


                                    {/* RIGHT: DROPDOWN + STATUS ICONS */}
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative",}}>

                                        {(albumType === "ARCH" || albumType === "FMOUNTS" || albumType === "FPRINTS" || albumType === "HANDTORN") && (
                                            <select
                                                value={albumData.opening ?? ""}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setSelectedOpening(value);
                                                    setAlbumData(prev => ({
                                                        ...prev,
                                                        opening: value
                                                    }));
                                                }}
                                            >
                                                {openings.map(o => (
                                                    <option key={o} value={o}>
                                                        {o}
                                                    </option>
                                                ))}
                                            </select>
                                        )}

                                        {/* Show if it's a HANDTORN album OR if the Square (SQ) template is selected */}
                                        {albumType === "HANDTORN" && isHandtornSquareSize && (
                                            <div className="form-row">
                                                <select
                                                    className="shape-select"
                                                    value={albumData.handtornMode ?? "square"}
                                                    onChange={(e) => {
                                                        const value = e.target.value as "square" | "circle";
                                                        setAlbumData(prev => ({
                                                            ...prev,
                                                            handtornMode: value
                                                        }));
                                                    }}
                                                >
                                                    <option value="square">Square image</option>
                                                    <option value="circle">Circle image</option>
                                                </select>
                                            </div>
                                        )}

                                        {/* APPROVED */}
                                        {pageReviews[page.id]?.status === "approved" && (
                                            <CheckCircleIcon
                                                sx={{
                                                    color: "#4caf50",
                                                    fontSize: 28,
                                                }}
                                            />
                                        )}

                                        {/* REJECTED */}                                     
                                        {pageReviews[page.id]?.status === "rejected" && (
                                            <>
                                                <WarningAmberIcon
                                                    onClick={(e) => openPopover(e, page.id)}
                                                    sx={{
                                                        position: "absolute",
                                                        top: -15,
                                                        right: 0,
                                                        fontSize: 28,
                                                        color: "#f44336",
                                                        zIndex: 100,
                                                        cursor: "pointer",
                                                    }}
                                                />

                                                <Popover
                                                    open={
                                                        Boolean(popoverAnchor) &&
                                                        popoverPageId === page.id
                                                    }
                                                    PaperProps={{
                                                        sx: {
                                                            backgroundColor: "#6f6f6f",
                                                            borderRadius: 0.8,   // less rounded
                                                            p: 1.2,              // more padding
                                                            transform: "translateX(-80px) !important",
                                                        }
                                                    }}
                                                    anchorEl={popoverAnchor}
                                                    anchorOrigin={{
                                                        vertical: "bottom",
                                                        horizontal: "center",
                                                    }}
                                                    transformOrigin={{
                                                        vertical: "top",
                                                        horizontal: "center",
                                                    }}
                                                   
                                                    onClose={closePopover}
                                                >
                                                    <Box sx={{ p: 0.5, width: 220 }}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                mb: 0.5,
                                                                letterSpacing: "0.5px",
                                                                fontFamily: "Arial, sans-serif",
                                                                color:"#6ed4f5"
                                                            }}
                                                        >
                                                            Client feedback:
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                color: "#fff",
                                                                fontStyle: "italic",
                                                            }}
                                                        >
                                                            "{pageReviews[page.id]?.comment}"
                                                        </Typography>

                                                        <textarea
                                                            className="photographer-note-textarea"
                                                            value={pageReviews[page.id]?.photographerNote || ""}
                                                            onChange={(e) => {
                                                                const value = e.target.value;

                                                                photographerNotesRef.current[page.id] = value;

                                                                setPageReviews(prev => ({
                                                                    ...prev,
                                                                    [page.id]: {
                                                                        ...prev[page.id],
                                                                        photographerNote: value
                                                                    }
                                                                }));
                                                            }}
                                                        
                                                        
                                                            placeholder="Write update..."
                                                            style={{
                                                                width: "210px",
                                                                height: "45px",
                                                                fontSize: "12px",
                                                                padding: "3px",
                                                            }}
                                                        />

                                                        <button
                                                            onClick={async (e) => {
                                                                e.stopPropagation();

                                                                const saved = await savePhotographerNote(
                                                                    page.id,
                                                                    index,
                                                                    photographerNotesRef.current[page.id] || ""
                                                                );

                                                                if (saved) {
                                                                    closePopover();
                                                                }
                                                            }}
                                                            style={{
                                                                marginTop: "6px",
                                                                backgroundColor: "#6eafd7",
                                                                color: "#fff",
                                                                border: "none",
                                                                borderRadius: "4px",
                                                                height: "24px",
                                                                padding: "0 12px",
                                                                fontSize: "12px",
                                                                cursor: "pointer",
                                                            }}
                                                        >
                                                            Save
                                                        </button>
                                                    </Box>
                                                </Popover>
                                            </>
                                        )}

                                          
                                        
                                    </div>
                                </div>
                                {/* Stage Container */}
                                <div
                                    onClick={() => setSelectedPageId(page.id)}
                                    // At the Stage or container
                                    onMouseDown={() => {
                                        // Only close panels that are NOT Effects
                                        if (openDropdown && openDropdown !== "effects") {
                                            setOpenDropdown(null);
                                        }
                                    }}
                                    style={{
                                        width: stageSize.width,
                                        height: stageSize.height,
                                        background: "#fff",
                                        padding: 0,
                                        position: "relative",
                                    }}
                                >
                                   
                                    <AlbumPageCanvas                                   
                                        ref={(node) => {
                                            stageRefs.current[page.id] = node;
                                        }}
                                        albumType={albumType}
                                        //   size={{ width: stageSize.width, height: stageSize.height }}
                                        page={page}
                                        editorMode={editorMode}
                                        selectedIds={selectedIds}
                                        onSelect={(slotId, e) => {
                                            setSelectedPageId(page.id);
                                            handleSelectSlot(slotId, page.id, e); //  pass the event here
                                            setSelectedElement({ type: "slot", id: slotId });
                                        }}
                                        onSlotChange={(slotId, newAttrs) => handleSlotChange(page.id, slotId, newAttrs)}
                                        onSlotDragMove={(pos) => pos}
                                        onUpdateGuides={(newGuides) => {
                                            setGuidesByPage(prev => ({
                                                ...prev,
                                                [page.id]: newGuides
                                            }));
                                        }}
                                        onDeselectAll={() => {
                                            setSelectedSlotIds([]);
                                            setSelectedTextId(null);
                                            setSelectedShapeId(null);
                                            setSelectedElement(null);
                                            setIsEditingText(false);
                                        }}
                                        guides={guidesByPage[page.id] ?? []} // only the current page
                                        stageWidth={stageSize.width}
                                        stageHeight={stageSize.height}
                                        backgroundColor={page.backgroundColor}
                                        backgroundImage={backgroundImagesRef.current[page.id] ?? null} // preloaded HTMLImageElement
                                        showBackground={showBackgrounds[page.id] ?? true}
                                        backgroundOpacity={backgroundOpacities[page.id] ?? 1}
                                        backgroundZoom={backgroundZooms[page.id] ?? 1}
                                        backgroundOffsetX={backgroundOffsetsX[page.id] ?? 0}
                                        backgroundOffsetY={backgroundOffsetsY[page.id] ?? 0}
                                        isSelectedPage={selectedPageId === page.id}
                                        // --- Text Props (Crucial for the fix) ---
                                        texts={page.texts}
                                        selectedTextId={selectedTextId}
                                        onSelectText={handleSelectText}
                                        onTextChange={handleTextChange}
                                        isEditingText={isEditingText}
                                        onSetEditingText={setIsEditingText}
                                        onDragText={handleTextDragMove}
                                        onTextDragEnd={handleTextDragEnd}
                                        //-----Shapes props here
                                        shapes={page.shapes || []}
                                        selectedShapeId={selectedShapeId}
                                        onSelectShape={(id) => handleSelectShape(page.id, id)}
                                        onShapeChange={(id, newAttrs) => handleShapeChange(page.id, id, newAttrs)}
                                        onSlotRightClick={handleSlotRightClick}
                                        showCenterLine={showCenterLine} //  must be true                                     
                                        updateSlot={updateSlot}
                                        showSlotNumbers={showSlotNumbers}
                                        selectedOpening={safeOpening}
                                        setSelectedOpening={setSelectedOpening}
                                        handtornMode={handtornMode}
                                        scale={scale}
                                        hoveredSlotId={hoveredSlotId}
                                        setHoveredSlotId={setHoveredSlotId}            
                                        onChange={onChange}
                                        // --- New props for wrap computation ---
                                       // e.g., "CANVAS"
                                        selectedSize={size}
                                        onSelectMultiple={(ids) => {
                                            // ensure the page context is active
                                            setSelectedPageId(page.id);

                                            // update slot selection
                                            setSelectedSlotIdsByPage(prev => ({
                                                ...prev,
                                                [page.id]: ids,
                                            }));

                                            // tell the app that a slot-type selection exists (so effects panel knows)
                                            if (ids.length > 0) {
                                                setSelectedElement({ type: "slot", id: ids[0] });
                                            }
                                        }}
                                    />

                                 
                    
                                 {/*right click menu*/}
                                    {contextMenu && (
                                        <ul
                                            style={{
                                                position: "fixed",
                                                top: contextMenu.y,
                                                left: contextMenu.x,
                                                background: "#fff",
                                                padding: 8,
                                                borderRadius: 6,
                                                boxShadow: "0 2px 4px rgba(0,0,0,0.08)", // softer shadow
                                                listStyle: "none",
                                                zIndex: 1000,
                                                minWidth: 150,
                                            }}
                                            onMouseLeave={() => setContextMenu(null)}
                                        >
                                            {/* Copy Effects */}
                                            <li
                                                onClick={canCopyEffects ? handleCopyEffects : undefined}
                                                title={!canCopyEffects ? "No effects to copy" : undefined}
                                                style={{
                                                    cursor: canCopyEffects ? "pointer" : "not-allowed",
                                                    opacity: canCopyEffects ? 1 : 0.4,
                                                    padding: "6px 12px",
                                                    borderRadius: 4,
                                                    transition: "background 0.2s",
                                                }}
                                                onMouseEnter={e => {
                                                    if (canCopyEffects) e.currentTarget.style.background = "#d0ebff";
                                                }}
                                                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                                            >
                                                Copy Effects
                                            </li>

                                            {/* Paste Effects */}
                                            <li
                                                onClick={canPasteEffects ? handlePasteEffects : undefined}
                                                title={
                                                    !copiedSlotEffects
                                                        ? "Δεν υπάρχουν αντιγραμμένα εφέ"
                                                        : !slotHasImage
                                                            ? "Το πλαίσιο δεν έχει εικόνα"
                                                            : undefined
                                                }
                                                style={{
                                                    cursor: canPasteEffects ? "pointer" : "not-allowed",
                                                    opacity: canPasteEffects ? 1 : 0.4,
                                                    padding: "6px 12px",
                                                    borderRadius: 4,
                                                    transition: "background 0.2s",
                                                }}
                                                onMouseEnter={e => {
                                                    if (canPasteEffects) e.currentTarget.style.background = "#d0ebff";
                                                }}
                                                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                                            >
                                                Paste Effects
                                            </li>

                                            {/* --- CATEGORY SEPARATOR --- */}
                                            <li
                                                style={{
                                                    height: 1,
                                                    margin: "8px 6px",
                                                    background: "#e0e0e0",
                                                    pointerEvents: "none",
                                                }}
                                            />

                                            <li
                                                onClick={slotHasImage ? handleClearSlotImage : undefined}
                                                style={{
                                                    cursor: slotHasImage ? "pointer" : "not-allowed",
                                                    opacity: slotHasImage ? 1 : 0.4,
                                                    padding: "6px 12px",
                                                    borderRadius: 4,
                                                    transition: "background 0.2s",
                                                    color: "#d32f2f",
                                                }}
                                                onMouseEnter={e => {
                                                    if (slotHasImage) e.currentTarget.style.background = "#ffe3e3";
                                                }}
                                                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                                            >
                                                Clear Slot Image
                                            </li>

                                            {/* Fit Slot to Image */}
                                            <li
                                                onClick={canUseFitSlotToImage ? handleFitSlotToImage : undefined}
                                                title={
                                                    !canFitSlotToImage
                                                        ? "Slot must contain an image"
                                                        : !canUseFitSlotToImage
                                                            ? "Not available for this album type"
                                                            : "Resize slot to match image ratio"
                                                }
                                                style={{
                                                    cursor: canUseFitSlotToImage ? "pointer" : "not-allowed",
                                                    opacity: canUseFitSlotToImage ? 1 : 0.4,
                                                    padding: "6px 12px",
                                                    borderRadius: 4,
                                                    transition: "background 0.2s",
                                                }}
                                                onMouseEnter={e => {
                                                    if (canUseFitSlotToImage) {
                                                        e.currentTarget.style.background = "#d0ebff";
                                                    }
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.background = "transparent";
                                                }}
                                            >
                                                Fit Slot to Image
                                            </li>

                                        </ul>

                                    )}

                                     {/*Cut Area — only for non-cover albums */}
                                    {supportsCut && selectedPageId === page.id && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: cutMarginPx,
                                                left: cutMarginPx,
                                                width: cutAreaWidthPx - 4,
                                                height: cutAreaHeightPx - 4,
                                                border: "2px solid rgba(255, 0, 0, 0.5)",
                                                pointerEvents: "none",
                                                zIndex: 1,
                                            }}
                                        />
                                    )}
                     
                               
                                    {/* COVER ONLY */}
                                    {albumType === "AC" && (() => {
                                        // --- Convert physical width to cm ---
                                        let physicalWidthCm = size?.width || 1;

                                        if (size?.unit === "inches") {
                                            physicalWidthCm = physicalWidthCm * 2.54;
                                        }

                                        // pixels per cm
                                        const pxPerCm = (cutAreaWidthPx || 1) / physicalWidthCm;

                                        // --- constants (cover only) ---
                                        const foldCm = 3.0;
                                        const gapCm = 1.8;

                                        const foldPx = foldCm * pxPerCm;
                                        const gutterPx = gapCm * pxPerCm;

                                        //  SPINE: direct from Mongo (NO adjustments)
                                        const spineMm = spineWidth ?? 0;
                                        const spineCm = spineMm / 10;
                                        const spineWidthPx = spineCm * pxPerCm;

                                        // center
                                        const centerX = cutMarginPx + cutAreaWidthPx / 2;
                                        const spineLeftX = centerX - spineWidthPx / 2;
                                        const spineRightX = centerX + spineWidthPx / 2;

                                        const backCenterX =
                                            cutMarginPx + (spineLeftX - cutMarginPx) / 2;

                                        const frontCenterX =
                                            spineRightX + (cutMarginPx + cutAreaWidthPx - spineRightX) / 2;


                                        //  Overlay elements 
                                        return (
                                            <>
                                                {/* Top Fold */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        width: "100%",
                                                        height: `${foldPx}px`,
                                                        backgroundColor: "rgba(255, 0, 0, 0.25)",
                                                        borderBottom: "1px solid rgba(255,0,0,0.4)",
                                                        pointerEvents: "none",
                                                        zIndex: 1,
                                                    }}
                                                />

                                                {/* Bottom Fold */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        bottom: 0,
                                                        left: 0,
                                                        width: "100%",
                                                        height: `${foldPx}px`,
                                                        backgroundColor: "rgba(255, 0, 0, 0.25)",
                                                        borderTop: "1px solid rgba(255,0,0,0.4)",
                                                        pointerEvents: "none",
                                                        zIndex: 1,
                                                    }}
                                                />

                                                {/* Left fold */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        width: `${foldPx}px`,
                                                        height: "100%",
                                                        backgroundColor: "rgba(255, 0, 0, 0.25)",
                                                        borderRight: "1px solid rgba(255,0,0,0.5)",
                                                        pointerEvents: "none",
                                                        zIndex: 1,
                                                    }}
                                                />

                                                {/* Right fold */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        right: 0,
                                                        width: `${foldPx}px`,
                                                        height: "100%",
                                                        backgroundColor: "rgba(255, 0, 0, 0.25)",
                                                        borderLeft: "1px solid rgba(255,0,0,0.5)",
                                                        pointerEvents: "none",
                                                        zIndex: 1,
                                                    }}
                                                />

                                                {/* Spine area fill */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: `${spineLeftX}px`,
                                                        width: `${spineWidthPx}px`,
                                                        height: "100%",
                                                        backgroundColor: "rgba(154,154,154,0.30)",
                                                        pointerEvents: "none",
                                                        zIndex: 1,
                                                    }}
                                                />

                                                {/* Spine left gutter */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: `${spineLeftX - gutterPx}px`,
                                                        width: `${gutterPx}px`,
                                                        height: "100%",
                                                        backgroundColor: "rgba(154,154,154,0.30)",
                                                        opacity: 0.6,
                                                        pointerEvents: "none",
                                                        zIndex: 2,
                                                    }}
                                                />
                                                {/* Spine right gutter */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: `${spineRightX}px`,
                                                        width: `${gutterPx}px`,
                                                        height: "100%",
                                                        backgroundColor: "rgba(154,154,154,0.30)",
                                                        opacity: 0.6,
                                                        pointerEvents: "none",
                                                        zIndex: 2,
                                                    }}
                                                />
                                              
                                                {/* Back Cover Label */}
                                                {page.slots.every(slot => !slot.originalSrc) && (
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            left: `${backCenterX}px`,
                                                            top: "50%",
                                                            transform: "translate(-50%, -50%)",
                                                            color: "#555",
                                                            fontSize: "22px",
                                                            fontWeight: 600,
                                                            pointerEvents: "none",
                                                            zIndex: 10,
                                                        }}
                                                    >
                                                        Back
                                                    </div>
                                                )}

                                                {/* Front Cover Label */}
                                                {page.slots.every(slot => !slot.originalSrc) && (
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            left: `${frontCenterX}px`,
                                                            top: "50%",
                                                            transform: "translate(-50%, -50%)",
                                                            color: "#555",
                                                            fontSize: "22px",
                                                            fontWeight: 600,
                                                            pointerEvents: "none",
                                                            zIndex: 10,
                                                        }}
                                                    >
                                                        Front
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                    {/* SLIPCASE ONLY */}
                                    {albumType === "ASC" && (() => {
                                       const physicalWidthCm = size.unit === "inches"
                                            ? size.width * 2.54
                                            : size.width;

                                        const pxPerCm = cutAreaWidthPx / physicalWidthCm;

                                        const foldPx = 3.0 * pxPerCm;

                                        const spinePx = ((spineWidth ?? 0) / 10) * pxPerCm;
                                        const innerWidthPx = cutAreaWidthPx - 2 * foldPx;
                                        const cartonWidthPx = (innerWidthPx - spinePx) / 2;

                                        const backCenterX = foldPx + cartonWidthPx / 2;
                                        const frontCenterX = foldPx + cartonWidthPx + spinePx + cartonWidthPx / 2;

                                        return (
                                            <div>

                                                {/* Top Fold */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        width: "100%",
                                                        height: `${foldPx}px`,
                                                        backgroundColor: "rgba(255, 0, 0, 0.25)",
                                                        borderBottom: "1px solid rgba(255,0,0,0.4)",
                                                        pointerEvents: "none",
                                                        zIndex: 1,
                                                    }}
                                                />

                                                {/* Bottom Fold */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        bottom: 0,
                                                        left: 0,
                                                        width: "100%",
                                                        height: `${foldPx}px`,
                                                        backgroundColor: "rgba(255, 0, 0, 0.25)",
                                                        borderTop: "1px solid rgba(255,0,0,0.4)",
                                                        pointerEvents: "none",
                                                        zIndex: 1,
                                                    }}
                                                />

                                                {/* Left fold */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        width: `${foldPx}px`,
                                                        height: "100%",
                                                        backgroundColor: "rgba(255, 0, 0, 0.25)",
                                                        borderRight: "1px solid rgba(255,0,0,0.5)",
                                                        pointerEvents: "none",
                                                        zIndex: 1,
                                                    }}
                                                />

                                                {/* RIGHT FOLD */}
                                                <div style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    right: 0,
                                                    width: foldPx,
                                                    height: "100%",
                                                    background: "rgba(255,0,0,0.25)",
                                                     pointerEvents: "none",
                                                    zIndex: 1,
                                                }} />

                                                {/* LEFT CARTON */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: foldPx,
                                                        left: foldPx,
                                                        width: cartonWidthPx,
                                                        height: `calc(100% - ${2 * foldPx}px)`,
                                                        background: "rgba(0,0,0,0.03)", // very subtle OR "transparent"
                                                        pointerEvents: "none",
                                                        zIndex: 1,
                                                    }}
                                                />
                                                {/* BACK LABEL */}
                                                {page.slots.every(slot => !slot.originalSrc) && (
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            left: `${backCenterX}px`,
                                                            top: "50%",
                                                            transform: "translate(-50%, -50%)",
                                                            color: "#555",
                                                            fontSize: "22px",
                                                            fontWeight: 600,
                                                            pointerEvents: "none",
                                                            zIndex: 10,
                                                        }}
                                                    >
                                                        Front
                                                    </div>
                                                )}

                                                {/* FRONT LABEL */}
                                                {page.slots.every(slot => !slot.originalSrc) && (
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            left: `${frontCenterX}px`,
                                                            top: "50%",
                                                            transform: "translate(-50%, -50%)",
                                                            color: "#555",
                                                            fontSize: "22px",
                                                            fontWeight: 600,
                                                            pointerEvents: "none",
                                                            zIndex: 10,
                                                        }}
                                                    >
                                                        Back
                                                    </div>
                                                )}

                                                {/* SPINE */}
                                                <div style={{
                                                    position: "absolute",
                                                    top: foldPx,
                                                    left: foldPx + cartonWidthPx,
                                                    width: spinePx,
                                                    height: `calc(100% - ${2 * foldPx}px)`,
                                                    backgroundColor: "rgba(154,154,154,0.30)",
                                                    pointerEvents: "none",
                                                    zIndex: 1,
                                                }} />

                                                {/* RIGHT CARTON */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: foldPx,
                                                        left: foldPx + cartonWidthPx + spinePx,
                                                        width: cartonWidthPx,
                                                        height: `calc(100% - ${2 * foldPx}px)`,
                                                        background: "rgba(0,0,0,0.03)",
                                                        pointerEvents: "none",
                                                        zIndex: 1,// same subtle tone
                                                    }}
                                                />
                                            </div>
                                        );
                                    })()}
                                </div>

                                  
                               
                                    
                                {/* Page Controls */}
                                <div
                                    className="page-controls"
                                    style={{
                                        marginTop: 4,
                                        display: "flex",
                                        justifyContent: "space-between", // push left/right
                                        alignItems: "center",
                                        width: "100%", // make full width of page container
                                    }}
                                >
                                    <Stack direction="row" spacing={0.3} alignItems="center">
                                        <Tooltip title="Add Spread" arrow>
                                            <IconButton
                                                onClick={() => handleAddPageAtIndex(index)}
                                                size="small"
                                                sx={{ padding: "4px" }}
                                                disabled={isAlbumType}
                                            >
                                                <img src={`${process.env.PUBLIC_URL}/addPageIcon.png`} alt="Add"
                                                    style={{ width: 22, height: 22, opacity: isAlbumType ? 0.5 : 1, }} />
                                            </IconButton>
                                        </Tooltip>


                                        <Tooltip title="Duplicate Spread" arrow>
                                            <IconButton
                                                onClick={() => handleDuplicatePageAtIndex(index)}
                                                size="small"
                                                sx={{ padding: "4px" }}
                                               // disabled={isAlbumType}
                                            >
                                                <img src={`${process.env.PUBLIC_URL}/duplicatePageIcon.png`} alt="Duplicate"
                                                    style={{ width: 22, height: 22, opacity: 1, }} />
                                            </IconButton>
                                        </Tooltip>


                                        <Tooltip title="Remove Spread" arrow>
                                            <IconButton
                                                onClick={() => handleRemovePageAtIndex(index)}
                                                size="small"
                                                sx={{ padding: "4px" }}
                                                disabled={pages.length === 1}
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/removePageIcon.png`}
                                                    alt="Remove"
                                                    style={{ width: 22, height: 22, opacity: pages.length === 1 ? 0.5 : 1 }}
                                                />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Move Spread Up" arrow>
                                            <IconButton
                                                onClick={() => movePageUp(index)}
                                                size="small"
                                                sx={{ padding: "4px" }}
                                                disabled={isAlbumType || index === 0}
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/moveUpIcon.png`}
                                                    alt="Move Up"
                                                    style={{ width: 23, height: 23, opacity: index === 0 ? 0.5 : 1 }}
                                                />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Move Spread Down" arrow>
                                            <IconButton
                                                onClick={() => movePageDown(index)}
                                                size="small"
                                                sx={{ padding: "4px" }}
                                                disabled={isAlbumType || index === pages.length - 1}
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/moveDownIcon.png`}
                                                    alt="Move Down"
                                                    style={{ width: 23, height: 23, opacity: index === pages.length - 1 ? 0.5 : 1 }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>

                                    {/* Right buttons */}
                                    <Stack direction="row" spacing={0.3} alignItems="center">
                                        <Tooltip title="Flip Spread Horizontally" arrow>
                                            <IconButton onClick={() => flipPage(page.id, "horizontal")}
                                                size="small"
                                                sx={{ padding: "4px" }}
                                                disabled={disableFlip}
                                            >
                                                <img src={`${process.env.PUBLIC_URL}/flip_page_hor_icon.png`} alt="Flip H" style={{ width: 22, height: 22 }} />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Flip Spread Vertically" arrow>
                                            <IconButton onClick={() => flipPage(page.id, "vertical")}
                                                size="small"
                                                sx={{ padding: "4px" }}
                                                disabled={disableFlip}
                                            >
                                                <img src={`${process.env.PUBLIC_URL}/flip_page_vert_icon.png`} alt="Flip V" style={{ width: 22, height: 22 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </div>

                                {/* Page Mode Button (centered) */}
                                {/* Global Page Mode Button (shown under every page) */}
                                <div
                                    style={{
                                        marginTop: -30,
                                        textAlign: "center",
                                    }}
                                >
                                    <Tooltip
                                        title={
                                            editorMode === "manual"
                                                ? "Switch to Automatic Layout"
                                                : "Switch to Free Edit"
                                        }
                                    >
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={toggleEditorMode}
                                        disabled={disabledProducts.has(albumType)}
                                        sx={{
                                                minWidth: 150,
                                                height: 36,
                                                px: 2.5,   // left and right padding
                                                borderRadius: "4px",
                                                fontSize: 14,
                                                textTransform: "none",
                                                color: editorMode === "manual" ? "#1976d2" : "inherit",
                                                borderColor: editorMode === "manual" ? "#1976d2" : undefined,
                                                backgroundColor:
                                                    editorMode === "manual"
                                                        ? "rgba(25,118,210,0.10)"
                                                        : "transparent",
                                            }}
                                        >
                                            {editorMode === "manual" ? "Free Edit" : "Automatic Layout"}
                                        </Button>
                                    </Tooltip>
                                </div>


                            </div>                
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {saveStatus !== "idle" && (
                <div
                    style={{
                        position: "fixed",
                        bottom: "2px",
                        right: "65px",
                        background: saveStatus === "saving" ? "#f0ad4e" : "#4caf50",
                        color: "white",
                        padding: "6px 12px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                        zIndex: 1000,
                        transition: "opacity 0.3s ease",
                    }}
                >
                    {saveStatus === "saving" ? "Saving..." : "Saved "}
                </div>
            )}

                {showOrderModal && (
                    <div style={overlayStyle}>
                        <div
                            style={{
                                ...modalStyle,
                                width: 250,
                                maxWidth: "90vw",
                            }}
                        >
                            <h3>Order Information</h3>

                            <p>
                                Please enter your Seldex order number so we can match your files to your order.
                            </p>

                            <input
                                type="text"
                                placeholder="Order Number (S12345)"
                                value={exportOrderId}
                                onChange={(e) => {
                                    let val = e.target.value.toUpperCase();
                                    val = val.replace(/[^A-Z0-9]/g, "");

                                    if (!val.startsWith("S")) {
                                        val = "S" + val.replace(/\D/g, "");
                                    }

                                    val = "S" + val.slice(1).replace(/\D/g, "").slice(0, 5);

                                    setExportOrderId(val);
                                    setOrderError("");
                                }}
                                className="form-input-small"
                                style={{ width: "100%", marginTop: 10, padding: 4 }}
                            />

                            {orderError && (
                                <div style={{ color: "red", marginTop: 5 }}>
                                    {orderError}
                                </div>
                            )}

                            <input
                                type="text"
                                placeholder="Studio Reference (optional)"
                                value={exportOrderRef}
                                onChange={(e) => setExportOrderRef(e.target.value)}
                                className="form-input-small"
                                style={{ width: "100%", marginTop: 10, padding: 4 }}
                            />

                            <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => setShowOrderModal(false)}
                                    sx={{
                                        height: 34,
                                        minHeight: 34,
                                        padding: "4px 12px",
                                        fontSize: 13,
                                        textTransform: "none"
                                    }}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    disabled={!/^S\d{5}$/.test(exportOrderId)}
                                    onClick={async () => {
                                        setShowOrderModal(false);
                                        await startPrintExport(exportOrderId, exportOrderRef);
                                    }}
                                    sx={{
                                        height: 34,
                                        minHeight: 34,
                                        padding: "4px 12px",
                                        fontSize: 13,
                                        textTransform: "none"
                                    }}
                                >
                                    Continue
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/*web proof dialog*/}
                {showWebProofModal && (
                    <div style={overlayStyle}>
                        <div
                            style={{
                                ...modalStyle,
                                width: 600,
                                maxWidth: "90vw",
                            }}
                        >
                            <h3
                                style={{
                                    marginTop: 0,
                                    marginBottom: 20,
                                    color: "#555",
                                    fontSize: "22px",
                                    fontWeight: 600,
                                    letterSpacing: "0.5px",
                                }}
                            >
                                Album Clients
                            </h3>

                            {/* Groom */}
                            <div
                                style={{
                                    display: "flex",
                                    gap: 10,
                                    width: "100%",
                                }}
                            >
                                <input
                                    placeholder="Groom name"
                                    value={clientInfo.groomName}
                                    onChange={(e) =>
                                        setClientInfo({
                                            ...clientInfo,
                                            groomName: e.target.value,
                                        })
                                    }
                                    className="form-input-small"
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                />

                                <input
                                    placeholder="Groom email"
                                    type="email"
                                    value={clientInfo.groomEmail}
                                    onChange={(e) =>
                                        setClientInfo({
                                            ...clientInfo,
                                            groomEmail: e.target.value,
                                        })
                                    }
                                    className="form-input-small"
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                />
                            </div>


                            {/* Bride */}
                            <div
                                style={{
                                    display: "flex",
                                    gap: 10,
                                    marginTop: 10,
                                    width: "100%",
                                }}
                            >
                                <input
                                    placeholder="Bride name"
                                    value={clientInfo.brideName}
                                    onChange={(e) =>
                                        setClientInfo({
                                            ...clientInfo,
                                            brideName: e.target.value,
                                        })
                                    }
                                    className="form-input-small"
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                />

                                <input
                                    placeholder="Bride email"
                                    type="email"
                                    value={clientInfo.brideEmail}
                                    onChange={(e) =>
                                        setClientInfo({
                                            ...clientInfo,
                                            brideEmail: e.target.value,
                                        })
                                    }
                                    className="form-input-small"
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                />
                            </div>


                            <label style={{ display: "block", marginTop: 15 }}>
                                <input
                                    type="checkbox"
                                    checked={clientInfo.sendInvitation}
                                    onChange={(e) =>
                                        setClientInfo({
                                            ...clientInfo,
                                            sendInvitation: e.target.checked,
                                        })
                                    }
                                />
                                {" "}Send email invitation upon publish
                            </label>


                            <label style={{ display: "block", marginTop: 8 }}>
                                <input
                                    type="checkbox"
                                    checked={clientInfo.sendCopy}
                                    onChange={(e) =>
                                        setClientInfo({
                                            ...clientInfo,
                                            sendCopy: e.target.checked,
                                        })
                                    }
                                />
                                {" "}Send me a copy
                            </label>


                            <div
                                style={{
                                    marginTop: 20,
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: 10,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={() => setShowWebProofModal(false)}
                                    sx={{
                                        height: 34,
                                        minWidth: 120,
                                        fontSize: 13,
                                        textTransform: "none",
                                    }}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    variant="contained"
                                    disabled={!hasValidClient}
                                    onClick={() => {
                                        setShowWebProofModal(false);
                                        handleExportWebProof(clientInfo);
                                    }}
                                    sx={{
                                        height: 34,
                                        minWidth: 120,
                                        fontSize: 13,
                                        textTransform: "none",
                                    }}
                                >
                                    Create Proof
                                </Button>
                            </div>

                        </div>
                    </div>
                )}


                {/* Pages exporting */}
                {exporting && !creatingVideo && (
                    <div style={overlayStyle}>
                        <div style={modalStyle}>
                            {/* Show progress and message only while exporting */}
                            {exportProgress < 100 && (
                                <>
                                    <span style={modalTextStyle}>
                                        {exportMode === "print"
                                            ? "Please wait, the spreads are being sent to the lab"
                                            : exportMode === "slideshow"
                                                ? "Please wait, the pages for the slideshow are exported"
                                                : exportMode === "web-proof"
                                                    ? "Please wait, the Online proof is being created"
                                                    : "Please wait, the PDF proof file is being created"}
                                    </span>

                        {/* Lab logo */}
                                    {exportMode === "print" && (
                                        <img
                                            src={`${process.env.PUBLIC_URL}/client_export.png`}
                                            alt="Lab Logo"
                                            style={{ width: 140, marginBottom: 12 }}
                                        />
                                    )}

                                    <LinearProgress
                                        variant="determinate"
                                        value={exportProgress}
                                        style={{ width: "100%", height: 10, borderRadius: 5 }}
                                    />
                                    <span style={{ marginTop: 12, fontSize: 16, color: "#333" }}>
                                        Spread {Math.round((exportProgress / 100) * pages.length) || 0} / {pages.length}
                                    </span>
                                </>
                            )}

                            {/* Show finished-state button when done */}
                            {exportProgress >= 100 && webProofUrl && (
                                <div style={{ textAlign: "center", marginTop: 16 }}>
                                    <div style={{ fontWeight: 500, color: "#5f6368" }}>
                                        The preview is ready.
                                    </div>

                                    <button
                                        style={{
                                            marginTop: 16,
                                            padding: "6px 12px",
                                            fontSize: 14,
                                            background: "transparent",
                                            border: "1px solid #1976d2",
                                            color: "#1976d2",
                                            borderRadius: 6,
                                            cursor: "pointer",
                                        }}
                                        onClick={() => {
                                            window.open(webProofUrl, "_blank");
                                            setWebProofUrl(null);
                                            setExporting(false);
                                        }}
                                    >
                                        Open Preview
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>


                )}



                {/* Video creating */}
                {creatingVideo && (
                    <div style={overlayStyle}>
                        <div style={modalStyle}>
                            <span style={{ marginBottom: 16, fontSize: 16 }}>
                                Please wait… the slideshow is being created
                            </span>

                            <LinearProgress
                                variant="determinate"
                                value={videoProgress}
                                style={{ width: "100%", height: 10, borderRadius: 5 }}
                            />

                            <span style={{ marginTop: 12, fontSize: 14, color: "#555" }}>
                                {videoProgress}% completed
                            </span>
                        </div>
                    </div>
            )}

            {/* Bottom Bar (full width now) */}
                <div
                    style={{
                        gridRow: "3 / 4",
                        gridColumn: "1 / 4",
                        background: "#fff",
                        borderTop: "1px solid #b1b1b1",
                        padding: "6px",
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                        paddingLeft: "175px",
                        height: "21px",
                        fontSize: "14px",
                    }}
                >

                    <div>{albumName}</div>
                    <div>{albumType}</div>
                    <div>
                        {albumType === "CANVAS" || albumType === "FCANVAS"
                            ? `${formatDisplaySize(size.width - CANVAS_WRAP)} × ${formatDisplaySize(size.height - CANVAS_WRAP)} ${unit}`
                            : `${formatDisplaySize(size.width)} x ${formatDisplaySize(size.height)} ${unit}`}
                    </div>

                    {albumType !== "CANVAS" && albumType !== "MONTAGE" && albumType !== "AC" && albumType !== "ACC" &&  albumType !== "FCANVAS" &&(
                        <div>
                            {albumTypesNo.has(albumType)
                                ? `Spreads: ${pages.length}`
                                : `Items: ${pages.length}`}
                        </div>
                    )}
                    {/*<div>*/}
                    {/*    Stage Size: {stageSize.width} X {stageSize.height} px*/}
                    {/*</div>*/}


                    {/* SLOT INFO */}
                    {selectedSlot && !disableSlotOptions.has(albumType) && (
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            marginLeft: "25px"                          
                        }}>

                            <span style={{ fontWeight: 500 }}>Slot:</span>

                            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                <span>X:</span>
                                <input
                                    className="inline-input"
                                    type="number"
                                    value={Math.round(selectedSlot.x)}
                                    onChange={(e) => updateSlot(selectedSlot.id, { x: parseFloat(e.target.value) })}
                                />
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                <span>Y:</span>
                                <input
                                    className="inline-input"
                                    type="number"
                                    value={Math.round(selectedSlot.y)}
                                    onChange={(e) => updateSlot(selectedSlot.id, { y: parseFloat(e.target.value) })}
                                />
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                <span>W:</span>
                                <input
                                    className="inline-input"
                                    type="number"
                                    value={Math.round(selectedSlot.width)}
                                    onChange={(e) => updateSlot(selectedSlot.id, { width: parseFloat(e.target.value) })}
                                />
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                <span>H:</span>
                                <input
                                    className="inline-input"
                                    type="number"
                                    value={Math.round(selectedSlot.height)}
                                    onChange={(e) => updateSlot(selectedSlot.id, { height: parseFloat(e.target.value) })}
                                />
                            </div>

                            <span style={{ color: "#667" }}>px</span>

                        </div>
                    )}

                {/*  <button*/}
                {/*    style={{*/}
                {/*        marginLeft: "10px",*/}
                {/*        padding: "4px 10px",*/}
                {/*        border: "1px solid #ccc",*/}
                {/*        borderRadius: "4px",*/}
                {/*        background: "#f8f8f8",*/}
                {/*        cursor: "pointer",*/}
                {/*    }}*/}
                {/*    onClick={() => {*/}
                {/*        const page = pages.find(p => p.id === selectedPageId);*/}
                {/*        if (!page) return;*/}

                {/*        const slotCode = page.slots*/}
                {/*            .map((slot, i) => {*/}
                {/*                return `  { id: "${i + 1}", x: ${Math.round(slot.x)}, y: ${Math.round(slot.y)}, width: ${Math.round(slot.width)}, height: ${Math.round(slot.height)}, rotation: ${Math.round(slot.rotation || 0)}, imageRotation: ${Math.round(slot.imageRotation || 0)} }`;*/}
                {/*            })*/}
                {/*            .join(",\n");*/}

                {/*        const fullCode = `slots: [\n${slotCode}\n],`;*/}

                {/*        navigator.clipboard.writeText(fullCode);*/}
                {/*        alert(" Slot code copied to clipboard!");*/}
                {/*    }}*/}
                {/*>*/}
                {/* Copy Slots Code*/}
                    {/*</button>*/}
                    {selectedSlot && (
                        <div
                            style={{
                                marginLeft: "20px",
                                borderLeft: "1px solid #ccc",
                                paddingLeft: "10px",
                                width: "320px",
                                display: "flex",
                                alignItems: "center",
                                overflow: "hidden",
                            }}
                        >
                            <span
                                style={{
                                    display: "block",
                                    width: "100%",
                                    fontSize: 13,
                                    color: "#444",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {selectedSlot.originalSrc?.split("/").pop()}
                            </span>
                        </div>
                    )}
                  

                 {/**/}
                 {/*   </button>*/}
                    {/*<button*/}
                    {/*    style={{*/}
                    {/*        marginLeft: "10px",*/}
                    {/*        padding: "4px 10px",*/}
                    {/*        border: "1px solid #ccc",*/}
                    {/*        borderRadius: "4px",*/}
                    {/*        background: "#f8f8f8",*/}
                    {/*        cursor: "pointer",*/}
                    {/*    }}*/}
                    {/*    onClick={() => {*/}
                    {/*        const page = pages.find(p => p.id === selectedPageId);*/}
                    {/*        if (!page) return;*/}

                    {/*        // --- Copy image slots ---*/}
                    {/*        const slotCode = page.slots*/}
                    {/*            .map((slot, i) => {*/}
                    {/*                return `  { id: "${i + 1}", x: ${Math.round(slot.x)}, y: ${Math.round(slot.y)}, width: ${Math.round(slot.width)}, height: ${Math.round(slot.height)}, rotation: ${Math.round(slot.rotation || 0)}, imageRotation: ${Math.round(slot.imageRotation || 0)} }`;*/}
                    {/*            })*/}
                    {/*            .join(",\n");*/}

                    {/*        // --- Copy text elements ---*/}
                    {/*        const textSlotCode = page.texts?.map((textEl, i) => {*/}
                    {/*            return `  { id: "${textEl.id}", text: "${textEl.text.replace(/\n/g, "\\n")}", x: ${Math.round(textEl.x)}, y: ${Math.round(textEl.y)}, width: ${Math.round(textEl.width)}, fontSize: ${textEl.fontSize}, fontFamily: "${textEl.fontFamily}", fontWeight: "${textEl.fontWeight}", align: "${textEl.align}", rotation: ${textEl.rotation || 0}, fill: "${textEl.fill}", opacity: ${textEl.opacity ?? 1}, strokeWidth: ${textEl.strokeWidth ?? 0}, shadowColor: "${textEl.shadowColor ?? '#000'}" }`;*/}
                    {/*        }).join(",\n");*/}

                    {/*        const fullCode = `slots: [\n${slotCode}\n],\ntexts: [\n${textSlotCode}\n],`;*/}

                    {/*        navigator.clipboard.writeText(fullCode);*/}
                    {/*        alert("Slots + Text code copied to clipboard!");*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    Copy Slots + Text Code*/}
                    {/*</button>*/}

                    {/* DIVIDE BUTTON */}
                    {selectedSlot && !disableSlotOptions.has(albumType) && (
                        <button
                            ref={cutMergeButtonRef}
                            onClick={() => {
                                if (cutMergeButtonRef.current) {
                                    const rect =
                                        cutMergeButtonRef.current.getBoundingClientRect();

                                    setCutMergePos({
                                        left: rect.left,
                                        bottom: window.innerHeight - rect.top + 8,
                                    });
                                }

                                setIsCutMergeOpen(true);
                            }}
                        disabled={!selectedSlot || isCutMergeOpen}
                            style={{
                                position: "absolute",
                                right: photoUploaderWidth + 200,
                                padding: "4px 8px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                background: "#f8f8f8",
                                cursor: "pointer",
                            }}
                        >
                            Divide - Merge
                        </button>
                    )}
                    {/* DIVIDE POPUP */}
                    {isCutMergeOpen && (
                        <div
                            style={{
                                position: "fixed",
                                left: `${cutMergePos.left - 62}px`,
                                bottom: `${cutMergePos.bottom - 2}px`,
                                backgroundColor: "#fff",
                                border: "1px solid #ccc",
                                borderRadius: "6px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                padding: "10px 12px",
                                zIndex: 1000,
                                minWidth: "140px",
                            }}
                        >
                                    {/* Header */}
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "8px",
                                        }}
                                    >
                                        <span style={{ fontSize: 14, fontWeight: 500 }}>
                                            Divide - Merge
                                        </span>

                                        <button
                                            onClick={() => setIsCutMergeOpen(false)}
                                            style={{
                                                border: "none",
                                                background: "transparent",
                                                cursor: "pointer",
                                                fontSize: "20px",
                                                lineHeight: "20px",
                                                color: "#666",
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>

                                    {/* Divide */}
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <span style={{ fontSize: 13 }}>
                                            Divide:
                                        </span>

                                        <Tooltip title="Divide Vertically" arrow>
                                            <Button
                                                variant="outlined"
                                                onClick={() =>
                                                    handleDivideSelectedSlot(
                                                        "vertical",
                                                        divideParts,
                                                        divideGap
                                                    )
                                                }
                                                sx={{
                                                    minWidth: 0,
                                                    width: 28,
                                                    height: 26,
                                                    borderColor: "#ccc",
                                                    backgroundColor: "#f8f8f8",
                                                }}
                                            >
                                                ↕
                                            </Button>
                                        </Tooltip>

                                        <Tooltip title="Divide Horizontally" arrow>
                                            <Button
                                                variant="outlined"
                                                onClick={() =>
                                                    handleDivideSelectedSlot(
                                                        "horizontal",
                                                        divideParts,
                                                        divideGap
                                                    )
                                                }
                                                sx={{
                                                    minWidth: 0,
                                                    width: 28,
                                                    height: 26,
                                                    borderColor: "#ccc",
                                                    backgroundColor: "#f8f8f8",
                                                }}
                                            >
                                                ↔
                                            </Button>
                                        </Tooltip>
                                    </div>

                                    {/* Gap */}
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            marginTop: "10px",
                                        }}
                                    >
                                        <label style={{ fontSize: 13 }}>
                                            Gap:
                                        </label>

                                        <input
                                            type="number"
                                            min={4}
                                            max={50}
                                            value={divideGap}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);

                                                setDivideGap(
                                                    !isNaN(value) && value >= 4
                                                        ? value
                                                        : 4
                                                );
                                            }}
                                            style={{
                                                width: 35,
                                                height: 20,
                                                textAlign: "center",
                                                border: "1px solid #ccc",
                                                borderRadius: 4,
                                            }}
                                        />
                                    </div>

                                    {/* Pieces */}
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            marginTop: "8px",
                                        }}
                                    >
                                        <label style={{ fontSize: 13 }}>
                                            Pieces:
                                        </label>

                                        <select
                                            value={divideParts}
                                            onChange={(e) =>
                                                setDivideParts(
                                                    parseInt(e.target.value)
                                                )
                                            }
                                            style={{
                                                padding: "2px 4px",
                                                border: "1px solid #ccc",
                                                borderRadius: 4,
                                                fontSize: 13,
                                            }}
                                        >
                                            {[2, 3, 4, 5, 6, 7].map((n) => (
                                                <option key={n} value={n}>
                                                    {n}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Merge */}
                                    <button
                                        onClick={handleMergeSelectedSlots}
                                        style={{
                                            marginTop: "12px",
                                            width: "100%",
                                            padding: "5px",
                                            border: "1px solid #ccc",
                                            borderRadius: 4,
                                            background: "#f8f8f8",
                                            cursor: "pointer",
                                        }}
                                    >
                          
                                Merge
                            </button>

                        </div>
                    )}


                    {/*Gap button*/}
                    {!disableGap.has(albumType) && (
                        <button
                            ref={gapButtonRef}
                            onClick={openGapPopup}
                            style={{
                                position: "absolute",
                                right: photoUploaderWidth + 20,
                                padding: "4px 8px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                background: "#f8f8f8",
                                cursor: "pointer",
                            }}
                        >
                            Change Gap
                        </button>
                    )}
                  
                </div>

             
                {/* Gap Popup */}
                {isGapPopupOpen && (
                    <div
                        style={{
                            position: "fixed",
                            left: `${popupPos.left + 16}px`,
                            bottom: `${popupPos.bottom + 2}px`,
                            backgroundColor: "#fff",
                            border: "1px solid #ccc",
                            borderRadius: "6px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                            padding: "6px 10px",
                            zIndex: 1000,
                            width: "170px",
                        }}
                    >
                        {/* Top bar */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "6px",
                            }}
                        >
                            <span style={{ fontSize: "14px", fontWeight: "500" }}>
                                Change Gap
                            </span>

                            <button
                                onClick={closeGapPopup}
                                style={{
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    fontSize: "20px",
                                    lineHeight: "20px",
                                    color: "#666",
                                }}
                            >
                                ×
                            </button>
                        </div>


                        {/* Slider row */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                            }}
                        >
                            <Slider
                                value={toUI(gap)}   //  convert internal → UI
                                min={0}
                                max={20}
                                step={1}
                                onChange={(_, value) => {
                                    const uiVal = value as number;
                                    const internalVal = toInternal(uiVal);

                                    setGap(internalVal);
                                    handleSmartGap(internalVal, gapScope);
                                }}
                                sx={{
                                    height: 6,
                                    width: "110px",
                                    '& .MuiSlider-thumb': {
                                        width: 16,
                                        height: 16,
                                        backgroundColor: "#fff",
                                        border: 1,
                                    },
                                    '& .MuiSlider-rail': {
                                        height: 5,
                                        color: "#59b3d8",
                                    },
                                    '& .MuiSlider-track': {
                                        height: 4,
                                        color: "#59b3d8",
                                    },
                                }}
                            />

                            <input
                                type="number"
                                min={0}
                                max={20}
                                value={toUI(gap)}  //  display value
                                onChange={e => {
                                    let uiVal = Number(e.target.value);

                                    if (isNaN(uiVal)) uiVal = 0;

                                    uiVal = Math.max(0, Math.min(20, uiVal));

                                    const internalVal = toInternal(uiVal);

                                    setGap(internalVal);
                                    handleSmartGap(internalVal, gapScope);
                                }}
                                style={{
                                    width: "38px",
                                    height: "20px",
                                    fontSize: "13px",
                                    textAlign: "center",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                }}
                            />
                        </div>


                        {/* Scope options */}
                        <div
                            style={{
                                marginTop: "8px",
                                borderTop: "1px solid #eee",
                                paddingTop: "6px",
                                fontSize: "13px",
                                display: "flex",
                                gap: "12px",
                                alignItems: "center",
                            }}
                        >
                            <label
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "3px",
                                    cursor: "pointer",
                                }}
                            >
                                <input
                                    type="radio"
                                    checked={gapScope === "page"}
                                    onChange={() => setGapScope("page")}
                                />
                                Current
                            </label>

                            <label
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "3px",
                                    cursor: "pointer",
                                }}
                            >
                                <input
                                    type="radio"
                                    checked={gapScope === "all"}
                                    onChange={() => setGapScope("all")}
                                />
                                All
                            </label>
                        </div>

                    </div>
                )}
               
        
            </div>
        </div>

    );
};

export default AlbumPageDesigner;
